import {
  resolvePregnancyStands,
  type PregnancyStatusReading,
} from "../../pregnancy/resolvePregnancyStatus";
import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  useCallback,
} from "react";
import {
  Dimensions,
  Image,
  Platform,
  ScrollView,
  Text,
  View,
  type ImageSourcePropType,
} from "react-native";
import {
  ArrowRight,
  CalendarCheck,
  CalendarClock,
  Check,
  CheckCircle2,
  Clock3,
  CircleMinus,
  FlaskConical,
  Funnel,
  Info,
  Map as MapIcon,
  TrendingUp,
  XCircle,
  type LucideIcon,
} from "lucide-react-native";
import {
  ActionBanner,
  CardHeader,
  CardSurface,
  CardSubtitleText,
  CardTitleText,
  ClinicalDataStatus,
  CompactActionButton,
  Dialog,
  DialogHeader,
  EmptyStateSubtitleText,
  EmptyStateTitleText,
  DialogIcon,
  GlassButton,
  MoonFilterChip,
  PrimaryButton,
  ActionRowCard,
  MetricLabelText,
  MetricValueText,
  MetaText,
  EmptyState,
  MoonFormField,
  MoonSegmentedControl,
  type MoonSegmentedOption,
  PrimaryTitleText,
  SecondaryButton,
  SectionActionLink,
  SectionTitleRow,
  StatusBadge,
  SupportingSubtitleText,
  Tag,
  haptic,
  TextActionButton,
  Row,
  semanticColors,
  TextField,
  LoadingState,
} from "@louise/moon-ui";
import { ConfettiBurst } from "@louise/moon-ui/primitives/ConfettiBurst";
import calendarEmptyIllustration from "@src/packages/moon-ui/assets/images/calendar-reminder-dialog-illustration.png";
import { DocumentFileDropZone } from "@src/packages/document-vault/DocumentFileDropZone";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@louise/moon-ui/primitives/Accordion";
import { EMBER, colors } from "@louise/moon-ui/theme/tokens";
import {
  getTestByName,
  getMedicationByBrandName,
  getProcedureByName,
  getBlock,
  makeId,
  type ContentBlock,
} from "@src/packages/content";
import {
  useLocale,
  useTranslations,
  type TranslationFunction,
} from "@src/packages/i18n";
import { useCycleHeaderState } from "@src/packages/cycles/hooks/useCycleHeaderState";
import { presentCycleHeaderForHero } from "@src/packages/cycles/presenters/cycleHeaderHero";
import { buildHeroMetrics } from "@src/packages/cycles/presenters/cycleHeroMetrics";
import { getPostProcedureWaitContext } from "@src/packages/cycles/presenters/postProcedureWait";
import { semenAnalysisWaitFacts } from "@src/packages/cycles/presenters/semenAnalysisWait";
import { ivfHomeStep } from "@src/packages/cycles/presenters/ivfHomeStep";
import {
  spermCollectionFacts,
  type SpermCollectionFacts,
} from "@src/packages/cycles/presenters/spermCollectionFacts";
import {
  fetTransferFacts,
  type FetTransferFacts,
} from "@src/packages/cycles/presenters/fetTransferFacts";
import { useDocuments } from "@src/packages/documents/hooks/useDocuments";
import { isConsentDocument } from "../hooks/usePlanProgress";
import type {
  PostProcedureOutcomeContentKey,
  PostProcedureOutcomeRoute,
} from "@src/packages/cycles/presenters/postProcedureOutcome";
import type { PostProcedureOutcomeResult } from "@src/packages/cycles/presenters/postProcedureOutcomeState";
import {
  formatPhaseTime,
  formatRelativePhaseDate,
} from "@src/packages/cycles/presenters/formatPhaseDate";
import {
  useActiveCycle,
  useActiveCycleWithPhases,
  useCycles,
  useCyclePhases,
  type Cycle,
  type CycleType,
  type Phase,
} from "@src/packages/cycles/hooks/useCycles";
import { extractObservedResults } from "@src/packages/cycles/presenters/postCycleState";
import { useTreatmentDashboard } from "../hooks/useTreatmentDashboard";
import { usePatientMedications } from "@src/packages/care/hooks/usePatientMedications";
import { useUpdateMedication } from "@src/packages/care";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import type { ParticipantStatus } from "@src/packages/experience";
import { calculateAge } from "@src/packages/homepage/utils/patientCalculations";
import { toLocalDateString, getTodayString } from "@src/packages/utils/date";
import { formatDate } from "@src/packages/i18n/formatters";
import { makeTFunc } from "@src/packages/i18n/translations";
import enTranslations from "@src/packages/i18n/locales/en.json";
import type {
  ResultInterpretation,
  TestResultInsightsQueryState,
} from "../intelligence/useTestResultInsights";
import { resultInterpretationKey } from "../intelligence/useTestResultInsights";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import type { PlanAction, StartTreatmentPresetCycleType } from "../plan.types";
import { OrganicMiniIcon, type OrganicMiniIconKind } from "./OrganicMiniIcon";
import {
  AppointmentRow,
  getTodayRowIllustration,
  isCompletableTodayRow,
} from "./AppointmentRow";
import {
  OrganicExplanatoryIllustration,
  OutcomeEstimateFunnelSection,
} from "./EggFreezingBiologyDeepCarouselCard";
import {
  OutcomeDotFunnelRows,
  type OutcomeDotFunnelStage,
} from "./OutcomeDotFunnelRows";
import {
  OUTCOME_FUNNEL_ILLUSTRATIONS,
  type OutcomeFunnelIllustrationKey,
} from "./OutcomeFunnelIllustrations";
import {
  CryoVaultOverviewCard,
  useCryoSpecimenSnapshotsBuilder,
} from "@src/packages/care/cryovault/CryoVaultOverviewCard";
import {
  useFrozenSpecimens,
  useFrozenSpecimensForCycle,
  useOocyteCohorts,
  useSpecimens,
} from "@src/packages/care/cryovault/useSpecimens";
import { semenAnalysisResultsFromSpermiograms } from "../semenAnalysisFromSpermiograms";
import { semenAnalysisResultsFromSpecimens } from "../semenAnalysisFromSpecimens";
import { JourneyHandoffCard } from "./JourneyHandoffCard";
import { FutureUseEstimateTabs } from "./FutureUseEstimateTabs";
import { SpermCryosurvivalEstimateWidget } from "./SpermCryosurvivalEstimateCard";
import { SpermResultPathsCard } from "./SpermResultPathsCard";
import { StepExplainerCard } from "./StepExplainerCard";
import {
  SpermAnalysisSkeletonCard,
  type SpermAnalysisResults,
} from "./SpermAnalysisSkeletonCard";
import { HorizontalSnapCarousel } from "./HorizontalSnapCarousel";
import {
  Hero,
  HeroWaitProgress,
  type HeroCycleEnding,
  type HeroMenuAction,
  type HeroPrimaryAction,
} from "./hero/Hero";
import {
  MedicationRow,
  type MedicationAvailabilityStatus,
  type MedicationForm,
} from "./MedicationRow";
import TestResultInsightCard from "./TestResultInsightCard";
import {
  useLogMedicationDose,
  useMedicationLogs,
  type CreateMedicationLogPayload,
  type MedicationLog,
} from "@src/packages/care/hooks/useMedicationLogs";
import { createIdempotencyKey } from "@src/packages/api/idempotency";
import { useTestResults } from "@src/packages/test-results";
import {
  getTodayDoseLog,
  getTodayDoseStatus,
  type DoseStatus,
} from "@src/packages/care/medications";
import {
  getTodayActionCompletion,
  serializeTodayActionCompletion,
  type TodayActionCompletionStatus,
} from "@src/packages/care/todayActionCompletion";
import {
  useCreatePatientNote,
  usePatientNotes,
  type CreateNotePayload,
} from "@src/packages/care/hooks/usePatientNotes";
import { useTimezone } from "@src/packages/preferences";
import {
  usePostProcedureOutcomeStage,
  type PostProcedureOutcomeStage,
} from "../hooks/usePostProcedureOutcomeStage";
import {
  buildEarlyPregnancyDating,
  gestationFromReportedWeeks,
  useCyclePregnancyFollowUp,
  type PatientPregnancyStands,
} from "../../pregnancy";
import { shouldShowEarlyPregnancyGuidance } from "../showEarlyPregnancyGuidance";
import { standingPregnancyCopyKeys } from "../standingPregnancyCopy";
import {
  closedWithoutResultCopyKeys,
  wasClosedWithoutARecordedEnding,
} from "../closedWithoutResultCopy";
import { resolveOutcomeHeroLinks } from "../outcomeHeroLinks";
import { recordedBirthCopyKeys, resolveRecordedBirth } from "../recordedBirth";
import { buildEarlyPregnancyWhatToKnowCards } from "../earlyPregnancyCards";
import { pregnancyTrackingSetupStatusLabel } from "./PregnancyTrackingSetupDialog";
import MonitoringEmptyStateCard from "@src/packages/care/MonitoringEmptyStateCard";
import type { StepExplainer } from "@louise/care/education";
import { useCurrentStepExplainer } from "@louise/care/education/useCurrentStepExplainer";

const inCycleHeroBackground =
  require("@src/apps/moon/assets/images/precycle-hero-bronze-gradient.webp") as ImageSourcePropType;

const handoffBackground =
  require("@src/apps/moon/assets/images/silk-brown.png") as number;

const medicationPillIllustration =
  require("@src/apps/moon/assets/images/pill-capsule.png") as number;
const medicationVialIllustration =
  require("@src/apps/moon/assets/images/vial-syringe.png") as number;
const medicationPenIllustration =
  require("@src/apps/moon/assets/images/injection-pen.png") as number;
const testTubesIllustration =
  require("@src/apps/moon/assets/images/you-icons/test-tubes.png") as number;

export type HomepageReviewConceptVariant =
  | "setup"
  | "learn-explorer"
  | "trying-naturally"
  | "egg-freezing-active"
  | "egg-freezing-postcycle"
  | "embryo-freezing-active"
  | "embryo-freezing-postcycle"
  | "sperm-freezing-active"
  | "sperm-freezing-wait"
  | "sperm-freezing-postcycle"
  | "ivf-precycle"
  | "ivf-active"
  | "post-retrieval"
  | "fet-active"
  | "beta-wait"
  | PostProcedureOutcomeRoute
  | "iui-precycle"
  | "iui-active"
  | "iui-wait"
  | "oi-wait"
  | "oi-active"
  | "cycle-status-review"
  | "amab-family-building-precycle"
  | "egg-donor"
  | "sperm-donor"
  | "carrier"
  | "intended-parent";

export type HomepageMedicationFactSheetPayload = {
  item: ContentBlock<"medication"> | null;
  fallbackName: string;
  medicationId?: string;
  /** Opens the shared taken/late/missed logger for this scheduled occurrence. */
  onLogDose?: () => void;
  userContext: {
    originalName?: string;
    dosage?: string;
    route?: string | null;
    timeTaken?: string[];
  };
};

export type HomepageProcedureFactSheetPayload = {
  item: ContentBlock<"procedure">;
  appointment: {
    id: string;
    /** Raw appointment UUID — required for delete/update API calls. */
    appointmentId?: string;
    title: string;
    subtitle: string;
    time?: string;
    date?: string;
    notes?: string;
    /** Ownership metadata — required for the shared appointment-management
     * predicate; sheets fail closed without it (#2232). */
    ownership?: string | null;
    ownerId?: string | null;
  };
};

export type HomepageTestFactSheetPayload = {
  item: ContentBlock<"test">;
  testName: string;
};

export type Metric = {
  label: string;
  value: string;
  note?: string;
};

export type Task = {
  title: string;
  detail: string;
  owner: string;
  due: string;
  status: "done" | "now" | "waiting" | "locked";
};

export type TimelineItem = {
  label: string;
  detail: string;
  status: "done" | "now" | "next" | "waiting";
};

export type FunnelStage = {
  label: string;
  count: number;
  detail: string;
};

type PostRetrievalMetricKey =
  | "retrieved_oocytes"
  | "m2_count"
  | "fertilized_oocytes"
  | "embryos_day_3"
  | "embryos_day_5"
  | "frozen_embryo";

type PostRetrievalMetric = NonNullable<Phase["fertilityMetrics"]>[number];

type PostRetrievalMilestone = {
  key: PostRetrievalMetricKey;
  label: string;
  waitingDetail: string;
  illustrationKey: OutcomeFunnelIllustrationKey;
};

function latestPostRetrievalMetric(
  phases: Phase[],
  type: PostRetrievalMetricKey,
): PostRetrievalMetric | null {
  return (
    phases
      .flatMap((phase) => phase.fertilityMetrics ?? [])
      .filter((metric) => metric.type === type)
      .sort((a, b) => a.updatedAt.localeCompare(b.updatedAt))
      .at(-1) ?? null
  );
}

function postRetrievalSourceLabel(
  source: PostRetrievalMetric["source"],
  t: TranslationFunction,
): string {
  switch (source) {
    case "provider":
      return t("homepage.review_concept.inline.post_retrieval.from_clinic");
    case "patient":
      return t("homepage.review_concept.inline.post_retrieval.added_by_you");
    case "partner":
      return t(
        "homepage.review_concept.inline.post_retrieval.added_by_partner",
      );
    default:
      return t("homepage.review_concept.inline.post_retrieval.recorded");
  }
}

function buildPostRetrievalMilestones(
  cycleType: CycleType | undefined,
  t: TranslationFunction,
): PostRetrievalMilestone[] {
  const shared: PostRetrievalMilestone[] = [
    {
      key: "retrieved_oocytes",
      label: t(
        "homepage.review_concept.inline.post_retrieval.retrieved_eggs_label",
      ),
      waitingDetail: t(
        "homepage.review_concept.inline.post_retrieval.retrieved_eggs_waiting",
      ),
      illustrationKey: "retrieved-eggs",
    },
    {
      key: "m2_count",
      label: t(
        "homepage.review_concept.inline.post_retrieval.mature_eggs_label",
      ),
      waitingDetail: t(
        "homepage.review_concept.inline.post_retrieval.mature_eggs_waiting",
      ),
      illustrationKey: "mature-eggs",
    },
  ];

  if (cycleType === "egg_freezing") {
    return [
      ...shared,
      {
        key: "frozen_embryo",
        label: t(
          "homepage.review_concept.inline.post_retrieval.frozen_eggs_label",
        ),
        waitingDetail: t(
          "homepage.review_concept.inline.post_retrieval.frozen_eggs_waiting",
        ),
        illustrationKey: "frozen-eggs",
      },
    ];
  }

  const embryoMilestones: PostRetrievalMilestone[] = [
    {
      key: "fertilized_oocytes",
      label: t(
        "homepage.review_concept.inline.post_retrieval.fertilized_eggs_label",
      ),
      waitingDetail: t(
        "homepage.review_concept.inline.post_retrieval.fertilized_eggs_waiting",
      ),
      illustrationKey: "fertilized-eggs",
    },
    {
      key: "embryos_day_3",
      label: t(
        "homepage.review_concept.inline.post_retrieval.day_three_embryos_label",
      ),
      waitingDetail: t(
        "homepage.review_concept.inline.post_retrieval.day_three_embryos_waiting",
      ),
      illustrationKey: "day-3-embryos",
    },
    {
      key: "embryos_day_5",
      label: t(
        "homepage.review_concept.inline.post_retrieval.day_five_embryos_label",
      ),
      waitingDetail: t(
        "homepage.review_concept.inline.post_retrieval.day_five_embryos_waiting",
      ),
      illustrationKey: "day-5-embryos",
    },
  ];

  if (cycleType === "embryo_freezing") {
    embryoMilestones.push({
      key: "frozen_embryo",
      label: t(
        "homepage.review_concept.inline.post_retrieval.frozen_embryos_label",
      ),
      waitingDetail: t(
        "homepage.review_concept.inline.post_retrieval.frozen_embryos_waiting",
      ),
      illustrationKey: "frozen-embryos",
    });
  }

  return [...shared, ...embryoMilestones];
}

function buildPostRetrievalFunnelStages(
  phases: Phase[],
  cycleType: CycleType | undefined,
  t: TranslationFunction,
  onPress: () => void,
): OutcomeDotFunnelStage[] {
  return buildPostRetrievalMilestones(cycleType, t).map((milestone) => {
    const metric = latestPostRetrievalMetric(phases, milestone.key);
    const recordedValue = metric?.value;
    const isRecorded = recordedValue != null;

    return {
      id: milestone.key,
      label: milestone.label,
      value: recordedValue ?? 0,
      detail: isRecorded
        ? postRetrievalSourceLabel(metric?.source ?? "system", t)
        : milestone.waitingDetail,
      missing: !isRecorded,
      illustration: OUTCOME_FUNNEL_ILLUSTRATIONS[milestone.illustrationKey],
      onPress,
    };
  });
}

type TodayRowKind = "medication" | "appointment" | "monitoring" | "task";
type MedicationLogOptionId = "on-time" | "late" | "missed" | "skipped";

export type TodayRow = {
  id: string;
  time: string;
  title: string;
  detail: string;
  /** Free-form notes set by the user when they created the appointment / med. */
  notes?: string;
  partnerName?: string;
  icon: OrganicMiniIconKind;
  status?: string;
  kind: TodayRowKind;
  /** Calendar day for persisted Today action events. */
  date?: string;
  /** Explicitly marks a non-medication row as a patient action. */
  completable?: boolean;
  medicationName?: string;
  medicationId?: string;
  medicationDose?: string;
  medicationRoute?: string | null;
  scheduledFor?: string | null;
  medicationFormat?: string;
  medicationForm?: MedicationForm;
  /**
   * `medication.form` as the API stores it — not `medicationForm` above.
   *
   * That one has four values and picks an illustration. This is the
   * administration method she chose when she added the medication, and it is
   * what lets the injection coach confirm a device instead of asking for one.
   */
  medicationApiForm?: string | null;
  medicationAvailabilityStatus?: MedicationAvailabilityStatus;
  medicationIsStopped?: boolean;
  medicationEndDate?: string | null;
  /** Raw appointment UUID — required for delete/update API calls.
   * The `id` field above is a composite like `appt-<uuid>` used for React
   * keys and routing; the bare UUID needs to be plumbed separately. */
  appointmentId?: string;
  /** Ownership metadata for the shared appointment-management predicate —
   * sheets fail closed without it (#2232). */
  ownership?: "patient" | "provider";
  ownerId?: string;
  procedureName?: string;
  appointmentResultType?:
    "monitoring" | "bloodwork" | "collection" | "lab" | "call" | "storage";
};

export type DecisionOption = {
  title: string;
  detail: string;
  status: string;
};

const MEDICATION_LOG_VISUALS: Record<
  MedicationLogOptionId,
  { icon: LucideIcon; color: string }
> = {
  "on-time": { icon: CheckCircle2, color: semanticColors.doseStatus.taken },
  late: { icon: Clock3, color: semanticColors.doseStatus.late },
  missed: { icon: XCircle, color: semanticColors.doseStatus.missed },
  skipped: { icon: CircleMinus, color: semanticColors.doseStatus.skipped },
};

/**
 * A step on a "what to know" card.
 *
 * A plain string is a line of prose. The object form carries a `tag` — a short
 * value that belongs to *this* patient rather than to the copy, such as the
 * calendar window a milestone falls in for her. It is rendered as a pill so it
 * reads as hers at a glance instead of dissolving into the sentence.
 */
export type WhatToKnowBullet = {
  label: string;
  tag?: string | null;
  /**
   * One of the app's own organic icons, standing in for the bullet's dot.
   *
   * Presentational only, and never carrying meaning the words do not: a reader
   * who sees none of them loses nothing. It is here to give a list of steps a
   * shape the eye can move down, which four identical dots do not — drawn from
   * the set the rest of the app uses rather than from emoji, which belong to the
   * platform's voice and not to Louise's.
   */
  icon?: OrganicMiniIconKind | null;
};

export type WhatToKnowCard = {
  badge: string;
  title: string;
  body: string;
  bullets: (string | WhatToKnowBullet)[];
  tone: "steady" | "caution";
  /**
   * One of the app's own glyphs, drawn large beside the badge.
   *
   * Optional: a card without one reads exactly as it did. Where it is set it
   * says what kind of thing the card is about — a scan, a hormone test, a call
   * to the clinic — in Louise's hand rather than the platform's.
   */
  illustration?: OrganicMiniIconKind;
};

type LabUpdateMetric = {
  label: string;
  value: string;
  note: string;
};

export type LabUpdateCard = {
  title: string;
  timeframe: string;
  status: "next" | "pending";
  body: string;
  metrics: LabUpdateMetric[];
};

type CycleLogisticsChecklistItem = {
  label: string;
  detail: string;
  /**
   * `done` only when her record says so. `context` is background she should
   * know, not a step: it carries no tick.
   */
  status: "done" | "next" | "waiting" | "context";
};

export type CycleLogisticsCard = {
  title: string;
  timeframe: string;
  body: string;
  icon: OrganicMiniIconKind;
  items: CycleLogisticsChecklistItem[];
  action?: PlanAction;
};

export type TestResultsVariant = "default" | "iui-active";

export type Widget =
  | {
      type: "metrics";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      metrics: Metric[];
    }
  | {
      type: "today";
      title: string;
      subtitle?: string;
      rows: TodayRow[];
    }
  | {
      type: "action-rows";
      rows: OutcomeActionRow[];
    }
  | {
      type: "tasks";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      tasks: Task[];
      action?: PlanAction;
    }
  | {
      type: "timeline";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      items: TimelineItem[];
    }
  | {
      type: "funnel";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      stages: FunnelStage[];
      action?: PlanAction;
      /** When true, renders just the dot rows (no card title/icon/action)
       *  so the section title is the only heading. */
      bare?: boolean;
    }
  | {
      type: "outcome-estimate-funnel";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      variant: "egg-freezing" | "embryo-freezing";
    }
  | {
      type: "lab-carousel";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      cards: LabUpdateCard[];
    }
  | {
      type: "cycle-logistics";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      cards: CycleLogisticsCard[];
    }
  | {
      type: "decision";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      options: DecisionOption[];
      action?: PlanAction;
    }
  | {
      /**
       * The exploration content, folded away at the foot of the page.
       *
       * A patient who has finished a cycle does not need to be told how to
       * choose a path — that was the pre-cycle home's whole job, and leaving it
       * as her hero is what made a finished cycle read as not started. But she
       * may still want it, so it keeps a door: collapsed, last, and never the
       * subject.
       */
      type: "explore-options";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      rows: OutcomeActionRow[];
    }
  | {
      type: "what-to-know";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      cards: WhatToKnowCard[];
    }
  | {
      type: "test-results";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      variant?: TestResultsVariant;
    }
  | {
      type: "data-status";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      detail: string;
      action?: PlanAction;
    }
  | {
      /**
       * Her records are still loading or couldn't load. Shown in place of
       * what they would decide, so a failed load never reads as "none yet".
       */
      type: "records-status";
      state: "loading" | "unavailable";
      loadingCaption: string;
      unavailableTitle: string;
      unavailableMessage: string;
      retryLabel: string;
      retryingLabel: string;
      onRetry: () => void;
      isRetrying: boolean;
    }
  | {
      /**
       * The semen analysis the lab is still running. The screen supplies the
       * card through `renderSemenAnalysisPending`; when it does not (a result
       * is already recorded), nothing renders.
       */
      type: "semen-analysis-pending";
    }
  | {
      type: "monitoring-chart";
      title: string;
      subtitle?: string;
      icon: OrganicMiniIconKind;
      primaryAction: PlanAction;
    }
  | {
      type: "cryo-vault";
      title?: string;
      subtitle?: string;
    }
  | {
      type: "future-use-estimate";
      primaryUnit?: "eggs" | "embryos";
    }
  | {
      type: "sperm-cryosurvival-estimate";
    }
  | {
      type: "handoff";
      title: string;
      body: string;
      ctaLabel: string;
      checklist: string[];
      action: PlanAction;
    }
  | {
      type: "semen-analysis";
      title: string;
      subtitle?: string;
      results?: SpermAnalysisResults | null;
      /** What the card says before any figure exists; see `SpermAnalysisSkeletonCard`. */
      emptyCard?: { title: string; action: PlanAction };
    }
  | {
      /** What each kind of semen analysis result usually leads to. */
      type: "sperm-result-paths";
    }
  | {
      /**
       * The explainer for the step she is on, the treatment hub's own for that
       * step, in full. The page supplies it (`useCurrentStepExplainer`); a
       * section holding only this is left out while there is none.
       */
      type: "step-explainer";
    };

export type ConceptSection = {
  title: string;
  subtitle: string;
  widgets: Widget[];
  action?: PlanAction;
};

type Concept = {
  eyebrow: string;
  title: string;
  summary: string;
  icon: OrganicMiniIconKind;
  heroMetrics?: Metric[];
  primaryAction?: PlanAction;
  sections: ConceptSection[];
};

type OutcomeActionRow = {
  title: string;
  detail: string;
  icon: OrganicMiniIconKind;
  action?: PlanAction;
  /** A feature Louise does not offer yet: badged, with nowhere to go. */
  comingSoon?: boolean;
};

const REVIEW_VARIANTS = new Set<HomepageReviewConceptVariant>([
  "setup",
  "learn-explorer",
  "trying-naturally",
  "egg-freezing-active",
  "egg-freezing-postcycle",
  "embryo-freezing-active",
  "embryo-freezing-postcycle",
  "sperm-freezing-active",
  "sperm-freezing-wait",
  "sperm-freezing-postcycle",
  "ivf-precycle",
  "ivf-active",
  "post-retrieval",
  "fet-active",
  "beta-wait",
  "ivf-positive",
  "ivf-negative",
  "ivf-uncertain",
  "ivf-adverse",
  "ivf-unavailable",
  "fet-positive",
  "fet-negative",
  "fet-uncertain",
  "fet-adverse",
  "fet-unavailable",
  "iui-positive",
  "iui-negative",
  "iui-uncertain",
  "iui-adverse",
  "iui-unavailable",
  "oi-positive",
  "oi-negative",
  "oi-uncertain",
  "oi-adverse",
  "oi-unavailable",
  "iui-precycle",
  "iui-active",
  "iui-wait",
  "oi-wait",
  "oi-active",
  "cycle-status-review",
  "amab-family-building-precycle",
  "egg-donor",
  "sperm-donor",
  "carrier",
  "intended-parent",
]);

const POST_PROCEDURE_OUTCOME_VARIANTS = new Set<PostProcedureOutcomeRoute>([
  "ivf-positive",
  "ivf-negative",
  "ivf-uncertain",
  "ivf-adverse",
  "ivf-unavailable",
  "fet-positive",
  "fet-negative",
  "fet-uncertain",
  "fet-adverse",
  "fet-unavailable",
  "iui-positive",
  "iui-negative",
  "iui-uncertain",
  "iui-adverse",
  "iui-unavailable",
  "oi-positive",
  "oi-negative",
  "oi-uncertain",
  "oi-adverse",
  "oi-unavailable",
]);

export type CycleStatusReviewKind = "cancelled" | "converted";

const POST_CYCLE_VARIANTS = new Set<HomepageReviewConceptVariant>([
  "egg-freezing-postcycle",
  "embryo-freezing-postcycle",
  "sperm-freezing-postcycle",
]);

export function isPostCycleVariant(
  value: HomepageReviewConceptVariant | null | undefined,
): boolean {
  return value != null && POST_CYCLE_VARIANTS.has(value);
}

export function isPostProcedureOutcomeVariant(
  value: HomepageReviewConceptVariant | null | undefined,
): value is PostProcedureOutcomeRoute {
  return (
    value != null &&
    POST_PROCEDURE_OUTCOME_VARIANTS.has(value as PostProcedureOutcomeRoute)
  );
}

export function getPostProcedureOutcomeContentKey(
  value: HomepageReviewConceptVariant | null | undefined,
): PostProcedureOutcomeContentKey | null {
  if (!isPostProcedureOutcomeVariant(value)) return null;
  return value.replace(
    /-(positive|negative|uncertain|adverse|unavailable)$/,
    "",
  ) as PostProcedureOutcomeContentKey;
}

export function getHomepageReviewConceptVariant(
  value: string | null,
): HomepageReviewConceptVariant | null {
  return REVIEW_VARIANTS.has(value as HomepageReviewConceptVariant)
    ? (value as HomepageReviewConceptVariant)
    : null;
}

// Static English fallback `t()` used by module-level data builders so the
// exported `*Cards` constants stay typed and stable. Localized rendering
// goes through React hooks that pass a real `t` into the same builders.
const tEn = makeTFunc(enTranslations);

function buildActions(t: TranslationFunction) {
  const RC2 = "homepage.review_concept.inline";
  return {
    calendar: {
      label: t(`${RC2}.action_calendar`),
      screen: "CareOverview",
    },
    medicalHistory: {
      label: t(`${RC2}.action_complete_history`),
      screen: "MedicalHistory",
    },
    testing: {
      label: t(`${RC2}.action_check_tests`),
      screen: "AtHomeFertilityTesting",
    },
    findCare: {
      label: t(`${RC2}.action_find_care`),
      screen: "FindCareScreen",
    },
    coverage: {
      label: t(`${RC2}.action_check_coverage`),
      signal: "open-coverage-status-modal",
    },
    programs: {
      label: t(`${RC2}.action_open_program`),
      screen: "ProgramsScreen",
    },
    documents: {
      label: t(`${RC2}.action_open_documents`),
      screen: "DocumentsScreen",
    },
    medications: {
      label: t(`${RC2}.action_review_meds`),
      screen: "CareOverview",
      params: { focus: "medications" },
    },
    careTeam: {
      label: t(`${RC2}.action_open_care_team`),
      screen: "CareOverview",
      params: { focus: "care-team" },
    },
    logMonitoring: {
      label: t(`${RC2}.action_log_monitoring`),
      screen: "CycleDetailScreen",
      params: { focus: "monitoring" },
    },
    logSymptoms: {
      label: t(`${RC2}.action_log_symptoms`),
      screen: "CycleDetailScreen",
      params: { focus: "symptoms" },
    },
  } satisfies Record<string, PlanAction>;
}

export function buildEggLabUpdateCards(
  t: TranslationFunction,
): LabUpdateCard[] {
  const RC2 = "homepage.review_concept.egg_lab";
  return [
    {
      title: t(`${RC2}.retrieval.title`),
      timeframe: t(`${RC2}.retrieval.timeframe`),
      status: "next",
      body: t(`${RC2}.retrieval.body`),
      metrics: [
        {
          label: t(`${RC2}.retrieval.m1_label`),
          value: t(`${RC2}.retrieval.m1_value`),
          note: t(`${RC2}.retrieval.m1_note`),
        },
        {
          label: t(`${RC2}.retrieval.m2_label`),
          value: t(`${RC2}.retrieval.m2_value`),
          note: t(`${RC2}.retrieval.m2_note`),
        },
        {
          label: t(`${RC2}.retrieval.m3_label`),
          value: t(`${RC2}.retrieval.m3_value`),
          note: t(`${RC2}.retrieval.m3_note`),
        },
      ],
    },
    {
      title: t(`${RC2}.maturity.title`),
      timeframe: t(`${RC2}.maturity.timeframe`),
      status: "pending",
      body: t(`${RC2}.maturity.body`),
      metrics: [
        {
          label: "MII",
          value: t(`${RC2}.maturity.m1_value`),
          note: t(`${RC2}.maturity.m1_note`),
        },
        {
          label: t(`${RC2}.maturity.m2_label`),
          value: t(`${RC2}.maturity.m2_value`),
          note: t(`${RC2}.maturity.m2_note`),
        },
        {
          label: t(`${RC2}.maturity.m3_label`),
          value: t(`${RC2}.maturity.m3_value`),
          note: t(`${RC2}.maturity.m3_note`),
        },
      ],
    },
    {
      title: t(`${RC2}.vitrification.title`),
      timeframe: t(`${RC2}.vitrification.timeframe`),
      status: "pending",
      body: t(`${RC2}.vitrification.body`),
      metrics: [
        {
          label: t(`${RC2}.vitrification.m1_label`),
          value: t(`${RC2}.vitrification.m1_value`),
          note: t(`${RC2}.vitrification.m1_note`),
        },
        {
          label: t(`${RC2}.vitrification.m2_label`),
          value: t(`${RC2}.vitrification.m2_value`),
          note: t(`${RC2}.vitrification.m2_note`),
        },
        {
          label: t(`${RC2}.vitrification.m3_label`),
          value: t(`${RC2}.vitrification.m3_value`),
          note: t(`${RC2}.vitrification.m3_note`),
        },
      ],
    },
    {
      title: t(`${RC2}.storage.title`),
      timeframe: t(`${RC2}.storage.timeframe`),
      status: "pending",
      body: t(`${RC2}.storage.body`),
      metrics: [
        {
          label: t(`${RC2}.storage.m1_label`),
          value: t(`${RC2}.storage.m1_value`),
          note: t(`${RC2}.storage.m1_note`),
        },
        {
          label: t(`${RC2}.storage.m2_label`),
          value: t(`${RC2}.storage.m2_value`),
          note: t(`${RC2}.storage.m2_note`),
        },
        {
          label: t(`${RC2}.storage.m3_label`),
          value: t(`${RC2}.storage.m3_value`),
          note: t(`${RC2}.storage.m3_note`),
        },
      ],
    },
  ];
}

export const eggLabUpdateCards: LabUpdateCard[] = buildEggLabUpdateCards(tEn);

export function buildEmbryoFunnel(t: TranslationFunction): FunnelStage[] {
  const RC2 = "homepage.review_concept.embryo_funnel";
  return [
    { label: t(`${RC2}.s1_label`), count: 12, detail: t(`${RC2}.s1_detail`) },
    { label: t(`${RC2}.s2_label`), count: 10, detail: t(`${RC2}.s2_detail`) },
    { label: t(`${RC2}.s3_label`), count: 8, detail: t(`${RC2}.s3_detail`) },
    {
      label: t(`${RC2}.s4_label`),
      count: 4,
      detail: t(`${RC2}.s4_detail`),
    },
    {
      label: t(`${RC2}.s5_label`),
      count: 2,
      detail: t(`${RC2}.s5_detail`),
    },
  ];
}

export const embryoFunnel: FunnelStage[] = buildEmbryoFunnel(tEn);

export function buildBetaMetrics(t: TranslationFunction): Metric[] {
  const RC2 = "homepage.review_concept.beta";
  return [
    {
      label: t(`${RC2}.date_label`),
      value: "May 24",
      note: t(`${RC2}.date_note`),
    },
    {
      label: t(`${RC2}.dpt_label`),
      value: "6",
      note: t(`${RC2}.dpt_note`),
    },
    {
      label: t(`${RC2}.meds_label`),
      value: t(`${RC2}.meds_value`),
      note: t(`${RC2}.meds_note`),
    },
    {
      label: t(`${RC2}.symptoms_label`),
      value: t(`${RC2}.symptoms_value`),
      note: t(`${RC2}.symptoms_note`),
    },
  ];
}

export const betaMetrics: Metric[] = buildBetaMetrics(tEn);

export function buildBetaWaitResultPlanCards(
  t: TranslationFunction,
  betaDateLabel?: string | null,
): CycleLogisticsCard[] {
  const RC2 = "homepage.review_concept.inline";
  return [
    {
      title: t(`${RC2}.beta_wait_result_clinic_title`),
      timeframe:
        betaDateLabel ?? t(`${RC2}.beta_wait_result_clinic_timeframe_pending`),
      icon: "start",
      body: t(`${RC2}.beta_wait_result_clinic_body`),
      items: [
        {
          label: t(`${RC2}.beta_wait_result_clinic_i1_label`),
          detail: t(`${RC2}.beta_wait_result_clinic_i1_detail`),
          status: "next",
        },
        {
          label: t(`${RC2}.beta_wait_result_clinic_i2_label`),
          detail: t(`${RC2}.beta_wait_result_clinic_i2_detail`),
          status: "waiting",
        },
        {
          label: t(`${RC2}.beta_wait_result_clinic_i3_label`),
          detail: t(`${RC2}.beta_wait_result_clinic_i3_detail`),
          status: "next",
        },
      ],
    },
    {
      title: t(`${RC2}.beta_wait_result_positive_title`),
      timeframe: t(`${RC2}.beta_wait_result_positive_timeframe`),
      icon: "hormones",
      body: t(`${RC2}.beta_wait_result_positive_body`),
      items: [
        {
          label: t(`${RC2}.beta_wait_result_positive_i1_label`),
          detail: t(`${RC2}.beta_wait_result_positive_i1_detail`),
          status: "waiting",
        },
        {
          label: t(`${RC2}.beta_wait_result_positive_i2_label`),
          detail: t(`${RC2}.beta_wait_result_positive_i2_detail`),
          status: "waiting",
        },
        {
          label: t(`${RC2}.beta_wait_result_positive_i3_label`),
          detail: t(`${RC2}.beta_wait_result_positive_i3_detail`),
          status: "waiting",
        },
      ],
    },
    // The "if negative" branch is intentionally NOT surfaced proactively during
    // the wait — leading with the loss path mid-wait is the wrong tone. That
    // content moves behind the result flow (RecordBetaResultModal) instead.
  ];
}

export const betaWaitResultPlanCards: CycleLogisticsCard[] =
  buildBetaWaitResultPlanCards(tEn);

/**
 * The IUI and OI waits name the test date only when the cycle records one, as
 * the IVF wait does (`buildBetaWaitResultPlanCards`). They used to print a
 * fixed "May 31" to every patient.
 */
function buildIuiWaitResultPlanCards(
  t: TranslationFunction,
  betaDateLabel?: string | null,
): CycleLogisticsCard[] {
  const RC2 = "homepage.review_concept.inline";
  return [
    {
      title: t(`${RC2}.iui_wait_result_window_title`),
      timeframe:
        betaDateLabel ?? t(`${RC2}.beta_wait_result_clinic_timeframe_pending`),
      icon: "hormones",
      body: t(`${RC2}.iui_wait_result_window_body`),
      items: [
        {
          label: t(`${RC2}.iui_wait_result_window_i1_label`),
          detail: t(`${RC2}.can_affect_home_tests`),
          // Not every IUI has a trigger, and the cycle doesn't record one
          // here, so this explains rather than ticks.
          status: "context",
        },
        {
          label: t(`${RC2}.iui_wait_result_window_i2_label`),
          detail: t(`${RC2}.clinic_lab_appointment`),
          status: "next",
        },
        {
          label: t(`${RC2}.iui_wait_result_window_i3_label`),
          detail: t(`${RC2}.iui_wait_result_window_i3_detail`),
          status: "waiting",
        },
      ],
    },
    {
      title: t(`${RC2}.iui_wait_result_bleeding_title`),
      timeframe: t(`${RC2}.iui_wait_result_bleeding_timeframe`),
      icon: "panel",
      body: t(`${RC2}.iui_wait_result_bleeding_body`),
      items: [
        {
          label: t(`${RC2}.iui_wait_result_bleeding_i1_label`),
          detail: t(`${RC2}.iui_wait_result_bleeding_i1_detail`),
          status: "next",
        },
        {
          label: t(`${RC2}.iui_wait_result_bleeding_i2_label`),
          detail: t(`${RC2}.iui_wait_result_bleeding_i2_detail`),
          status: "waiting",
        },
        {
          label: t(`${RC2}.iui_wait_result_bleeding_i3_label`),
          detail: t(`${RC2}.iui_wait_result_bleeding_i3_detail`),
          status: "waiting",
        },
      ],
    },
  ];
}

function buildOiWaitResultPlanCards(
  t: TranslationFunction,
  betaDateLabel?: string | null,
): CycleLogisticsCard[] {
  const RC2 = "homepage.review_concept.inline";
  return [
    {
      title: t(`${RC2}.oi_wait_result_window_title`),
      timeframe:
        betaDateLabel ?? t(`${RC2}.beta_wait_result_clinic_timeframe_pending`),
      icon: "hormones",
      body: t(`${RC2}.oi_wait_result_window_body`),
      items: [
        {
          label: t(`${RC2}.oi_wait_result_window_i1_label`),
          detail: t(`${RC2}.your_clinic_dates_the_test_from_it`),
          status: "context",
        },
        {
          label: t(`${RC2}.oi_wait_result_window_i2_label`),
          detail: t(`${RC2}.oi_wait_result_window_i2_detail`),
          status: "next",
        },
        {
          label: t(`${RC2}.oi_wait_result_window_i3_label`),
          detail: t(`${RC2}.oi_wait_result_window_i3_detail`),
          status: "waiting",
        },
      ],
    },
    {
      title: t(`${RC2}.oi_wait_result_bleeding_title`),
      timeframe: t(`${RC2}.oi_wait_result_bleeding_timeframe`),
      icon: "panel",
      body: t(`${RC2}.oi_wait_result_bleeding_body`),
      items: [
        {
          label: t(`${RC2}.oi_wait_result_bleeding_i1_label`),
          detail: t(`${RC2}.oi_wait_result_bleeding_i1_detail`),
          status: "next",
        },
        {
          label: t(`${RC2}.oi_wait_result_bleeding_i2_label`),
          detail: t(`${RC2}.oi_wait_result_bleeding_i2_detail`),
          status: "waiting",
        },
        {
          label: t(`${RC2}.oi_wait_result_bleeding_i3_label`),
          detail: t(`${RC2}.oi_wait_result_bleeding_i3_detail`),
          status: "waiting",
        },
      ],
    },
  ];
}

export function todayRows(
  kind:
    | "stimulation"
    | "collection"
    | "wait"
    | "iui-wait"
    | "oi-wait"
    | "donor"
    | "iui-active",
  t: TranslationFunction = tEn,
): TodayRow[] {
  const RC = "homepage.review_concept.today_rows";
  const RC2 = "homepage.review_concept.inline";
  if (kind === "collection") {
    return [
      {
        id: "collection-appointment",
        time: "8:30 AM",
        title: t(`${RC}.collection.appt_title`),
        detail: t(`${RC}.collection.appt_detail`),
        icon: "sperm" as const,
        status: "scheduled",
        kind: "appointment",
        procedureName: "Sperm collection",
        appointmentResultType: "collection",
      },
      {
        id: "sample-processing",
        time: "11:00 AM",
        title: t(`${RC}.collection.proc_title`),
        detail: t(`${RC}.collection.proc_detail`),
        icon: "panel" as const,
        status: "lab",
        kind: "appointment",
        procedureName: "Semen analysis",
        appointmentResultType: "lab",
      },
      {
        id: "storage-confirmation",
        time: "4:00 PM",
        title: t(`${RC}.collection.storage_title`),
        detail: t(`${RC}.collection.storage_detail`),
        icon: "cryo" as const,
        status: "pending",
        kind: "appointment",
        procedureName: "Sperm freezing",
        appointmentResultType: "storage",
      },
    ];
  }

  if (kind === "wait") {
    return [
      {
        id: "progesterone-am",
        time: "8:00 AM",
        title: t(`${RC}.wait.prog_title`),
        detail: t(`${RC}.wait.prog_detail`),
        icon: "hormones" as const,
        status: "med",
        kind: "medication",
        medicationName: "Progesterone",
      },
      {
        id: "symptom-check",
        time: t(`${RC}.wait.any_time`),
        title: t(`${RC}.wait.symptom_title`),
        detail: t(`${RC}.wait.symptom_detail`),
        icon: "panel" as const,
        status: "support",
        kind: "task",
      },
      {
        id: "beta-blood-draw",
        time: "May 24",
        title: t(`${RC}.wait.beta_title`),
        detail: t(`${RC}.wait.beta_detail`),
        icon: "start" as const,
        status: "scheduled",
        kind: "appointment",
        procedureName: "Hormonal testing",
        appointmentResultType: "bloodwork",
      },
    ];
  }

  if (kind === "iui-wait") {
    return [
      {
        id: "luteal-support",
        time: "8:00 AM",
        title: t(`${RC2}.today_rows_iui_wait_luteal_title`),
        detail: t(`${RC2}.today_rows_iui_wait_luteal_detail`),
        icon: "hormones" as const,
        status: "med",
        kind: "medication",
        medicationName: "Progesterone",
      },
      {
        id: "avoid-early-testing",
        time: t(`${RC2}.today_rows_iui_wait_this_week`),
        title: t(`${RC2}.today_rows_iui_wait_avoid_title`),
        detail: t(`${RC2}.today_rows_iui_wait_avoid_detail`),
        icon: "panel" as const,
        status: "support",
        kind: "task",
      },
      {
        id: "iui-beta-blood-draw",
        time: "May 31",
        title: t(`${RC2}.today_rows_iui_wait_beta_title`),
        detail: t(`${RC2}.today_rows_iui_wait_beta_detail`),
        icon: "start" as const,
        status: "scheduled",
        kind: "appointment",
        procedureName: "Hormonal testing",
        appointmentResultType: "bloodwork",
      },
    ];
  }

  if (kind === "oi-wait") {
    return [
      {
        id: "oi-luteal-support",
        time: "8:00 AM",
        title: t(`${RC2}.today_rows_oi_wait_luteal_title`),
        detail: t(`${RC2}.today_rows_oi_wait_luteal_detail`),
        icon: "hormones" as const,
        status: "med",
        kind: "medication",
        medicationName: "Progesterone",
      },
      {
        id: "oi-avoid-early-testing",
        time: t(`${RC2}.today_rows_oi_wait_this_week`),
        title: t(`${RC2}.today_rows_oi_wait_avoid_title`),
        detail: t(`${RC2}.today_rows_oi_wait_avoid_detail`),
        icon: "panel" as const,
        status: "support",
        kind: "task",
      },
      {
        id: "oi-beta-blood-draw",
        time: "May 31",
        title: t(`${RC2}.today_rows_oi_wait_beta_title`),
        detail: t(`${RC2}.today_rows_oi_wait_beta_detail`),
        icon: "start" as const,
        status: "scheduled",
        kind: "appointment",
        procedureName: "Pregnancy blood test",
        appointmentResultType: "bloodwork",
      },
    ];
  }

  if (kind === "donor") {
    return [
      {
        id: "screening-labs",
        time: "10:00 AM",
        title: t(`${RC}.donor.screening_title`),
        detail: t(`${RC}.donor.screening_detail`),
        icon: "panel" as const,
        status: "today",
        kind: "appointment",
        procedureName: "Genetic carrier screening",
        appointmentResultType: "bloodwork",
      },
      {
        id: "coordinator-call",
        time: "2:30 PM",
        title: t(`${RC}.donor.coord_title`),
        detail: t(`${RC}.donor.coord_detail`),
        icon: "concierge" as const,
        status: "scheduled",
        kind: "appointment",
        procedureName: "Initial consultation",
        appointmentResultType: "call",
      },
    ];
  }

  if (kind === "iui-active") {
    return [
      {
        id: "ovidrel-trigger",
        time: "9:00 PM",
        title: t(`${RC2}.today_rows_iui_active_trigger_title`),
        detail: t(`${RC2}.today_rows_iui_active_trigger_detail`),
        icon: "hormones" as const,
        status: "tonight",
        kind: "medication",
        medicationName: "Ovidrel",
      },
      {
        id: "partner-sample",
        time: "May 20 8:00 AM",
        title: t(`${RC2}.today_rows_iui_active_partner_title`),
        detail: t(`${RC2}.today_rows_iui_active_partner_detail`),
        icon: "sperm" as const,
        status: "scheduled",
        kind: "appointment",
        procedureName: "Sperm collection",
        appointmentResultType: "collection",
      },
      {
        id: "iui-appointment",
        time: "May 20 9:30 AM",
        title: t(`${RC2}.today_rows_iui_active_iui_title`),
        detail: t(`${RC2}.today_rows_iui_active_iui_detail`),
        icon: "start" as const,
        status: "scheduled",
        kind: "appointment",
        procedureName: "IUI",
        appointmentResultType: "collection",
      },
    ];
  }

  return [
    {
      id: "gonal-f-am",
      time: "7:00 AM",
      title: t(`${RC2}.today_rows_stim_gonal_title`),
      detail: t(`${RC}.stim.gonal_detail`),
      icon: "hormones" as const,
      status: "med",
      kind: "medication",
      medicationName: "Gonal-F",
      medicationDose: "225 IU",
      medicationFormat: t(`${RC}.stim.subc_pen`),
      medicationForm: "pen",
      medicationAvailabilityStatus: "on-hand",
    },
    {
      id: "monitoring-visit",
      time: "8:20 AM",
      title: t(`${RC}.stim.monitoring_title`),
      detail: t(`${RC}.stim.monitoring_detail`),
      icon: "afc" as const,
      status: "appointment",
      kind: "monitoring",
      procedureName: "Transvaginal ultrasound",
      appointmentResultType: "monitoring",
    },
    {
      id: "cetrotide-pm",
      time: "9:00 PM",
      title: "Cetrotide",
      detail: t(`${RC}.stim.cetrotide_detail`),
      icon: "hormones" as const,
      status: "med",
      kind: "medication",
      medicationName: "Cetrotide",
      medicationDose: "0.25 mg",
      medicationFormat: t(`${RC}.stim.subc_vial`),
      medicationForm: "vial",
      medicationAvailabilityStatus: "low-stock",
    },
  ];
}

function timeline(
  label: string,
  detail: string,
  status: TimelineItem["status"],
): TimelineItem {
  return { label, detail, status };
}

function today(title: string, rows: TodayRow[], subtitle?: string): Widget {
  return { type: "today", title, subtitle, rows };
}

function actionRows(rows: OutcomeActionRow[]): Widget {
  return { type: "action-rows", rows };
}

function testResults(
  title: string,
  subtitle?: string,
  variant?: TestResultsVariant,
): Widget {
  return { type: "test-results", title, subtitle, icon: "panel", variant };
}

function dataStatus(
  title: string,
  detail: string,
  icon: OrganicMiniIconKind,
  action?: PlanAction,
): Widget {
  return { type: "data-status", title, detail, icon, action };
}

function semenAnalysisPending(): Widget {
  return { type: "semen-analysis-pending" };
}

function semenAnalysis(
  title: string,
  subtitle?: string,
  results?: SpermAnalysisResults | null,
  emptyCard?: { title: string; action: PlanAction },
): Widget {
  return { type: "semen-analysis", title, subtitle, results, emptyCard };
}

function spermResultPaths(): Widget {
  return { type: "sperm-result-paths" };
}

function stepExplainer(): Widget {
  return { type: "step-explainer" };
}

/**
 * The IVF Home from the trigger to the retrieval: the day ahead, how the step
 * she is on works, and the follicles and estimate that still describe what the
 * retrieval will collect. Stimulation's readings no longer apply.
 */
function buildIvfRetrievalSections(t: TranslationFunction): ConceptSection[] {
  return [
    buildActiveEmbryoStimulationSection(t),
    section(t(`${RC}.section_good_to_know_title`), "", [stepExplainer()]),
    buildActiveMonitoringSection(t),
    section("", "", [
      outcomeEstimateFunnel(
        t(`${RC}.cycle_may_become_title`),
        "genetics",
        "embryo-freezing",
        t(`${RC}.cycle_may_become_subtitle`),
      ),
    ]),
  ];
}

/**
 * The IVF Home on transfer day and the days before it: the day ahead, then the
 * lab's counts (added under the first section), then how the step she is on
 * works.
 */
function buildIvfTransferSections(t: TranslationFunction): ConceptSection[] {
  return [
    buildActiveEmbryoStimulationSection(t),
    section(t(`${RC}.section_good_to_know_title`), "", [stepExplainer()]),
  ];
}

function cryoVault(): Widget {
  return { type: "cryo-vault" };
}

function futureUseEstimate(primaryUnit?: "eggs" | "embryos"): Widget {
  return { type: "future-use-estimate", primaryUnit };
}

function spermCryosurvivalEstimate(): Widget {
  return { type: "sperm-cryosurvival-estimate" };
}

function taskToTodayRow(taskItem: Task, index: number): TodayRow {
  const ownerPrefix =
    taskItem.owner && taskItem.owner !== "You" ? `${taskItem.owner} - ` : "";
  return {
    id: `task-${taskItem.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${index}`,
    time: taskItem.due,
    title: taskItem.title,
    detail: `${ownerPrefix}${taskItem.detail}`,
    icon: "panel",
    status:
      taskItem.status === "done"
        ? "done"
        : taskItem.status === "locked"
          ? "locked"
          : taskItem.status === "waiting"
            ? "waiting"
            : "now",
    kind: "task",
  };
}

// A funnel with no card title/icon/action; the section title is the heading.
function bareFunnel(stages: FunnelStage[], subtitle?: string): Widget {
  return {
    type: "funnel",
    title: "",
    icon: "eggs",
    stages,
    subtitle,
    bare: true,
  };
}

function outcomeEstimateFunnel(
  title: string,
  icon: OrganicMiniIconKind,
  variant: "egg-freezing" | "embryo-freezing",
  subtitle?: string,
): Widget {
  return { type: "outcome-estimate-funnel", title, subtitle, icon, variant };
}

function cycleLogisticsCarousel(
  title: string,
  icon: OrganicMiniIconKind,
  cards: CycleLogisticsCard[],
  subtitle?: string,
): Widget {
  return { type: "cycle-logistics", title, subtitle, icon, cards };
}

function exploreOptions(
  title: string,
  subtitle: string,
  rows: OutcomeActionRow[],
): Widget {
  return { type: "explore-options", title, subtitle, icon: "learn", rows };
}

function whatToKnow(
  title: string,
  icon: OrganicMiniIconKind,
  cards: WhatToKnowCard[],
  subtitle?: string,
): Widget {
  return { type: "what-to-know", title, subtitle, icon, cards };
}

function timelineWidget(
  title: string,
  icon: OrganicMiniIconKind,
  items: TimelineItem[],
  subtitle?: string,
): Widget {
  return { type: "timeline", title, subtitle, icon, items };
}

function monitoringChart(
  t: TranslationFunction,
  title: string,
  subtitle?: string,
): Widget {
  const actions = buildActions(t);
  return {
    type: "monitoring-chart",
    title,
    subtitle,
    icon: "afc",
    primaryAction: actions.logMonitoring,
  };
}

function section(
  title: string,
  subtitle: string,
  widgets: Widget[],
  action?: PlanAction,
): ConceptSection {
  return { title, subtitle, widgets, action };
}

// All `active*` section builders below take a `TranslationFunction` so the
// inline section/widget strings are localized at render time. The top-level
// `useConcepts()` hook composes them with the per-variant inline keys.
const RC = "homepage.review_concept.inline" as const;

function buildActiveMonitoringSection(t: TranslationFunction): ConceptSection {
  return section(
    t(`${RC}.monitoring_progress_title`),
    t(`${RC}.monitoring_progress_subtitle`),
    [monitoringChart(t, t(`${RC}.monitoring_progress_title`))],
  );
}

function buildActiveStimulationSection(t: TranslationFunction): ConceptSection {
  return section(
    t(`${RC}.section_today_title`),
    t(`${RC}.section_today_subtitle`),
    [today(t(`${RC}.today_timeline_title`), todayRows("stimulation", t))],
    { label: t(`${RC}.calendar_action`), screen: "CareOverview" },
  );
}

function buildActiveStimulationTestingSection(
  t: TranslationFunction,
): ConceptSection {
  return section(
    t(`${RC}.section_testing_title`),
    t(`${RC}.section_testing_subtitle`),
    [
      testResults(
        t(`${RC}.test_results_title`),
        t(`${RC}.test_results_subtitle`),
      ),
    ],
    { label: t(`${RC}.all_tests_action`), screen: "AtHomeFertilityTesting" },
  );
}

/**
 * The monitoring section, worded for a cycle with no retrieval at the end.
 *
 * The stimulation version's subtitle ends on "and retrieval timing", which is
 * the one thing an ovulation induction cycle is not building towards. What it
 * is timing is the trigger, or the intercourse window.
 */
function buildActiveOvulationInductionMonitoringSection(
  t: TranslationFunction,
): ConceptSection {
  return section(
    t(`${RC}.monitoring_progress_title`),
    t(`${RC}.follow_growth_and_trigger_timing`),
    [monitoringChart(t, t(`${RC}.monitoring_progress_title`))],
  );
}

function buildActiveStimulationMoreSection(
  t: TranslationFunction,
): ConceptSection {
  return section(
    t(`${RC}.section_good_to_know_title`),
    t(`${RC}.section_good_to_know_subtitle`),
    [
      stepExplainer(),
      outcomeEstimateFunnel(
        t(`${RC}.cycle_may_freeze_title`),
        "eggs",
        "egg-freezing",
        t(`${RC}.cycle_may_freeze_subtitle`),
      ),
    ],
  );
}

function buildActiveEggFreezingSections(
  t: TranslationFunction,
): ConceptSection[] {
  return [
    buildActiveStimulationSection(t),
    buildActiveStimulationTestingSection(t),
    buildActiveMonitoringSection(t),
    buildActiveStimulationMoreSection(t),
  ];
}

/**
 * The day, on a post-cycle Home, as a block of its own.
 *
 * Shared by the three preservation journeys so they stay the one shape. The
 * cycle is over, so this is rarely anything — `HomepageReviewConceptHome` drops
 * the section when the real agenda comes back empty rather than parking a
 * permanent "nothing today" card under her results.
 */
/**
 * What a banked sample is good for, once the cycle is over.
 *
 * The figures are the ones the sperm-freezing journey already carries
 * (`plan/journeys/sperm-freezing-solo.ts`) and the guidance catalogue already
 * states, rather than new clinical claims: how much of a bank one attempt
 * consumes, that storage is an upkeep and not a one-off, and that a second bank
 * is measured against the ~74-day regeneration cycle.
 *
 * Deliberately not repeating the cryosurvival figure — the estimate card beside
 * the vault is where the thaw expectation belongs, and saying it twice would
 * make one of the two look like a different number.
 *
 * Card leaves keep the `badge`/`title`/`body`/`b1..` shape the six sibling card
 * builders in this file use: it is the component's own contract, and the
 * semantic identity sits in the parent namespace.
 */
function buildSpermPostCycleWhatToKnowCards(
  t: TranslationFunction,
): WhatToKnowCard[] {
  const RC2 = "homepage.review_concept.sperm_postcycle_cards";
  return [
    {
      badge: t(`${RC2}.one_attempt_at_a_time.badge`),
      title: t(`${RC2}.one_attempt_at_a_time.title`),
      body: t(`${RC2}.one_attempt_at_a_time.body`),
      bullets: [
        t(`${RC2}.one_attempt_at_a_time.b1`),
        t(`${RC2}.one_attempt_at_a_time.b2`),
        t(`${RC2}.one_attempt_at_a_time.b3`),
      ],
      tone: "steady",
    },
    {
      badge: t(`${RC2}.storage_is_an_upkeep.badge`),
      title: t(`${RC2}.storage_is_an_upkeep.title`),
      body: t(`${RC2}.storage_is_an_upkeep.body`),
      bullets: [
        t(`${RC2}.storage_is_an_upkeep.b1`),
        t(`${RC2}.storage_is_an_upkeep.b2`),
        t(`${RC2}.storage_is_an_upkeep.b3`),
      ],
      tone: "caution",
    },
    {
      badge: t(`${RC2}.banking_more_takes_a_cycle.badge`),
      title: t(`${RC2}.banking_more_takes_a_cycle.title`),
      body: t(`${RC2}.banking_more_takes_a_cycle.body`),
      bullets: [
        t(`${RC2}.banking_more_takes_a_cycle.b1`),
        t(`${RC2}.banking_more_takes_a_cycle.b2`),
        t(`${RC2}.banking_more_takes_a_cycle.b3`),
      ],
      tone: "steady",
    },
  ];
}

/** Static English sample for the component canvas. */
export const spermPostCycleWhatToKnowCards: WhatToKnowCard[] =
  buildSpermPostCycleWhatToKnowCards(tEn);

function buildPostCycleTodaySection(t: TranslationFunction): ConceptSection {
  return section(
    t(`${RC}.section_today_title`),
    t(`${RC}.section_today_subtitle`),
    [today(t(`${RC}.today_schedule_title`), [])],
    { label: t(`${RC}.calendar_action`), screen: "CareOverview" },
  );
}

function buildActiveEmbryoStimulationSection(
  t: TranslationFunction,
): ConceptSection {
  return section(
    t(`${RC}.section_today_title`),
    t(`${RC}.section_today_subtitle`),
    [today(t(`${RC}.today_timeline_title`), todayRows("stimulation", t))],
    { label: t(`${RC}.calendar_action`), screen: "CareOverview" },
  );
}

function buildActiveEmbryoTestingSection(
  t: TranslationFunction,
): ConceptSection {
  return section(
    t(`${RC}.section_testing_title`),
    t(`${RC}.section_testing_subtitle`),
    [
      testResults(
        t(`${RC}.test_results_title`),
        t(`${RC}.test_results_subtitle`),
      ),
    ],
    { label: t(`${RC}.all_tests_action`), screen: "AtHomeFertilityTesting" },
  );
}

function buildActiveEmbryoMoreSection(t: TranslationFunction): ConceptSection {
  return section(
    t(`${RC}.section_good_to_know_title`),
    t(`${RC}.section_good_to_know_subtitle`),
    [
      stepExplainer(),
      outcomeEstimateFunnel(
        t(`${RC}.cycle_may_become_title`),
        "genetics",
        "embryo-freezing",
        t(`${RC}.cycle_may_become_subtitle`),
      ),
    ],
  );
}

function buildActiveEmbryoFreezingSections(
  t: TranslationFunction,
): ConceptSection[] {
  return [
    buildActiveEmbryoStimulationSection(t),
    buildActiveEmbryoTestingSection(t),
    buildActiveMonitoringSection(t),
    buildActiveEmbryoMoreSection(t),
  ];
}

/**
 * Where the lab's results come from. Recovery and the warning signs to call
 * about are the retrieval explainer's, shown beside it.
 */
function buildPostRetrievalLabCard(t: TranslationFunction): WhatToKnowCard[] {
  const RC2 = `${RC}.post_retrieval`;
  return [
    {
      badge: t(`${RC2}.lab_badge`),
      title: t(`${RC2}.lab_updates_come_from_your_clinic`),
      body: t(`${RC2}.each_step_shows_once_the_lab_records_it`),
      bullets: [
        t(`${RC2}.fertilization_and_development_as_recorded`),
        t(`${RC2}.eggs_frozen_and_where_they_are_stored`),
        t(`${RC2}.not_recorded_stays_blank`),
      ],
      tone: "steady",
    },
  ];
}

function buildPostRetrievalSections(t: TranslationFunction): ConceptSection[] {
  return [
    section(
      t(`${RC}.post_retrieval_today_section_title`),
      t(`${RC}.post_retrieval_today_section_subtitle`),
      [today(t(`${RC}.today_timeline_title`), [])],
      { label: t(`${RC}.calendar_action`), screen: "CareOverview" },
    ),
    section(
      t(`${RC}.post_retrieval_guidance_section_title`),
      t(`${RC}.post_retrieval_guidance_section_subtitle`),
      [
        stepExplainer(),
        whatToKnow(
          t(`${RC}.post_retrieval_guidance_title`),
          "panel",
          buildPostRetrievalLabCard(t),
          t(`${RC}.post_retrieval_guidance_subtitle`),
        ),
      ],
    ),
  ];
}

function buildActiveIuiTodaySection(t: TranslationFunction): ConceptSection {
  return section(
    t(`${RC}.section_today_title`),
    t(`${RC}.section_today_iui_subtitle`),
    [today(t(`${RC}.today_timeline_title`), todayRows("iui-active", t))],
    { label: t(`${RC}.calendar_action`), screen: "CareOverview" },
  );
}

function buildActiveIuiTestingSection(t: TranslationFunction): ConceptSection {
  return section(
    t(`${RC}.section_testing_title`),
    t(`${RC}.section_testing_iui_subtitle`),
    [
      testResults(
        t(`${RC}.test_results_title`),
        t(`${RC}.test_results_iui_subtitle`),
        "iui-active",
      ),
    ],
    { label: t(`${RC}.all_tests_action`), screen: "AtHomeFertilityTesting" },
  );
}

function buildActiveIuiMoreSection(t: TranslationFunction): ConceptSection {
  return section(
    t(`${RC}.section_good_to_know_title`),
    t(`${RC}.section_good_to_know_iui_subtitle`),
    [stepExplainer()],
  );
}

function buildActiveIuiSections(t: TranslationFunction): ConceptSection[] {
  return [
    buildActiveIuiTodaySection(t),
    buildActiveIuiTestingSection(t),
    buildActiveMonitoringSection(t),
    buildActiveIuiMoreSection(t),
  ];
}

// Static English fallback for non-React consumers (e.g. fixture data in
// CareReviewConceptScreen). Localized rendering should go through
// `useConcepts()` instead.
export const activeEggFreezingSections: ConceptSection[] =
  buildActiveEggFreezingSections(tEn);

function getSemenAnalysisValue(result: {
  patientValue: string | null;
  doctorValue: string | null;
  value: string | null;
  unit: string | null;
}): string | undefined {
  const rawValue = [result.doctorValue, result.patientValue, result.value].find(
    (value) => value != null && value.trim().length > 0,
  );
  if (!rawValue) return undefined;

  const value = rawValue.trim();
  const unit = result.unit?.trim();
  if (!unit || value.toLowerCase().endsWith(unit.toLowerCase())) {
    return value;
  }
  return `${value} ${unit}`;
}

function getSemenAnalysisField(
  name: string | null,
  localizedLabels?: Partial<
    Record<"volume" | "concentration" | "motility" | "morphology", string>
  >,
): keyof SpermAnalysisResults | undefined {
  const normalize = (value: string) =>
    value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  const normalized = normalize(name ?? "");
  const matches = (...candidates: (string | undefined)[]) =>
    candidates.some((candidate) => {
      const normalizedCandidate = candidate ? normalize(candidate) : "";
      return (
        normalizedCandidate.length > 0 &&
        (normalized === normalizedCandidate ||
          normalized.includes(normalizedCandidate))
      );
    });

  if (matches("volume", localizedLabels?.volume)) return "volume";
  if (
    matches("concentration", localizedLabels?.concentration) ||
    normalized.includes("count")
  ) {
    return "concentration";
  }
  if (matches("motility", localizedLabels?.motility)) {
    return normalized.includes("status")
      ? "motilityStatus"
      : "progressiveMotility";
  }
  if (matches("morphology", localizedLabels?.morphology)) {
    return "normalMorphology";
  }
  if (normalized.includes("dna") && normalized.includes("fragment")) {
    return "dnaFragmentation";
  }
  if (normalized.includes("methylation")) return "methylation";
  if (normalized.includes("oxidative") && normalized.includes("stress")) {
    return "oxidativeStress";
  }
  if (normalized.includes("vitality")) return "vitality";
  if (normalized.includes("aneuploidy")) return "aneuploidy";
  if (normalized.includes("acrosome")) return "acrosome";
  if (normalized.includes("head")) return "head";
  if (normalized.includes("neck")) return "neck";
  if (normalized.includes("tail")) return "tail";
  return undefined;
}

/**
 * The analysis she recorded on her own cycle.
 *
 * The card is fed from `semenFluidAnalysis` test-result rows, which is what a
 * filed lab report becomes. The Analysis step of the treatment hub writes
 * somewhere else entirely, the `spermiograms` columns on the cycle's phase, so
 * a patient who typed her figures in the hub still met an empty card here.
 *
 * The row is picked by what it holds (`semenAnalysisResultsFromSpermiograms`):
 * the collection step's row carries no figures and must not stand in for the
 * analysis. The cycle is the one the wait or post-cycle Home is about
 * (`spermAnalysisCycleId`), open while the lab works and closed after.
 */
function useSpermiogramAnalysisResults(): SpermAnalysisResults | null {
  const spermCycleId = useSpermAnalysisCycleId();
  const phases = useCyclePhases(spermCycleId);

  return useMemo(
    () =>
      semenAnalysisResultsFromSpermiograms(
        (phases.data ?? []).flatMap((phase) => phase.spermiograms ?? []),
      ),
    [phases.data],
  );
}

/**
 * The analysis she recorded on her stored samples.
 *
 * The Cryo Vault's intake — the "+" on "What you stored" leads straight to it —
 * writes the semen analysis onto the specimen: a spermiogram linked to each
 * vial, and vitality and DNA fragmentation as specimen-level metrics. Neither
 * of the two stores above ever held it, so figures typed there stayed invisible
 * to the results section a few blocks down. The rule lives in
 * `semenAnalysisResultsFromSpecimens`, where it is tested.
 */
function useVaultAnalysisResults(): SpermAnalysisResults | null {
  const spermCycleId = useSpermAnalysisCycleId();
  const { data: specimens } = useSpecimens();
  return useMemo(
    () => semenAnalysisResultsFromSpecimens(specimens, spermCycleId),
    [specimens, spermCycleId],
  );
}

/**
 * Semen analysis values are stored as one test-result row per parameter.
 * Keep the homepage card backed by the most recently dated analysis rather
 * than the static concept fixture values used by the rest of this screen.
 */
function useSemenAnalysisResults(): SpermAnalysisResults | null {
  const t = useTranslations();
  const { data: testResults } = useTestResults();
  const fromSpermiogram = useSpermiogramAnalysisResults();
  const fromVault = useVaultAnalysisResults();
  const localizedLabels = useMemo(
    () => ({
      volume: t("documents.semen_result.volume"),
      concentration: t("documents.semen_result.concentration"),
      motility: t("documents.semen_result.motility"),
      morphology: t("documents.semen_result.morphology"),
    }),
    [t],
  );

  const fromTestResults = useMemo(() => {
    if (!testResults) return null;

    const semenResults = testResults.filter(
      (result) => result.type === "semenFluidAnalysis",
    );
    if (semenResults.length === 0) return null;

    const getDate = (result: (typeof semenResults)[number]) =>
      result.date?.trim() || null;
    const sorted = [...semenResults].sort((a, b) => {
      const aDate = getDate(a) ?? a.createdAt ?? "";
      const bDate = getDate(b) ?? b.createdAt ?? "";
      return bDate.localeCompare(aDate);
    });

    // Add-document saves all parameters from one report with the same date.
    // For records without a report date, use one undated group so a partially
    // populated analysis can still be shown instead of dropping its fields.
    const latestDate = getDate(sorted[0]!);
    const latestGroup = sorted.filter(
      (result) => getDate(result) === latestDate,
    );
    const mapped: SpermAnalysisResults = {};

    for (const result of latestGroup) {
      const field = getSemenAnalysisField(result.name, localizedLabels);
      const displayValue = getSemenAnalysisValue(result);
      if (field && displayValue) mapped[field] = displayValue;
    }

    return Object.keys(mapped).length > 0 ? mapped : null;
  }, [localizedLabels, testResults]);

  // A filed lab report outranks a hand-entered figure; hers answers when no
  // report has been filed, which until now left the card permanently empty.
  // The hub's figures come before the vault's: the hub is the cycle's own
  // record, the vault describes the samples it left behind.
  return fromTestResults ?? fromSpermiogram ?? fromVault;
}

function buildPostProcedureOutcomeConcept(
  t: TranslationFunction,
  contentKey: PostProcedureOutcomeContentKey,
  resultStatus:
    "positive" | "negative" | "uncertain" | "adverse" | "unavailable",
): Concept {
  const ns = `homepage.review_concept.post_procedure_outcome.${contentKey}.${resultStatus}`;
  const shared = "homepage.review_concept.post_procedure_outcome.shared";

  if (
    resultStatus === "unavailable" ||
    resultStatus === "uncertain" ||
    resultStatus === "adverse"
  ) {
    return {
      eyebrow: t(`${ns}.eyebrow`),
      title: t(`${ns}.title`),
      summary: t(`${ns}.summary`),
      icon: "process",
      sections: [
        section(t(`${ns}.section_title`), t(`${ns}.section_subtitle`), [
          today(t(`${ns}.today_section_title`), []),
          actionRows([
            {
              title: t(`${shared}.stage_action_review_records`),
              detail: t(`${shared}.stage_action_review_records_detail`),
              icon: "medical-records",
              action: {
                label: t(`${shared}.stage_action_review_records`),
                signal: "open-recorded-cycle",
              },
            },
            {
              title: t(`${shared}.stage_action_schedule_appointment`),
              detail: t(`${shared}.stage_action_schedule_appointment_detail`),
              icon: "clinics",
              action: {
                label: t(`${shared}.stage_action_schedule_appointment`),
                screen: "CareOverview",
                params: { focus: "appointment" },
              },
            },
            {
              title: t(`${shared}.stage_action_save_question`),
              detail: t(`${shared}.stage_action_save_question_detail`),
              icon: "concierge",
              action: {
                label: t(`${shared}.stage_action_save_question`),
                signal: "open-ai-chat",
                params: {
                  initialPrompt: t(
                    `${shared}.stage_action_save_question_prompt`,
                  ),
                },
              },
            },
          ]),
        ]),
      ],
    };
  }

  return {
    eyebrow: t(`${ns}.eyebrow`),
    // The negative heading is shared across modalities — see
    // `take_a_breath_after_this_cycle`. Reading `${ns}.title` here survived the
    // move and now resolves to nothing: `t` returns the path itself for a missing
    // key, so this would render a raw i18n path the day something displays it.
    title:
      resultStatus === "negative"
        ? t(`${shared}.take_a_breath_after_this_cycle`)
        : t(`${ns}.title`),
    summary: t(`${ns}.summary`),
    icon: resultStatus === "positive" ? "start" : "process",
    sections: [
      section(t(`${ns}.section_title`), t(`${ns}.section_subtitle`), [
        // The outcome page must not invent tasks. Real appointments and
        // medications are supplied by PlanHomeScreen; when there is no
        // scheduled follow-up, Today renders its canonical empty state.
        today(t(`${ns}.today_section_title`), []),
      ]),
    ],
  };
}

function buildConcepts(
  t: TranslationFunction,
  postCycleFunnels: PostCycleFunnels,
  semenAnalysisResults?: SpermAnalysisResults | null,
  cycleStatusReviewKind?: CycleStatusReviewKind | null,
  betaDateLabel?: string | null,
  viewerIsSupporting = false,
): Partial<Record<HomepageReviewConceptVariant, Concept>> {
  const cycleStatusReviewCopy = cycleStatusReviewKind
    ? `${RC}.cycle_status_review_${cycleStatusReviewKind}`
    : `${RC}.cycle_status_review`;
  const cycleStatusReviewHeroCopy = cycleStatusReviewKind
    ? cycleStatusReviewCopy
    : `${RC}.cycle_status_review`;
  // Named because the negative route appends a funnel section to it below, and
  // building the concept twice to do that would let the two copies drift.
  const ivfNegativeConcept = buildPostProcedureOutcomeConcept(
    t,
    "ivf",
    "negative",
  );

  return {
    "egg-freezing-active": {
      eyebrow: t(`${RC}.egg_freezing_active_eyebrow`),
      title: t(`${RC}.egg_freezing_active_title`),
      summary: t(`${RC}.egg_freezing_active_summary`),
      icon: "eggs",
      sections: buildActiveEggFreezingSections(t),
    },
    "egg-freezing-postcycle": {
      eyebrow: t(`${RC}.egg_postcycle_eyebrow`),
      title: t(`${RC}.egg_postcycle_title`),
      summary: t(`${RC}.egg_postcycle_summary`),
      icon: "cryo",
      sections: [
        /**
         * The order the page reads in: today, what she has, what she can do,
         * the detail, then the rest.
         *
         * The day leads because anything in it is the only thing here with a
         * date on it — and it costs nothing on the days it is empty, because it
         * is the one block drawn conditionally. That is also why it needs its
         * own heading rather than a place inside the results section, where a
         * stimulation medication rendered under "Egg freezing results", above
         * the mature-and-vitrified counts.
         */
        buildPostCycleTodaySection(t),
        // What she has, and what it is worth to her later: one question, so one
        // section. They sat in two, with the estimate stranded after the page's
        // detail.
        section(
          t(`${RC}.postcycle_what_you_stored`),
          t(`${RC}.postcycle_what_you_stored_detail_egg`),
          [cryoVault(), futureUseEstimate()],
        ),
        buildPostCycleWhatNowSection(t, "egg", viewerIsSupporting),
        section(
          t(`${RC}.egg_postcycle_section_title`),
          t(`${RC}.egg_postcycle_section_subtitle`),
          [
            // Observed results only (#1817): omitted when no retrieval
            // metrics were recorded — a missing record is not a zero.
            ...(postCycleFunnels.egg ? [bareFunnel(postCycleFunnels.egg)] : []),
          ],
        ),
        buildPostCycleExploreSection(t, viewerIsSupporting),
      ],
    },
    "embryo-freezing-active": {
      eyebrow: t(`${RC}.embryo_freezing_active_eyebrow`),
      title: t(`${RC}.embryo_freezing_active_title`),
      summary: t(`${RC}.embryo_freezing_active_summary`),
      icon: "genetics",
      sections: buildActiveEmbryoFreezingSections(t),
    },
    "embryo-freezing-postcycle": {
      eyebrow: t(`${RC}.embryo_postcycle_eyebrow`),
      title: t(`${RC}.embryo_postcycle_title`),
      summary: t(`${RC}.embryo_postcycle_summary`),
      icon: "genetics",
      sections: [
        // Same order as the other two journeys — see the note there.
        buildPostCycleTodaySection(t),
        section(
          t(`${RC}.postcycle_what_you_stored`),
          t(`${RC}.postcycle_what_you_stored_detail_embryo`),
          [cryoVault(), futureUseEstimate("embryos")],
        ),
        buildPostCycleWhatNowSection(t, "embryo", viewerIsSupporting),
        section(
          t(`${RC}.embryo_postcycle_section_title`),
          t(`${RC}.embryo_postcycle_section_subtitle`),
          [
            /**
             * The counts lead, the lab results follow.
             *
             * This section is titled "what this cycle gave" and its subtitle
             * names the lab stages from fertilization to storage — so what
             * belongs at the top is the outcome, "6 fertilized, 8 blastocysts",
             * and not the hormone values that were monitored on the way to it.
             * Read the other way round, three LH and oestradiol rows pushed the
             * only figures she came here for below the fold.
             *
             * Observed results only (#1817): omitted when no retrieval metrics
             * were recorded — a missing record is not a zero.
             */
            ...(postCycleFunnels.embryo
              ? [bareFunnel(postCycleFunnels.embryo)]
              : []),
            testResults(
              t(`${RC}.embryo_postcycle_testing_title`),
              t(`${RC}.embryo_postcycle_testing_subtitle`),
            ),
          ],
          {
            label: t(`${RC}.all_tests_action`),
            screen: "AtHomeFertilityTesting",
          },
        ),
        buildPostCycleExploreSection(t, viewerIsSupporting),
      ],
    },
    "sperm-freezing-active": {
      eyebrow: t(`${RC}.sperm_active_eyebrow`),
      title: t(`${RC}.sperm_active_title`),
      summary: t(`${RC}.sperm_active_summary`),
      icon: "sperm",
      sections: [
        section(
          t(`${RC}.section_today_title`),
          t(`${RC}.section_today_subtitle`),
          [today(t(`${RC}.today_schedule_title`), [])],
          buildActions(t).calendar,
        ),
      ],
    },
    "sperm-freezing-wait": {
      eyebrow: t(`${RC}.sperm_freezing_wait_eyebrow`),
      title: t(`${RC}.sperm_freezing_wait_title`),
      summary: t(`${RC}.sperm_freezing_wait_summary`),
      icon: "sperm",
      sections: [
        // "Today", as on every other Home: the day's real agenda, and the
        // analysis the lab is still running as part of the day.
        section(
          t(`${RC}.section_today_title`),
          t(`${RC}.section_today_subtitle`),
          [today(t(`${RC}.today_schedule_title`), []), semenAnalysisPending()],
          buildActions(t).calendar,
        ),
      ],
    },
    "sperm-freezing-postcycle": {
      eyebrow: t(`${RC}.sperm_freezing_postcycle_eyebrow`),
      title: t(`${RC}.sperm_freezing_postcycle_title`),
      summary: t(`${RC}.sperm_freezing_postcycle_summary`),
      icon: "cryo",
      sections: [
        // Same order as the other two journeys — see the note there.
        buildPostCycleTodaySection(t),
        /**
         * What he has and what it is worth to him later, as one section.
         *
         * The cryosurvival estimate used to sit in a section of its own, three
         * blocks lower, because it reads after the lab figures it derives from.
         * That put the vault's "what you stored" and its own answer to "what
         * could this give me later" at opposite ends of the page, when egg and
         * embryo freezing ask that as one question beside the vault. It is one
         * question here too.
         */
        section(
          t(`${RC}.postcycle_what_you_stored`),
          t(`${RC}.postcycle_what_you_stored_detail_sperm`),
          [cryoVault(), spermCryosurvivalEstimate()],
        ),
        buildPostCycleWhatNowSection(t, "sperm", viewerIsSupporting),
        /**
         * What the cycle gave, which for sperm is the lab's reading of the
         * sample rather than a funnel of counts.
         *
         * There is deliberately no count block: the number of samples banked in
         * this cycle is what the hero already says, so a metrics card under it
         * would state the same figure twice. The subtitle used to describe
         * storage — "what was banked and where" — which the vault section above
         * now answers; it names the lab figures this section actually holds.
         */
        section(
          t(`${RC}.sperm_postcycle_section_title`),
          t(`${RC}.sperm_postcycle_lab_reading_of_the_sample`),
          [
            semenAnalysis(
              t(`${RC}.sperm_postcycle_semen_title`),
              undefined,
              semenAnalysisResults,
              // The cycle is done, so the analysis happened; until its figures
              // are added, the card asks for them rather than offering a test.
              {
                title: t(
                  "homepage.sperm_analysis_skeleton.add_the_figures_from_your_clinic",
                ),
                action: {
                  label: t("homepage.shared.upload_semen_analysis_results"),
                  signal: "open-document-upload",
                  params: { initialCategory: "medical_records" },
                },
              },
            ),
          ],
        ),
        // What a banked sample is actually good for. The three questions a
        // patient asks once the cycle is over and nothing presses: how much of
        // it does one attempt use, does it keep, and what if I want more.
        section(
          t(`${RC}.sperm_postcycle_good_to_know`),
          t(`${RC}.sperm_postcycle_good_to_know_subtitle`),
          [
            whatToKnow(
              t(`${RC}.sperm_postcycle_what_a_bank_is_for`),
              "sperm",
              buildSpermPostCycleWhatToKnowCards(t),
            ),
          ],
        ),
        buildPostCycleExploreSection(t, viewerIsSupporting),
      ],
    },
    "ivf-active": {
      eyebrow: t(`${RC}.ivf_active_eyebrow`),
      title: t(`${RC}.ivf_active_title`),
      summary: t(`${RC}.ivf_active_summary`),
      icon: "genetics",
      sections: [
        buildActiveEmbryoStimulationSection(t),
        buildActiveEmbryoTestingSection(t),
        buildActiveMonitoringSection(t),
        buildActiveEmbryoMoreSection(t),
      ],
    },
    "post-retrieval": {
      eyebrow: t(`${RC}.post_retrieval_eyebrow`),
      title: t(`${RC}.post_retrieval_title`),
      summary: t(`${RC}.post_retrieval_summary`),
      icon: "genetics",
      sections: buildPostRetrievalSections(t),
    },
    "fet-active": {
      eyebrow: t(`${RC}.fet_active_plan_eyebrow`),
      title: t(`${RC}.fet_active_plan_heading`),
      summary: t(`${RC}.fet_active_plan_summary`),
      icon: "process",
      sections: [
        section(
          t(`${RC}.section_today_title`),
          t(`${RC}.section_today_subtitle`),
          [today(t(`${RC}.today_schedule_title`), [])],
          buildActions(t).calendar,
        ),
        // The lining is what lining preparation is for, and the monitoring
        // card already reads and adds lining scans.
        section(t("homepage.fet_lining_prep.lining_checks"), "", [
          monitoringChart(t, t("homepage.fet_lining_prep.lining_checks")),
        ]),
      ],
    },
    "cycle-status-review": {
      eyebrow: t(`${RC}.cycle_status_review_eyebrow`),
      title: t(`${cycleStatusReviewHeroCopy}_title`),
      summary: t(`${cycleStatusReviewHeroCopy}_summary`),
      icon: "process",
      sections: [
        section(
          t(`${RC}.cycle_status_review_section_title`),
          t(`${RC}.cycle_status_review_section_subtitle`),
          [
            today(t(`${RC}.cycle_status_review_today_title`), []),
            actionRows([
              {
                title: t(`${RC}.cycle_status_review_records_title`),
                detail: t(`${RC}.cycle_status_review_records_detail`),
                icon: "medical-records",
                action: {
                  label: t(`${RC}.cycle_status_review_records_action`),
                  screen: "CycleDetailScreen",
                },
              },
              {
                title: t(`${RC}.cycle_status_review_clinic_title`),
                detail: t(`${RC}.cycle_status_review_clinic_detail`),
                icon: "clinics",
                action: {
                  label: t(`${RC}.cycle_status_review_clinic_action`),
                  screen: "CareOverview",
                  params: { focus: "appointment" },
                },
              },
              {
                title: t(`${RC}.cycle_status_review_question_title`),
                detail: t(`${RC}.cycle_status_review_question_detail`),
                icon: "concierge",
                action: {
                  label: t(`${RC}.cycle_status_review_question_action`),
                  signal: "open-ai-chat",
                  params: {
                    initialPrompt: t(
                      `${cycleStatusReviewHeroCopy}_question_prompt`,
                    ),
                  },
                },
              },
            ]),
          ],
        ),
      ],
    },
    "beta-wait": {
      eyebrow: t(`${RC}.beta_wait_eyebrow`),
      title: t(`${RC}.beta_wait_title`),
      summary: t(`${RC}.beta_wait_summary`),
      icon: "start",
      sections: [
        section(
          t(`${RC}.beta_wait_section_today_title`),
          t(`${RC}.beta_wait_section_today_subtitle`),
          [today(t(`${RC}.beta_wait_today_title`), todayRows("wait", t))],
          buildActions(t).calendar,
        ),
        section(
          t(`${RC}.beta_wait_section_testing_title`),
          t(`${RC}.beta_wait_section_testing_subtitle`),
          [
            cycleLogisticsCarousel(
              t(`${RC}.beta_wait_result_plan_title`),
              "start",
              buildBetaWaitResultPlanCards(t, betaDateLabel),
              t(`${RC}.beta_wait_result_plan_subtitle`),
            ),
          ],
          {
            label: t(`${RC}.all_tests_action`),
            screen: "AtHomeFertilityTesting",
          },
        ),
        section(
          t(`${RC}.beta_wait_section_more_title`),
          t(`${RC}.beta_wait_section_more_subtitle`),
          [stepExplainer()],
        ),
      ],
    },
    "fet-positive": buildPostProcedureOutcomeConcept(t, "fet", "positive"),
    "fet-negative": buildPostProcedureOutcomeConcept(t, "fet", "negative"),
    "fet-uncertain": buildPostProcedureOutcomeConcept(t, "fet", "uncertain"),
    "fet-adverse": buildPostProcedureOutcomeConcept(t, "fet", "adverse"),
    "fet-unavailable": buildPostProcedureOutcomeConcept(
      t,
      "fet",
      "unavailable",
    ),
    "iui-positive": buildPostProcedureOutcomeConcept(t, "iui", "positive"),
    "iui-negative": buildPostProcedureOutcomeConcept(t, "iui", "negative"),
    "iui-uncertain": buildPostProcedureOutcomeConcept(t, "iui", "uncertain"),
    "iui-adverse": buildPostProcedureOutcomeConcept(t, "iui", "adverse"),
    "iui-unavailable": buildPostProcedureOutcomeConcept(
      t,
      "iui",
      "unavailable",
    ),
    "oi-positive": buildPostProcedureOutcomeConcept(t, "oi", "positive"),
    "oi-negative": buildPostProcedureOutcomeConcept(t, "oi", "negative"),
    "oi-uncertain": buildPostProcedureOutcomeConcept(t, "oi", "uncertain"),
    "oi-adverse": buildPostProcedureOutcomeConcept(t, "oi", "adverse"),
    "oi-unavailable": buildPostProcedureOutcomeConcept(t, "oi", "unavailable"),
    "ivf-uncertain": buildPostProcedureOutcomeConcept(t, "ivf", "uncertain"),
    "ivf-adverse": buildPostProcedureOutcomeConcept(t, "ivf", "adverse"),
    "ivf-unavailable": buildPostProcedureOutcomeConcept(
      t,
      "ivf",
      "unavailable",
    ),
    "ivf-positive": buildPostProcedureOutcomeConcept(t, "ivf", "positive"),
    "ivf-negative": postCycleFunnels.ivf
      ? {
          ...ivfNegativeConcept,
          sections: [
            ...ivfNegativeConcept.sections,
            section(t(`${RC}.ivf_negative_funnel_title`), "", [
              bareFunnel(postCycleFunnels.ivf),
            ]),
          ],
        }
      : ivfNegativeConcept,
    "iui-active": {
      eyebrow: t(`${RC}.iui_active_eyebrow`),
      title: t(`${RC}.iui_active_title`),
      summary: t(`${RC}.iui_active_summary`),
      icon: "hormones",
      sections: buildActiveIuiSections(t),
    },
    "iui-wait": {
      eyebrow: t(`${RC}.iui_wait_eyebrow`),
      title: t(`${RC}.iui_wait_title`),
      summary: t(`${RC}.iui_wait_summary`),
      icon: "start",
      sections: [
        section(
          t(`${RC}.iui_wait_section_today_title`),
          t(`${RC}.iui_wait_section_today_subtitle`),
          [today(t(`${RC}.iui_wait_today_title`), todayRows("iui-wait", t))],
          buildActions(t).calendar,
        ),
        section(
          t(`${RC}.iui_wait_section_testing_title`),
          t(`${RC}.iui_wait_section_testing_subtitle`),
          [
            cycleLogisticsCarousel(
              t(`${RC}.iui_wait_result_plan_title`),
              "hormones",
              buildIuiWaitResultPlanCards(t, betaDateLabel),
              t(`${RC}.iui_wait_result_plan_subtitle`),
            ),
          ],
          {
            label: t(`${RC}.all_tests_action`),
            screen: "AtHomeFertilityTesting",
          },
        ),
        section(
          t(`${RC}.iui_wait_section_more_title`),
          t(`${RC}.iui_wait_section_more_subtitle`),
          [stepExplainer()],
        ),
      ],
    },
    /**
     * A stimulated cycle with no retrieval at the end of it.
     *
     * Assembled from the generic stimulation sections rather than the IVF or
     * IUI ones: the days are the same — medications, scans, a response to
     * follow — but IVF's sections speak of a retrieval and a lab timeline, and
     * IUI's of a partner sample and an insemination. Neither happens here.
     *
     * Its "good to know" is the step explainer alone, not the generic
     * stimulation section: that one carries an egg-freezing outcome projection
     * ("what this cycle may freeze", eggs retrieved through to live births),
     * and an ovulation induction cycle retrieves and freezes nothing. The hero
     * menu already agrees: `canShowOutcomeFunnel` tests the variant name and
     * drops the outlook item for this one.
     */
    "oi-active": {
      eyebrow: t(`${RC}.oi_active_eyebrow`),
      title: t(`${RC}.oi_active_title`),
      summary: t(`${RC}.oi_active_summary`),
      icon: "hormones",
      sections: [
        buildActiveStimulationSection(t),
        buildActiveStimulationTestingSection(t),
        buildActiveOvulationInductionMonitoringSection(t),
        section(t(`${RC}.section_good_to_know_title`), "", [stepExplainer()]),
      ],
    },
    "oi-wait": {
      eyebrow: t(`${RC}.oi_wait_eyebrow`),
      title: t(`${RC}.oi_wait_title`),
      summary: t(`${RC}.oi_wait_summary`),
      icon: "start",
      sections: [
        section(
          t(`${RC}.oi_wait_section_today_title`),
          t(`${RC}.oi_wait_section_today_subtitle`),
          [today(t(`${RC}.oi_wait_today_title`), todayRows("oi-wait", t))],
          buildActions(t).calendar,
        ),
        section(
          t(`${RC}.oi_wait_section_testing_title`),
          t(`${RC}.oi_wait_section_testing_subtitle`),
          [
            cycleLogisticsCarousel(
              t(`${RC}.oi_wait_result_plan_title`),
              "hormones",
              buildOiWaitResultPlanCards(t, betaDateLabel),
              t(`${RC}.oi_wait_result_plan_subtitle`),
            ),
          ],
          {
            label: t(`${RC}.all_tests_action`),
            screen: "AtHomeFertilityTesting",
          },
        ),
        section(
          t(`${RC}.oi_wait_section_more_title`),
          t(`${RC}.oi_wait_section_more_subtitle`),
          [stepExplainer()],
        ),
      ],
    },
    // Only a placeholder for the variant map: the Home builds this concept
    // from her records (see `intendedParentFacts`), loading until they're in.
    "intended-parent": buildIntendedParentConcept(t, null, {
      failed: false,
      onRetry: () => undefined,
      isRetrying: false,
    }),
  };
}

/**
 * What the Home knows about an intended parent's surrogacy, all of it read from
 * her own records.
 */
export type IntendedParentFacts = {
  /** Embryos stored in her vault. */
  embryos: number;
  /** Consent and legal documents she has uploaded. */
  legalDocuments: number;
  /** Whether a gestational carrier is linked to her plan. */
  carrierConnected: boolean;
  /** Where the surrogacy relationship stands, as the API records it. */
  participantStatus: ParticipantStatus | null;
};

const SURROGACY_HOME = "homepage.surrogacy_home";

/**
 * The intended parent's Home: her path to a transfer, built from her records,
 * with the tools Louise does not offer yet badged as coming soon.
 *
 * It replaced a mock that showed every intended parent the same two egg donors,
 * two agency calls and an escrow account that had not been opened. A step reads
 * as done only because a record says so, and a feature with no data behind it
 * carries a badge rather than an example. Without facts (still loading) the
 * hero has no figures rather than zeros.
 */
function buildIntendedParentConcept(
  t: TranslationFunction,
  facts: IntendedParentFacts | null,
  recordsLoad: { failed: boolean; onRetry: () => void; isRetrying: boolean },
): Concept {
  const SH = SURROGACY_HOME;
  const embryos = facts?.embryos ?? 0;
  const legalDocuments = facts?.legalDocuments ?? 0;
  const status = facts?.participantStatus ?? null;
  const done = [
    embryos > 0,
    facts?.carrierConnected ?? false,
    status === "active-care" || status === "complete",
    status === "complete",
  ];
  // The step under way is the first one not done, unless the relationship
  // record says legal review has started. Later steps wait: nothing in her
  // records orders them beyond that.
  const current =
    status === "legal-review" && !done[2] ? 2 : done.indexOf(false);
  const stepStatus = (index: number): TimelineItem["status"] =>
    done[index] ? "done" : index === current ? "now" : "waiting";
  const countParams = (count: number) => ({ params: { count: String(count) } });

  return {
    eyebrow: "",
    title: t(`${SH}.your_surrogacy_plan`),
    summary: t(`${SH}.embryos_carrier_legal_and_transfer_together`),
    icon: "partner",
    heroMetrics: facts
      ? [
          {
            label: t(`${SH}.embryos`),
            value: embryos > 0 ? String(embryos) : t(`${SH}.none_yet`),
            note: embryos > 0 ? t(`${SH}.in_storage`) : undefined,
          },
          {
            label: t(`${SH}.carrier`),
            value: facts.carrierConnected
              ? t(`${SH}.linked`)
              : t(`${SH}.not_linked`),
          },
          {
            label: t(`${SH}.legal_documents`),
            value:
              legalDocuments > 0 ? String(legalDocuments) : t(`${SH}.none_yet`),
            note: legalDocuments > 0 ? t(`${SH}.uploaded`) : undefined,
          },
        ]
      : undefined,
    sections: [
      buildPostCycleTodaySection(t),
      section(t(`${SH}.where_things_stand`), t(`${SH}.based_on_your_records`), [
        // Until her records are in, the path would put every plan at
        // "create embryos". Say they're loading, or couldn't load.
        !facts
          ? {
              type: "records-status",
              state: recordsLoad.failed ? "unavailable" : "loading",
              loadingCaption: t("clinical_data.loading"),
              unavailableTitle: t("clinical_data.unavailable_title"),
              unavailableMessage: t("clinical_data.unavailable_body"),
              retryLabel: t("clinical_data.retry"),
              retryingLabel: t("clinical_data.retrying"),
              onRetry: recordsLoad.onRetry,
              isRetrying: recordsLoad.isRetrying,
            }
          : timelineWidget(t(`${SH}.path_to_transfer`), "genetics", [
              timeline(
                t(`${SH}.embryos`),
                embryos > 0
                  ? t(`${SH}.embryos_in_storage_count`, countParams(embryos))
                  : t(`${SH}.create_embryos_or_add_yours`),
                stepStatus(0),
              ),
              timeline(
                t(`${SH}.carrier`),
                facts?.carrierConnected
                  ? t(`${SH}.your_carrier_is_linked`)
                  : t(`${SH}.matching_usually_happens_through_an_agency`),
                stepStatus(1),
              ),
              timeline(
                t(`${SH}.legal_agreements`),
                status === "legal-review"
                  ? t(`${SH}.legal_review_is_under_way`)
                  : legalDocuments > 0
                    ? t(
                        `${SH}.legal_documents_uploaded_count`,
                        countParams(legalDocuments),
                      )
                    : t(`${SH}.agreement_parentage_and_escrow`),
                stepStatus(2),
              ),
              timeline(
                t(`${SH}.transfer`),
                t(`${SH}.clinic_schedules_transfer_once_carrier_cleared`),
                stepStatus(3),
              ),
            ]),
      ]),
      section(
        t(`${SH}.donors_and_carriers`),
        t(`${SH}.finding_the_people_who_help`),
        [
          actionRows([
            {
              title: t(`${SH}.browse_screened_donors`),
              detail: t(`${SH}.donor_registry_in_private_preview`),
              icon: "donor",
              action: {
                label: t(`${SH}.browse_screened_donors`),
                screen: "DonorRegistryScreen",
                params: { returnTab: "Home" },
              },
            },
            {
              title: t(`${SH}.compare_surrogacy_agencies`),
              detail: t(`${SH}.agency_screening_fees_and_locations`),
              icon: "concierge",
              comingSoon: true,
            },
            {
              title: t(`${SH}.keep_donor_and_carrier_shortlists`),
              detail: t(`${SH}.save_profiles_and_compare`),
              icon: "process",
              comingSoon: true,
            },
          ]),
        ],
      ),
      section(
        t(`${SH}.legal_and_costs`),
        t(`${SH}.agreements_payments_and_financial_help`),
        [
          actionRows([
            {
              title: t(`${SH}.add_your_legal_documents`),
              detail:
                legalDocuments > 0
                  ? t(
                      `${SH}.legal_documents_uploaded_count`,
                      countParams(legalDocuments),
                    )
                  : t(`${SH}.agreement_parentage_order_and_consents`),
              icon: "legal-records",
              action: {
                label: t(`${SH}.add_your_legal_documents`),
                screen: "DocumentsScreen",
                params: {
                  openUpload: "true",
                  initialCategory: "legal_consent",
                },
              },
            },
            {
              title: t(`${SH}.track_escrow_and_carrier_payments`),
              detail: t(`${SH}.see_what_is_paid_and_what_is_due`),
              icon: "costs",
              comingSoon: true,
            },
            {
              title: t(`${SH}.grants_and_financial_help`),
              detail: t(`${SH}.some_programs_help_with_surrogacy_costs`),
              icon: "coverage-records",
              action: {
                label: t(`${SH}.grants_and_financial_help`),
                screen: "GrantsScreen",
                params: { returnTab: "Home" },
              },
            },
          ]),
        ],
      ),
      section(t(`${RC}.section_good_to_know_title`), "", [
        actionRows([
          {
            title: t(`${SH}.donor_and_carrier_treatment_across_borders`),
            detail: t(`${SH}.consent_screening_parentage_and_follow_up`),
            icon: "learn",
            action: {
              label: t(`${SH}.donor_and_carrier_treatment_across_borders`),
              screen: "ArticleDetail",
              params: {
                articleId:
                  "moon-article-donor-and-carrier-treatment-across-borders",
              },
            },
          },
          {
            title: t(`${SH}.using_donor_eggs_or_sperm`),
            detail: t(`${SH}.when_family_building_takes_a_different_path`),
            icon: "learn",
            action: {
              label: t(`${SH}.using_donor_eggs_or_sperm`),
              screen: "ArticleDetail",
              params: { articleId: "new-article-donor-guide" },
            },
          },
        ]),
      ]),
    ],
  };
}

// ── Post-cycle observed results (#1817) ─────────────────────────────
// The post-cycle funnels previously rendered hardcoded counts in the
// production path. They now derive from the newest closed cycle's
// recorded phase fertility metrics — the same store Past Cycle Detail
// reads via extractResultsFunnel — and each stage is included only when
// its metric was actually recorded (absence is "not recorded", never 0).
// When nothing was recorded the funnel widget is omitted entirely. A missing
// record is not a zero and should not be replaced with a development fixture.

type PostCycleFunnels = {
  egg: FunnelStage[] | null;
  embryo: FunnelStage[] | null;
  ivf: FunnelStage[] | null;
};

type FunnelMetricSpec = readonly {
  metric: string;
  labelKey: string;
  detailKey: string;
}[];

const EGG_POSTCYCLE_FUNNEL_METRICS: FunnelMetricSpec = [
  {
    metric: "retrieved_oocytes",
    labelKey: "egg_postcycle_funnel_retrieved_label",
    detailKey: "egg_postcycle_funnel_retrieved_detail",
  },
  {
    metric: "m2_count",
    labelKey: "egg_postcycle_funnel_mature_label",
    detailKey: "egg_postcycle_funnel_mature_detail",
  },
  {
    metric: "frozen_embryo",
    labelKey: "egg_postcycle_funnel_frozen_label",
    detailKey: "egg_postcycle_funnel_frozen_detail",
  },
];

const EMBRYO_POSTCYCLE_FUNNEL_METRICS: FunnelMetricSpec = [
  {
    metric: "retrieved_oocytes",
    labelKey: "embryo_funnel_retrieved_label",
    detailKey: "embryo_funnel_retrieved_detail",
  },
  {
    metric: "m2_count",
    labelKey: "embryo_funnel_mature_label",
    detailKey: "embryo_funnel_mature_detail",
  },
  {
    metric: "fertilized_oocytes",
    labelKey: "embryo_funnel_fertilized_label",
    detailKey: "embryo_funnel_fertilized_detail",
  },
  {
    metric: "embryos_day_5",
    labelKey: "embryo_funnel_blastocysts_label",
    detailKey: "embryo_funnel_blastocysts_detail",
  },
];

const IVF_POSTCYCLE_FUNNEL_METRICS: FunnelMetricSpec = [
  {
    metric: "retrieved_oocytes",
    labelKey: "ivf_negative_funnel_retrieved_label",
    detailKey: "ivf_negative_funnel_retrieved_detail",
  },
  {
    metric: "m2_count",
    labelKey: "ivf_negative_funnel_mature_label",
    detailKey: "ivf_negative_funnel_mature_detail",
  },
  {
    metric: "fertilized_oocytes",
    labelKey: "ivf_negative_funnel_fertilized_label",
    detailKey: "ivf_negative_funnel_fertilized_detail",
  },
  {
    metric: "embryos_day_5",
    labelKey: "ivf_negative_funnel_blasts_label",
    detailKey: "ivf_negative_funnel_blasts_detail",
  },
  {
    metric: "transferred_embryo",
    labelKey: "ivf_negative_funnel_transferred_label",
    detailKey: "ivf_negative_funnel_transferred_detail",
  },
];

function buildFunnelFromObservedResults(
  results: ReturnType<typeof extractObservedResults>,
  spec: FunnelMetricSpec,
  t: TranslationFunction,
): FunnelStage[] | null {
  const resultsByMetric = new globalThis.Map<string, number>(
    results.map((result) => [result.metric, result.value] as const),
  );
  const stages: FunnelStage[] = [];
  for (const row of spec) {
    const value = resultsByMetric.get(row.metric);
    if (value !== undefined) {
      stages.push({
        label: t(`${RC}.${row.labelKey}`),
        count: value,
        detail: t(`${RC}.${row.detailKey}`),
      });
    }
  }
  return stages.length > 0 ? stages : null;
}

function newestClosedCycleId(
  cycles: Cycle[] | undefined,
  cycleType: Cycle["cycleType"],
): string | undefined {
  const closed = (cycles ?? []).filter(
    (c) => c.status === "closed" && c.cycleType === cycleType,
  );
  if (closed.length === 0) return undefined;
  // Same recency rule as the Future Use projection source cycle.
  const sorted = [...closed].sort((a, b) => {
    const at = (cycle: Cycle) =>
      new Date(cycle.endDate ?? cycle.updatedAt ?? cycle.createdAt).getTime();
    return at(b) - at(a);
  });
  return sorted[0]?.id;
}

/**
 * The sperm-freezing cycle whose analysis Home reads: the open one while it
 * runs, since that is when the analysis arrives and the wait Home shows it,
 * and otherwise the most recently closed one, which the post-cycle Home is
 * about. The open cycle is the one `/active-cycle` returns, as for the hero.
 */
function useSpermAnalysisCycleId(): string | undefined {
  const { data: cycles } = useCycles();
  const { data: activeCycle } = useActiveCycle();
  if (
    activeCycle?.cycleType === "sperm_freezing" &&
    activeCycle.status === "open"
  ) {
    return activeCycle.id;
  }
  return newestClosedCycleId(cycles, "sperm_freezing");
}

function usePostCycleFunnels(): PostCycleFunnels {
  const t = useTranslations();
  const { data: cycles } = useCycles();
  const eggCycleId = newestClosedCycleId(cycles, "egg_freezing");
  const embryoCycleId = newestClosedCycleId(cycles, "embryo_freezing");
  const ivfCycleId = newestClosedCycleId(cycles, "ivf");
  const eggPhases = useCyclePhases(eggCycleId);
  const embryoPhases = useCyclePhases(embryoCycleId);
  const ivfPhases = useCyclePhases(ivfCycleId);

  return useMemo(() => {
    const liveEgg = buildFunnelFromObservedResults(
      extractObservedResults(eggPhases.data ?? []),
      EGG_POSTCYCLE_FUNNEL_METRICS,
      t,
    );
    const liveEmbryo = buildFunnelFromObservedResults(
      extractObservedResults(embryoPhases.data ?? []),
      EMBRYO_POSTCYCLE_FUNNEL_METRICS,
      t,
    );
    const liveIvf = buildFunnelFromObservedResults(
      extractObservedResults(ivfPhases.data ?? []),
      IVF_POSTCYCLE_FUNNEL_METRICS,
      t,
    );
    return {
      egg: liveEgg,
      embryo: liveEmbryo,
      ivf: liveIvf,
    };
  }, [eggPhases.data, embryoPhases.data, ivfPhases.data, t]);
}

function useConcepts(
  semenResults: SpermAnalysisResults | null,
  cycleStatusReviewKind?: CycleStatusReviewKind | null,
  betaDateLabel?: string | null,
  viewerIsSupporting = false,
): Partial<Record<HomepageReviewConceptVariant, Concept>> {
  const t = useTranslations();
  const postCycleFunnels = usePostCycleFunnels();
  return buildConcepts(
    t,
    postCycleFunnels,
    semenResults,
    cycleStatusReviewKind,
    betaDateLabel,
    viewerIsSupporting,
  );
}

/**
 * "What now?" — the three things a finished preservation cycle leaves open.
 *
 * Replaces the closing "decide your next step" card, which opened a chat. The
 * question it asked was right; the answer was not a conversation. These are the
 * three real continuations, and two of them are places in the app.
 *
 * "Use them" goes to the Cryo Vault rather than straight into the start-cycle
 * tunnel. Using stored material means choosing *which*, and the vault is where
 * that choice already lives — `openStartTreatment({ frozenPrefill })` is one tap
 * further on, with the lot she picked. Starting the tunnel from here with
 * everything preselected would be deciding for her.
 *
 * Waiting carries no action on purpose. It is the answer for most days, and the
 * thing she actually needs is the fact underneath it: stored material does not
 * expire on her.
 */
function buildPostCycleWhatNowSection(
  t: TranslationFunction,
  journey: "egg" | "embryo" | "sperm",
  viewerIsSupporting = false,
): ConceptSection {
  const preset: StartTreatmentPresetCycleType =
    journey === "egg"
      ? "egg_freezing"
      : journey === "embryo"
        ? "embryo_freezing"
        : "sperm_freezing";
  const startAnotherLabel = t(`${RC}.postcycle_freeze_another_round`);
  return section(
    t(`${RC}.postcycle_what_comes_next`),
    t(`${RC}.postcycle_nothing_presses`),
    [
      actionRows([
        {
          title: t(`${RC}.postcycle_use_what_you_stored_${journey}`),
          detail: t(`${RC}.postcycle_use_what_you_stored_${journey}_detail`),
          icon: "cryo",
          action: {
            label: t(`${RC}.postcycle_use_what_you_stored_${journey}`),
            // `CryoVaultScreen`, not `CryoVault`: `handleAction` looks the name
            // up in `SCREEN_MAP` and returns silently on a miss, so the wrong
            // key is not an error anywhere — just a row that does nothing.
            screen: "CryoVaultScreen",
          },
        },
        // Another round is the patient's to start, so a partner reads the
        // other two continuations without it.
        ...(viewerIsSupporting
          ? []
          : [
              {
                title: startAnotherLabel,
                detail: t(
                  `${RC}.postcycle_freeze_another_round_detail_${journey}`,
                ),
                icon: "start" as const,
                action: {
                  label: startAnotherLabel,
                  signal: "open-start-treatment-modal" as const,
                  params: { presetCycleType: preset },
                },
              },
            ]),
        {
          title: t(`${RC}.postcycle_wait`),
          detail: t(`${RC}.postcycle_wait_detail_${journey}`),
          icon: "process",
        },
      ]),
    ],
  );
}

/**
 * The exploration door, at the foot of every post-cycle home.
 *
 * Shared by the three journeys because the question it answers is the same one:
 * she is past a cycle and may still want to compare what comes next. The
 * `learn-explorer` hero asked it as the page's whole subject; here it is a line
 * she can open.
 */
function buildPostCycleExploreSection(
  t: TranslationFunction,
  viewerIsSupporting = false,
): ConceptSection {
  const actions = buildActions(t);
  return section(
    t(`${RC}.postcycle_explore_title`),
    t(`${RC}.postcycle_explore_subtitle`),
    [
      exploreOptions(
        t(`${RC}.postcycle_explore_options`),
        t(`${RC}.postcycle_explore_options_detail`),
        [
          {
            title: t(`${RC}.postcycle_compare_the_paths`),
            detail: t(`${RC}.postcycle_compare_the_paths_detail`),
            icon: "learn",
            action: {
              label: t(`${RC}.postcycle_compare_the_paths`),
              screen: "LearnScreen",
            },
          },
          // The plan is the patient's to revisit; a partner can still
          // compare the paths and look at clinics.
          ...(viewerIsSupporting
            ? []
            : [
                {
                  title: t(`${RC}.postcycle_revisit_your_plan`),
                  detail: t(`${RC}.postcycle_revisit_your_plan_detail`),
                  icon: "process" as const,
                  action: {
                    label: t(`${RC}.postcycle_revisit_your_plan`),
                    signal: "open-intent-picker" as const,
                  },
                },
              ]),
          {
            title: t(`${RC}.postcycle_look_at_clinics`),
            detail: t(`${RC}.postcycle_look_at_clinics_detail`),
            icon: "clinics",
            action: actions.findCare,
          },
        ],
      ),
    ],
  );
}

/**
 * The collection-day Home's "Before your collection" rows: only the things
 * still to do, each opening where it gets done, then how the step he is on
 * works.
 *
 * The date and screening rows open hub steps, which an id-less hub action can
 * only reach on an open cycle; without one they would start a new cycle, so
 * they are left out.
 */
function buildSpermCollectionWidgets(
  t: TranslationFunction,
  hasOpenCycle: boolean,
  spermCollection: {
    facts: SpermCollectionFacts;
    formatDate: (iso: string) => string;
    hasConsentDocument: boolean;
  } | null,
): Widget[] {
  const ns = "homepage.sperm_collection_day";
  const widgets: Widget[] = [];
  const facts = spermCollection?.facts;
  if (facts && hasOpenCycle) {
    const openStep = (label: string, focus: "screening" | "sperm_collection") =>
      ({
        label,
        screen: "CycleDetail",
        params: { focus },
      }) satisfies PlanAction;
    if (facts.collection.status === "not-booked") {
      widgets.push(
        dataStatus(
          t(`${ns}.add_your_collection_date`),
          t(`${ns}.home_counts_down_and_reminds_you`),
          "sperm",
          openStep(t(`${ns}.add_your_collection_date`), "sperm_collection"),
        ),
      );
    } else if (facts.collection.status === "overdue") {
      widgets.push(
        dataStatus(
          t(`${ns}.log_your_collection`),
          t(`${ns}.mark_it_done_or_change_the_date`),
          "sperm",
          openStep(t(`${ns}.log_your_collection`), "sperm_collection"),
        ),
      );
    }
    if (facts.screening.status !== "done") {
      widgets.push(
        dataStatus(
          t(`${ns}.screening_tests`),
          facts.screening.status === "booked"
            ? t(`${ns}.screening_booked_for_date`, {
                params: {
                  when: spermCollection.formatDate(facts.screening.on),
                },
              })
            : t(`${ns}.clinics_test_before_freezing`),
          "panel",
          openStep(t(`${ns}.screening_tests`), "screening"),
        ),
      );
    }
  }
  if (!spermCollection?.hasConsentDocument) {
    widgets.push(
      dataStatus(
        t(`${ns}.storage_consent`),
        t(`${ns}.consent_needed_before_storage`),
        "legal-records",
        {
          label: t(`${ns}.storage_consent`),
          signal: "open-document-upload",
          params: { initialCategory: "legal_consent" },
        },
      ),
    );
  }
  widgets.push(stepExplainer());
  return widgets;
}

/**
 * The frozen-transfer Home's "Before your transfer" rows: the transfer date
 * while none is recorded, then how the step she is on works. A booked date
 * needs no row (the hero states it), and a passed one belongs to the
 * post-procedure check-in. The date row opens a hub step, which an id-less hub
 * action can only reach on an open cycle, so it is left out without one.
 */
function buildFetTransferWidgets(
  t: TranslationFunction,
  hasOpenCycle: boolean,
  facts: FetTransferFacts | null,
): Widget[] {
  const ns = "homepage.fet_lining_prep";
  const widgets: Widget[] = [];
  if (facts && hasOpenCycle && facts.transfer.status === "not-booked") {
    widgets.push(
      dataStatus(
        t(`${ns}.add_your_transfer_date`),
        t(`${ns}.home_counts_down_and_explains_each_step`),
        "start",
        {
          label: t(`${ns}.add_your_transfer_date`),
          screen: "CycleDetail",
          params: { focus: "transfer" },
        },
      ),
    );
  }
  widgets.push(stepExplainer());
  return widgets;
}

function buildDetailedConceptSections(
  t: TranslationFunction,
  /** Whether the patient has an open cycle this Home can send them into. */
  hasOpenCycle: boolean,
  /** What the collection-day Home knows; null on every other variant. */
  spermCollection: {
    facts: SpermCollectionFacts;
    formatDate: (iso: string) => string;
    hasConsentDocument: boolean;
  } | null,
  /** What the frozen-transfer Home knows; null on every other variant. */
  fetTransfer: FetTransferFacts | null,
  /** The semen analysis recorded on the sperm-freezing cycle's own steps. */
  semenResults: SpermAnalysisResults | null,
): Partial<Record<HomepageReviewConceptVariant, ConceptSection[]>> {
  return {
    "fet-active": [
      section(
        t("homepage.fet_lining_prep.before_your_transfer"),
        t("homepage.fet_lining_prep.what_to_sort_out_before_the_day"),
        buildFetTransferWidgets(t, hasOpenCycle, fetTransfer),
      ),
    ],
    "egg-freezing-active": [],
    // The post-cycle pages build their whole order in `buildConcepts`, so that
    // the sequence reads in one place instead of being spliced from two lists.
    "egg-freezing-postcycle": [],
    "embryo-freezing-active": [],
    "embryo-freezing-postcycle": [],
    "sperm-freezing-active": [
      section(
        t("homepage.sperm_collection_day.before_your_collection"),
        t("homepage.sperm_collection_day.what_to_sort_out_before_the_day"),
        buildSpermCollectionWidgets(t, hasOpenCycle, spermCollection),
      ),
    ],
    "sperm-freezing-wait": [
      // Once figures are recorded, the cycle can still be open (the freezing
      // and its confirmation come after), so the card shows them here rather
      // than waiting for the post-cycle Home.
      section(
        semenResults
          ? t("homepage.sperm_results_wait.your_results_are_in")
          : t("homepage.sperm_results_wait.when_your_results_come_in"),
        t("homepage.sperm_results_wait.where_to_add_them_and_what_they_change"),
        [
          semenAnalysis(
            t("homepage.shared.semen_analysis"),
            undefined,
            semenResults,
            {
              title: t(
                "homepage.sperm_results_wait.analysis_will_fill_in_here",
              ),
              // With an open cycle, the analysis step is where the figures go
              // and entering them ends the wait. Without one (a closed cycle
              // whose result is missing), an id-less hub action would start a
              // new cycle, so the report upload stands in.
              action: hasOpenCycle
                ? {
                    label: t("homepage.sperm_results_wait.add_your_results"),
                    screen: "CycleDetail",
                    params: { focus: "analysis" },
                  }
                : {
                    label: t(
                      "homepage.sperm_results_wait.upload_your_lab_report",
                    ),
                    signal: "open-document-upload",
                    params: { initialCategory: "medical_records" },
                  },
            },
          ),
          spermResultPaths(),
        ],
      ),
    ],
    "sperm-freezing-postcycle": [],
    "ivf-active": [],
    "beta-wait": [],
    "ivf-positive": [],
    "ivf-negative": [],
    "iui-active": [],
    "iui-wait": [],
    "oi-wait": [],
    "oi-active": [],
  };
}

function buildPostRetrievalHeroCopy(
  t: TranslationFunction,
  cycleType: CycleType | undefined,
): { eyebrow: string; title: string; summary: string } {
  const procedureKey =
    cycleType === "egg_freezing"
      ? "egg_freezing"
      : cycleType === "embryo_freezing"
        ? "embryo_freezing"
        : "ivf";
  const RC2 = "homepage.review_concept";
  return {
    eyebrow: t(`${RC2}.hero_post_retrieval_${procedureKey}_eyebrow`),
    title: t(`${RC2}.hero_post_retrieval_title`),
    summary: t(`${RC2}.hero_post_retrieval_${procedureKey}_summary`),
  };
}

function buildHeroStatusCopy(
  t: TranslationFunction,
  viewerIsSupporting = false,
): Partial<
  Record<HomepageReviewConceptVariant, { title: string; summary: string }>
> {
  const RC = "homepage.review_concept";
  // What the Home says when it can't read a step from her cycle (none open,
  // or it didn't load). It names no step and claims no recorded response.
  return {
    "egg-freezing-active": {
      title: t(`${RC}.your_treatment`),
      summary: t(
        `${RC}.medications_scans_and_steps_appear_as_your_clinic_adds_them`,
      ),
    },
    "egg-freezing-postcycle": {
      title: t(`${RC}.hero_egg_freezing_postcycle_title`),
      summary: t(`${RC}.hero_egg_freezing_postcycle_summary`),
    },
    "embryo-freezing-active": {
      title: t(`${RC}.your_treatment`),
      summary: t(
        `${RC}.medications_scans_and_steps_appear_as_your_clinic_adds_them`,
      ),
    },
    "embryo-freezing-postcycle": {
      title: t(`${RC}.hero_embryo_freezing_postcycle_title`),
      summary: t(`${RC}.hero_embryo_freezing_postcycle_summary`),
    },
    "sperm-freezing-active": {
      title: t(`${RC}.getting_ready_for_your_collection`),
      summary: t(`${RC}.your_clinic_tells_you_how_to_prepare`),
    },
    "sperm-freezing-wait": {
      // The sample is the patient's; a partner reads that it is at the lab.
      title: viewerIsSupporting
        ? t(`${RC}.supporting.the_sample_is_with_the_lab`)
        : t(`${RC}.hero_sperm_freezing_wait_title`),
      summary: t(`${RC}.hero_sperm_freezing_wait_summary`),
    },
    "sperm-freezing-postcycle": {
      title: t(`${RC}.hero_sperm_freezing_postcycle_title`),
      summary: t(`${RC}.hero_sperm_freezing_postcycle_summary`),
    },
    "ivf-active": {
      title: t(`${RC}.your_treatment`),
      summary: t(
        `${RC}.medications_scans_and_steps_appear_as_your_clinic_adds_them`,
      ),
    },
    "fet-active": {
      title: t(`${RC}.fet_active_plan_heading`),
      summary: t(`${RC}.fet_active_plan_summary`),
    },
    "cycle-status-review": {
      title: t(`${RC}.cycle_status_review_title`),
      summary: t(`${RC}.cycle_status_review_summary`),
    },
    "beta-wait": {
      title: t(`${RC}.hero_beta_wait_title_no_date`),
      summary: t(`${RC}.hero_beta_wait_summary`),
    },
    "fet-positive": {
      title: t(`${RC}.post_procedure_outcome.fet.positive.title`),
      summary: t(`${RC}.post_procedure_outcome.fet.positive.summary`),
    },
    "fet-negative": {
      title: t(
        `${RC}.post_procedure_outcome.shared.take_a_breath_after_this_cycle`,
      ),
      summary: t(`${RC}.post_procedure_outcome.fet.negative.summary`),
    },
    "fet-uncertain": {
      title: t(`${RC}.post_procedure_outcome.fet.uncertain.title`),
      summary: t(`${RC}.post_procedure_outcome.fet.uncertain.summary`),
    },
    "fet-adverse": {
      title: t(`${RC}.post_procedure_outcome.fet.adverse.title`),
      summary: t(`${RC}.post_procedure_outcome.fet.adverse.summary`),
    },
    "fet-unavailable": {
      title: t(`${RC}.post_procedure_outcome.fet.unavailable.title`),
      summary: t(`${RC}.post_procedure_outcome.fet.unavailable.summary`),
    },
    "iui-positive": {
      title: t(`${RC}.post_procedure_outcome.iui.positive.title`),
      summary: t(`${RC}.post_procedure_outcome.iui.positive.summary`),
    },
    "iui-negative": {
      title: t(
        `${RC}.post_procedure_outcome.shared.take_a_breath_after_this_cycle`,
      ),
      summary: t(`${RC}.post_procedure_outcome.iui.negative.summary`),
    },
    "iui-uncertain": {
      title: t(`${RC}.post_procedure_outcome.iui.uncertain.title`),
      summary: t(`${RC}.post_procedure_outcome.iui.uncertain.summary`),
    },
    "iui-adverse": {
      title: t(`${RC}.post_procedure_outcome.iui.adverse.title`),
      summary: t(`${RC}.post_procedure_outcome.iui.adverse.summary`),
    },
    "iui-unavailable": {
      title: t(`${RC}.post_procedure_outcome.iui.unavailable.title`),
      summary: t(`${RC}.post_procedure_outcome.iui.unavailable.summary`),
    },
    "oi-positive": {
      title: t(`${RC}.post_procedure_outcome.oi.positive.title`),
      summary: t(`${RC}.post_procedure_outcome.oi.positive.summary`),
    },
    "oi-negative": {
      title: t(
        `${RC}.post_procedure_outcome.shared.take_a_breath_after_this_cycle`,
      ),
      summary: t(`${RC}.post_procedure_outcome.oi.negative.summary`),
    },
    "oi-uncertain": {
      title: t(`${RC}.post_procedure_outcome.oi.uncertain.title`),
      summary: t(`${RC}.post_procedure_outcome.oi.uncertain.summary`),
    },
    "oi-adverse": {
      title: t(`${RC}.post_procedure_outcome.oi.adverse.title`),
      summary: t(`${RC}.post_procedure_outcome.oi.adverse.summary`),
    },
    "oi-unavailable": {
      title: t(`${RC}.post_procedure_outcome.oi.unavailable.title`),
      summary: t(`${RC}.post_procedure_outcome.oi.unavailable.summary`),
    },
    "ivf-unavailable": {
      title: t(`${RC}.post_procedure_outcome.ivf.unavailable.title`),
      summary: t(`${RC}.post_procedure_outcome.ivf.unavailable.summary`),
    },
    "ivf-uncertain": {
      title: t(`${RC}.post_procedure_outcome.ivf.uncertain.title`),
      summary: t(`${RC}.post_procedure_outcome.ivf.uncertain.summary`),
    },
    "ivf-adverse": {
      title: t(`${RC}.post_procedure_outcome.ivf.adverse.title`),
      summary: t(`${RC}.post_procedure_outcome.ivf.adverse.summary`),
    },
    // The last two of the twenty outcome routes still reading legacy hero copy.
    // It was written for a fixture and states things no record backs: "Positive
    // beta, repeat in 48 hours" prescribes the next test, and "Result reviewed,
    // consult is May 23" names an appointment date out of nothing. Both sat
    // directly above a body that had already been moved onto the shared copy, so
    // the hero and the section under it described different situations.
    "ivf-positive": {
      title: t(`${RC}.post_procedure_outcome.ivf.positive.title`),
      summary: t(`${RC}.post_procedure_outcome.ivf.positive.summary`),
    },
    "ivf-negative": {
      // One title for every pathway. Naming the procedure told a patient whose
      // cycle never had one — a preservation cycle falls through
      // `resolveModality` to the IVF default — to take a breath "after your IVF
      // transfer". The summary was already shared word for word; only the
      // heading claimed a procedure, and an ending with no pregnancy does not
      // depend on which procedure preceded it.
      title: t(
        `${RC}.post_procedure_outcome.shared.take_a_breath_after_this_cycle`,
      ),
      summary: t(`${RC}.post_procedure_outcome.ivf.negative.summary`),
    },
    "iui-active": {
      title: t(`${RC}.your_iui_is_coming_up`),
      summary: t(`${RC}.your_clinic_sets_trigger_and_iui_times`),
    },
    "iui-wait": {
      title: t(`${RC}.waiting_for_the_clinic_blood_test`),
      summary: t(`${RC}.hero_iui_wait_summary`),
    },
    "oi-wait": {
      title: t(`${RC}.hero_oi_wait_title`),
      summary: t(`${RC}.hero_oi_wait_summary`),
    },
    "oi-active": {
      title: t(`${RC}.your_treatment`),
      summary: t(
        `${RC}.medications_scans_and_steps_appear_as_your_clinic_adds_them`,
      ),
    },
    "intended-parent": {
      title: t(`${SURROGACY_HOME}.your_surrogacy_plan`),
      summary: t(
        `${SURROGACY_HOME}.embryos_carrier_legal_and_transfer_together`,
      ),
    },
  };
}

function buildPostCycleHeroCopy(
  t: TranslationFunction,
  variant: HomepageReviewConceptVariant,
  counts: { eggs: number; embryos: number; sperm: number },
  /**
   * A partner reading about eggs or sperm that are the patient's. The stored
   * count stays as it is; what changes is whose cycle it was and whose decision
   * comes next.
   */
  readsAboutPartnersOwnMaterial = false,
) {
  const RC = "homepage.review_concept";
  const config =
    variant === "egg-freezing-postcycle"
      ? {
          count: counts.eggs,
          storedTitle: "hero_egg_freezing_postcycle_stored_title",
          storedSummary: "hero_egg_freezing_postcycle_stored_summary",
          title: "hero_egg_freezing_postcycle_title",
          summary: "hero_egg_freezing_postcycle_summary",
        }
      : variant === "embryo-freezing-postcycle"
        ? {
            count: counts.embryos,
            storedTitle: "hero_embryo_freezing_postcycle_stored_title",
            storedSummary: "hero_embryo_freezing_postcycle_stored_summary",
            title: "hero_embryo_freezing_postcycle_title",
            summary: "hero_embryo_freezing_postcycle_summary",
          }
        : {
            count: counts.sperm,
            storedTitle: "hero_sperm_freezing_postcycle_stored_title",
            storedSummary: "hero_sperm_freezing_postcycle_stored_summary",
            title: "hero_sperm_freezing_postcycle_title",
            summary: "hero_sperm_freezing_postcycle_summary",
          };

  if (readsAboutPartnersOwnMaterial) {
    return {
      title:
        config.count > 0
          ? t(`${RC}.${config.storedTitle}`, {
              params: { count: String(config.count) },
            })
          : variant === "egg-freezing-postcycle"
            ? t(`${RC}.supporting.the_egg_freezing_cycle_is_complete`)
            : t(`${RC}.supporting.the_sperm_freezing_cycle_is_complete`),
      summary: t(
        `${RC}.supporting.the_storage_record_is_there_to_review_together`,
      ),
    };
  }

  return {
    title:
      config.count > 0
        ? t(`${RC}.${config.storedTitle}`, {
            params: { count: String(config.count) },
          })
        : t(`${RC}.${config.title}`),
    summary:
      config.count > 0
        ? t(`${RC}.${config.storedSummary}`)
        : t(`${RC}.${config.summary}`),
  };
}

function WidgetFrame({
  title,
  subtitle,
  icon,
  children,
  action,
  onAction,
}: {
  title: string;
  subtitle?: string;
  icon: OrganicMiniIconKind;
  children: React.ReactNode;
  action?: PlanAction;
  onAction: (action: PlanAction) => void;
}) {
  return (
    <CardSurface style={{ padding: 15, gap: 12 }}>
      <CardHeader
        leading={<OrganicMiniIcon kind={icon} size={36} />}
        title={title}
        subtitle={subtitle}
      />

      {children}

      {action ? (
        <SectionActionLink
          label={action.label}
          onPress={() => onAction(action)}
          style={{ alignSelf: "flex-start", paddingLeft: 0 }}
        />
      ) : null}
    </CardSurface>
  );
}

function MetricGrid({ metrics: values }: { metrics: Metric[] }) {
  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        columnGap: 16,
        rowGap: 12,
      }}
    >
      {values.map((metric) => (
        <View
          key={`${metric.label}-${metric.value}`}
          style={{ flexGrow: 1, flexBasis: 116 }}
        >
          <MetricLabelText>{metric.label}</MetricLabelText>
          <MetricValueText style={{ marginTop: 3 }}>
            {metric.value}
          </MetricValueText>
          {metric.note ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 16,
                color: EMBER.colors.textMid,
                marginTop: 1,
              }}
            >
              {metric.note}
            </Text>
          ) : null}
        </View>
      ))}
    </View>
  );
}

// `testName` on the preview is translated copy, so matching against the catalog
// by name only works when the two strings happen to collide. The stable key is
// `interpretation.test` (`InterpretedTest`), which maps 1:1 to a factsheet id.
const INTERPRETED_TEST_FACTSHEET_IDS: Record<
  NonNullable<ResultInterpretation["test"]>,
  string
> = {
  amh: "test-amh",
  fsh: "test-fsh",
  lh: "test-lh",
  e2: "test-estradiol",
  progesterone: "test-progesterone-check",
  semen_analysis: "test-semen-analysis",
};

function resolvePreviewTestFactSheet(
  interpretation: ResultInterpretation,
): ContentBlock<"test"> | undefined {
  const factsheetId = interpretation.test
    ? INTERPRETED_TEST_FACTSHEET_IDS[interpretation.test]
    : undefined;
  if (factsheetId) {
    const block = getBlock(makeId(factsheetId));
    if (block?.kind === "test") return block;
  }
  return getTestByName(interpretation.testName);
}

function TestResultsPreview({
  results,
  title,
  subtitle,
  queryState,
  onAction,
  onOpenTestFactSheet,
}: {
  results?: ResultInterpretation[];
  title?: string;
  subtitle?: string;
  queryState?: TestResultInsightsQueryState;
  onAction: (action: PlanAction) => void;
  onOpenTestFactSheet?: (payload: HomepageTestFactSheetPayload) => void;
}) {
  const t = useTranslations();
  const rows = results ?? [];
  const uploadAction: PlanAction = {
    label: t("homepage.test_result_document.upload_action"),
    signal: "open-document-upload",
    params: { initialCategory: "medical_records" },
  };
  const handleUpload = () => onAction(uploadAction);

  if (queryState?.isLoading) {
    return (
      <CardSurface
        testID="home-test-results-loading"
        accessibilityLiveRegion="polite"
        style={{
          minHeight: 86,
          paddingHorizontal: 16,
          paddingVertical: 16,
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        }}
      >
        <LoadingState
          caption={t("clinical_data.loading")}
          style={{ paddingVertical: 0 }}
        />
      </CardSurface>
    );
  }

  if (queryState?.isError) {
    return (
      <ClinicalDataStatus
        state="unavailable"
        title={t("clinical_data.unavailable_title")}
        message={t("clinical_data.unavailable_body")}
        retryLabel={t("clinical_data.retry")}
        retryingLabel={t("clinical_data.retrying")}
        onRetry={queryState.onRetry}
        isRetrying={queryState.isFetching}
        testID="home-test-results-unavailable"
      />
    );
  }

  if (rows.length === 0) {
    return (
      <ActionRowCard
        leading={
          <Image
            source={testTubesIllustration}
            style={{ width: 32, height: 32 }}
            resizeMode="contain"
          />
        }
        title={title ?? t("homepage.review_concept.inline.test_results_title")}
        subtitle={
          subtitle ??
          t("homepage.review_concept.inline.test_results_not_recorded_detail")
        }
        onPress={handleUpload}
        accessibilityLabel={`${
          title ?? t("homepage.review_concept.inline.test_results_title")
        }. ${
          subtitle ??
          t("homepage.review_concept.inline.test_results_not_recorded_detail")
        }`}
      />
    );
  }

  return (
    <View style={{ gap: 9 }}>
      {rows.map((interpretation, index) => {
        const factsheet = resolvePreviewTestFactSheet(interpretation);
        return (
          <TestResultInsightCard
            key={`review-test-result-${resultInterpretationKey(interpretation, index)}`}
            interpretation={interpretation}
            state={{ kind: "new-result" }}
            onPress={
              factsheet && onOpenTestFactSheet
                ? () =>
                    onOpenTestFactSheet({
                      item: factsheet,
                      testName: interpretation.testName,
                    })
                : undefined
            }
          />
        );
      })}
    </View>
  );
}

function buildMedicationLogOptions(t: TranslationFunction) {
  const RC = "homepage.review_concept";
  return [
    {
      id: "on-time" as const,
      label: t(`${RC}.medication_log_on_time_label`),
      detail: t(`${RC}.medication_log_on_time_detail`),
    },
    {
      id: "late" as const,
      label: t(`${RC}.medication_log_late_label`),
      detail: t(`${RC}.medication_log_late_detail`),
    },
    {
      id: "missed" as const,
      label: t(`${RC}.medication_log_missed_label`),
      detail: t(`${RC}.medication_log_missed_detail`),
    },
    {
      id: "skipped" as const,
      label: t(`${RC}.medication_log_skipped_label`),
      detail: t(`${RC}.medication_log_skipped_detail`),
    },
  ];
}

function getMedicationLogIllustration(row: TodayRow): number {
  const format = row.medicationFormat?.toLowerCase() ?? "";
  if (row.medicationForm === "pen" || format.includes("pen")) {
    return medicationPenIllustration;
  }
  if (row.medicationForm === "vial" || format.includes("vial")) {
    return medicationVialIllustration;
  }
  if (format.includes("syringe")) return medicationVialIllustration;
  if (format.includes("inject") || format.includes("subcutaneous")) {
    return medicationPenIllustration;
  }

  const contentMedication = getMedicationByBrandName(
    row.medicationName ?? row.title,
  );
  const commonForms = contentMedication?.body.commonForms ?? [];
  if (commonForms.includes("pen")) return medicationPenIllustration;
  if (
    commonForms.includes("vial") ||
    commonForms.includes("pre_filled_syringe")
  ) {
    return medicationVialIllustration;
  }

  return medicationPillIllustration;
}

export type PeekAheadDay = {
  label: string;
  rows: TodayRow[];
  emptyTitle?: string;
  emptyDetail?: string;
};

export type MedicationLogReadState = {
  status: "loading" | "ready" | "error";
  isRetrying: boolean;
  retry: () => void;
};

function buildMedicationContext(
  row: TodayRow,
): HomepageMedicationFactSheetPayload["userContext"] {
  return {
    originalName: row.title,
    dosage: row.medicationDose,
    // The administration method she recorded, which this payload dropped. The
    // factsheet renders it as a line of its own, picks its form-specific
    // how-to from it, and hands it to the injection coach — all three were
    // blank whenever the sheet was opened from Home or the calendar.
    ...(row.medicationApiForm ? { selectedForm: row.medicationApiForm } : {}),
    ...(row.medicationRoute !== undefined && {
      route: row.medicationRoute,
    }),
    ...(row.notes ? { notes: row.notes } : {}),
    ...(row.medicationEndDate !== undefined && {
      endDate: row.medicationEndDate,
    }),
    timeTaken: row.time === "Any time" ? undefined : [row.time],
  };
}

function buildAppointmentContext(
  row: TodayRow,
): HomepageProcedureFactSheetPayload["appointment"] {
  return {
    id: row.id,
    appointmentId: row.appointmentId,
    title: row.title,
    subtitle: row.detail,
    time: row.time,
    date: "Today",
    notes: row.notes,
    ownership: row.ownership,
    ownerId: row.ownerId,
  };
}

function getTodayActionId(row: TodayRow): string {
  return row.appointmentId ?? row.id;
}

export function TodayList({
  rows,
  onOpenMedicationFactSheet,
  onOpenAppointment,
  onOpenProcedureFactSheet,
  onRequestDocumentAttachment,
  completionSource = "home",
  onCompletionFeedback,
  onMedicationLogReadStateChange,
  showCompletion = true,
  rowAppearance = "default",
  peekAheadDays,
}: {
  rows: TodayRow[];
  onOpenMedicationFactSheet?: (
    payload: HomepageMedicationFactSheetPayload,
  ) => void;
  /**
   * Appointment-management entry point. Care supplies this so a saved
   * appointment opens its details first; callers that omit it retain the
   * existing procedure-education behavior.
   */
  onOpenAppointment?: (row: TodayRow) => void;
  onOpenProcedureFactSheet?: (
    payload: HomepageProcedureFactSheetPayload,
  ) => void;
  /** Opens the canonical document picker with the row's provenance tags. */
  onRequestDocumentAttachment?: (row: TodayRow) => void;
  completionSource?: "home" | "care";
  onCompletionFeedback?: () => void;
  onMedicationLogReadStateChange?: (state: MedicationLogReadState) => void;
  showCompletion?: boolean;
  rowAppearance?: "default" | "calendar-glass";
  peekAheadDays?: PeekAheadDay[];
}) {
  const timezone = useTimezone();
  const updateMedication = useUpdateMedication();
  const today = getTodayString(timezone);
  const medicationLogDateRange = useMemo(() => {
    const dates = rows
      .map((row) => row.date)
      .filter((date): date is string => Boolean(date))
      .sort();
    const fallback = today;
    return {
      startDate: dates[0] ?? fallback,
      endDate: dates[dates.length - 1] ?? fallback,
    };
  }, [rows, today]);
  const todayLogs = useMedicationLogs({
    startDate: medicationLogDateRange.startDate,
    endDate: medicationLogDateRange.endDate,
    enabled: showCompletion,
  });
  const medicationLogsRefetch = useRef(todayLogs.refetch);
  medicationLogsRefetch.current = todayLogs.refetch;
  useEffect(() => {
    if (!onMedicationLogReadStateChange) return;
    onMedicationLogReadStateChange({
      status: todayLogs.error
        ? "error"
        : todayLogs.isLoading
          ? "loading"
          : "ready",
      isRetrying: todayLogs.isFetching,
      retry: () => {
        void medicationLogsRefetch.current();
      },
    });
  }, [
    onMedicationLogReadStateChange,
    todayLogs.error,
    todayLogs.isFetching,
    todayLogs.isLoading,
  ]);
  const { data: patientNotes } = usePatientNotes();

  const [completionDialogRow, setCompletionDialogRow] =
    useState<TodayRow | null>(null);

  const doseStatusFor = (row: TodayRow) =>
    row.kind === "medication"
      ? getTodayDoseStatus(todayLogs.data, row, row.date ?? today, timezone)
      : undefined;

  const isRowComplete = (row: TodayRow) =>
    row.kind === "medication"
      ? Boolean(row.medicationIsStopped) || Boolean(doseStatusFor(row))
      : isCompletableTodayRow(row)
        ? Boolean(getTodayActionCompletion(patientNotes, getTodayActionId(row)))
        : false;

  const allComplete =
    showCompletion && rows.length > 0 && rows.every(isRowComplete);

  const handleRowPress = (row: TodayRow) => {
    haptic("light");
    if (row.kind === "medication") {
      const medicationName = row.medicationName ?? row.title;
      const payload: HomepageMedicationFactSheetPayload = {
        item: getMedicationByBrandName(medicationName) ?? null,
        fallbackName: row.title,
        medicationId: row.medicationId,
        userContext: buildMedicationContext(row),
      };
      if (onOpenMedicationFactSheet) {
        onOpenMedicationFactSheet({
          ...payload,
          onLogDose: () => setCompletionDialogRow(row),
        });
      } else {
        setCompletionDialogRow(row);
      }
      return;
    }

    if (row.kind === "appointment" && onOpenAppointment) {
      onOpenAppointment(row);
      return;
    }

    if (row.kind === "appointment" || row.kind === "monitoring") {
      const procedure = getProcedureByName(
        row.procedureName ??
          (row.kind === "monitoring"
            ? "Transvaginal ultrasound"
            : "Initial Fertility Consultation"),
      );
      if (procedure && onOpenProcedureFactSheet) {
        onOpenProcedureFactSheet({
          item: procedure,
          appointment: buildAppointmentContext(row),
        });
        return;
      }
    }

    if (isCompletableTodayRow(row)) setCompletionDialogRow(row);
  };

  const handleCheckboxPress = (row: TodayRow) => {
    if (row.kind !== "medication" && !isCompletableTodayRow(row)) return;
    haptic("selection");
    setCompletionDialogRow(row);
  };

  const handleCompleteRow = (_row: TodayRow) => {
    setCompletionDialogRow(null);
    haptic("nudge");
    onCompletionFeedback?.();
  };

  return (
    <View style={{ gap: 9, position: "relative", overflow: "visible" }}>
      {allComplete ? <TodayDoneBanner peekAheadDays={peekAheadDays} /> : null}
      {rows.length === 0 ? (
        <TodayEmptyState peekAheadDays={peekAheadDays} />
      ) : null}
      {rows.map((row) => {
        const isStopped = Boolean(row.medicationIsStopped);
        const rowDoseStatus = doseStatusFor(row);
        const isComplete = showCompletion && isRowComplete(row);
        const isPartnerOwned = Boolean(row.partnerName);
        if (row.kind === "medication") {
          return (
            <MedicationRow
              key={row.id}
              name={row.medicationName ?? row.title}
              dose={row.medicationDose}
              format={row.medicationFormat}
              subtitle={row.detail}
              form={row.medicationForm}
              availabilityStatus={row.medicationAvailabilityStatus}
              time={row.time}
              surface="standalone"
              appearance={rowAppearance}
              completed={isComplete}
              doseStatus={rowDoseStatus}
              isStopped={isStopped}
              partnerName={row.partnerName}
              routes={[row.medicationRoute]}
              // A dose she confirms against her box in the coach, written to
              // the record behind this row. Only where the row knows it.
              onCorrectDose={
                row.medicationId
                  ? (dose) =>
                      updateMedication.mutate({
                        medicationId: row.medicationId!,
                        dose: dose.value,
                        doseUnit: dose.unit,
                      })
                  : undefined
              }
              // Her recorded administration method, so the coach confirms a
              // device rather than asking for one she has already given.
              apiForm={row.medicationApiForm}
              patientMedicationId={row.medicationId}
              rightAction={
                isPartnerOwned
                  ? "partnerTag"
                  : showCompletion && !isStopped
                    ? "checkbox"
                    : "none"
              }
              onPress={() => handleRowPress(row)}
              onCheckboxPress={() => handleCheckboxPress(row)}
            />
          );
        }

        return (
          <AppointmentRow
            key={row.id}
            row={row}
            isComplete={isComplete}
            showCheckbox={showCompletion}
            appearance={rowAppearance}
            onPress={() => handleRowPress(row)}
            onCheckboxPress={() => handleCheckboxPress(row)}
          />
        );
      })}
      {showCompletion ? (
        <TodayRowCompletionDialog
          row={completionDialogRow}
          visible={Boolean(completionDialogRow)}
          onClose={() => setCompletionDialogRow(null)}
          onRequestDocumentAttachment={onRequestDocumentAttachment}
          completionSource={completionSource}
          onComplete={handleCompleteRow}
          existingMedicationLog={
            completionDialogRow?.kind === "medication"
              ? getTodayDoseLog(
                  todayLogs.data,
                  completionDialogRow,
                  completionDialogRow.date ?? today,
                  timezone,
                )
              : undefined
          }
        />
      ) : null}
    </View>
  );
}

function TodayDoneBanner({
  peekAheadDays,
}: {
  peekAheadDays?: PeekAheadDay[];
}) {
  const t = useTranslations();
  const [peekVisible, setPeekVisible] = useState(false);

  return (
    <>
      <CardSurface
        style={{
          paddingHorizontal: 15,
          paddingVertical: 12,
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
        }}
      >
        {/* Bare, like the empty-day banner's calendar: no shape behind it. */}
        <Check size={20} color={EMBER.colors.textMid} strokeWidth={1.8} />
        <View style={{ flex: 1 }}>
          <EmptyStateTitleText>
            {t("homepage.review_concept.today_done_banner_title")}
          </EmptyStateTitleText>
          <EmptyStateSubtitleText>
            {t("homepage.review_concept.today_done_banner_subtitle")}
          </EmptyStateSubtitleText>
        </View>
        <CompactActionButton
          label={t("homepage.review_concept.peek_at_tomorrow")}
          action="homepage.review_concept.peek_at_tomorrow"
          accessibilityLabel={t("homepage.review_concept.peek_a11y")}
          variant="secondary"
          size="sm"
          trailingIcon={ArrowRight}
          onPress={() => setPeekVisible(true)}
        />
      </CardSurface>
      <PeekAheadDialog
        visible={peekVisible}
        onClose={() => setPeekVisible(false)}
        days={peekAheadDays}
      />
    </>
  );
}

function TodayEmptyState({
  peekAheadDays,
}: {
  peekAheadDays?: PeekAheadDay[];
}) {
  const t = useTranslations();
  const [peekVisible, setPeekVisible] = useState(false);

  return (
    <>
      <CardSurface
        style={{
          paddingHorizontal: 15,
          paddingVertical: 12,
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
        }}
      >
        <CalendarCheck
          size={20}
          color={EMBER.colors.textMid}
          strokeWidth={1.8}
        />
        <View style={{ flex: 1 }}>
          <EmptyStateTitleText>
            {t("homepage.review_concept.today_empty_banner_title")}
          </EmptyStateTitleText>
          <EmptyStateSubtitleText>
            {t("homepage.review_concept.today_empty_banner_subtitle")}
          </EmptyStateSubtitleText>
        </View>
        <CompactActionButton
          label={t("homepage.review_concept.peek_at_tomorrow")}
          action="homepage.review_concept.peek_at_tomorrow"
          accessibilityLabel={t("homepage.review_concept.peek_a11y")}
          variant="secondary"
          size="sm"
          trailingIcon={ArrowRight}
          onPress={() => setPeekVisible(true)}
        />
      </CardSurface>
      <PeekAheadDialog
        visible={peekVisible}
        onClose={() => setPeekVisible(false)}
        days={peekAheadDays}
      />
    </>
  );
}

function PeekAheadDialog({
  visible,
  onClose,
  days,
}: {
  visible: boolean;
  onClose: () => void;
  days?: PeekAheadDay[];
}) {
  const t = useTranslations();
  // An empty schedule is a real state. Never fall back to the old stimulation
  // preview here: post-cycle and result homes can legitimately have nothing
  // scheduled, and showing a fabricated monitoring visit or medication is
  // clinically misleading.
  const peekDays =
    days && days.length > 0
      ? days
      : [
          {
            label: t("homepage.review_concept.inline.peek_day_tomorrow"),
            rows: [],
            emptyTitle: t("homepage.review_concept.inline.peek_empty_title"),
            emptyDetail: t("homepage.review_concept.inline.peek_empty_detail"),
          },
        ];
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const selectedDay = peekDays[selectedDayIndex] ?? peekDays[0]!;

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      layout={Platform.OS === "web" ? "centered" : "bottom"}
      closeButton="minimal"
      testID="peek-ahead-dialog"
      contentStyle={Platform.OS === "web" ? { paddingBottom: 8 } : undefined}
    >
      <View style={{ maxHeight: Dimensions.get("window").height * 0.72 }}>
        <DialogHeader
          title="A peek ahead"
          subtitle="The next few days may shift after monitoring, but this is the current plan."
          align="center"
          style={{ marginBottom: 16 }}
        />

        {/* One day at a time. The compact chip keeps three day names on one
            row in the narrow centred dialog on web; the selected day reads
            from its tint, so no check shifts the row as the day changes. */}
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 12,
            justifyContent: "center",
          }}
        >
          {peekDays.map((day, index) => (
            <MoonFilterChip
              key={`${day.label}-${index}`}
              label={day.label}
              selected={index === selectedDayIndex}
              onPress={() => setSelectedDayIndex(index)}
              action="peek_ahead_day"
              size="sm"
              showCheck={false}
            />
          ))}
        </View>

        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ gap: 9, paddingBottom: 4 }}
        >
          {selectedDay.rows.length > 0 ? (
            selectedDay.rows.map((row) => (
              <PeekAheadRow key={row.id} row={row} />
            ))
          ) : (
            <EmptyState
              testID="peek-ahead-empty-state"
              illustration={
                <DialogIcon
                  source={calendarEmptyIllustration}
                  width={68}
                  height={80}
                  testID="peek-ahead-empty-illustration"
                />
              }
              title={selectedDay.emptyTitle ?? "Nothing scheduled yet."}
              titleVariant="featured"
              subtitle={
                selectedDay.emptyDetail ??
                "If the clinic adds something, it will show up here."
              }
              density="compact"
              style={{ paddingHorizontal: 0 }}
            />
          )}
        </ScrollView>
      </View>
    </Dialog>
  );
}

function PeekAheadRow({ row }: { row: TodayRow }) {
  const rowIllustration = getTodayRowIllustration(row);

  return (
    <CardSurface
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        paddingHorizontal: 16,
        paddingVertical: 13,
      }}
    >
      {row.time ? (
        <View style={{ width: 58, flexShrink: 0 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              lineHeight: 15,
              color: EMBER.colors.textLight,
              fontWeight: "800",
            }}
          >
            {row.time}
          </Text>
        </View>
      ) : null}
      <View
        style={{
          width: 34,
          height: 34,
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {rowIllustration ? (
          <Image
            source={rowIllustration}
            resizeMode="contain"
            style={{ width: 32, height: 32 }}
          />
        ) : (
          <OrganicMiniIcon kind={row.icon} size={30} />
        )}
      </View>
      <View style={{ flex: 1 }}>
        <PrimaryTitleText>{row.title}</PrimaryTitleText>
        <SupportingSubtitleText>{row.detail}</SupportingSubtitleText>
      </View>
    </CardSurface>
  );
}

function TodayRowCompletionDialog({
  row,
  visible,
  onClose,
  onRequestDocumentAttachment,
  completionSource,
  onComplete,
  existingMedicationLog,
}: {
  row: TodayRow | null;
  visible: boolean;
  onClose: () => void;
  onRequestDocumentAttachment?: (row: TodayRow) => void;
  completionSource: "home" | "care";
  onComplete: (row: TodayRow) => void;
  existingMedicationLog?: MedicationLog;
}) {
  const t = useTranslations();
  const logDose = useLogMedicationDose();
  const createPatientNote = useCreatePatientNote();
  const timezone = useTimezone();
  const medicationLogOptions = useMemo(() => buildMedicationLogOptions(t), [t]);
  const [medicationStatus, setMedicationStatus] = useState("on-time");
  const [medicationNotes, setMedicationNotes] = useState("");
  const [actionStatus, setActionStatus] =
    useState<TodayActionCompletionStatus>("completed");
  const [actionNotes, setActionNotes] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const medicationSubmissionRef = useRef<{
    signature: string;
    key: string;
    payload: CreateMedicationLogPayload;
  } | null>(null);
  const actionSubmissionRef = useRef<{
    signature: string;
    key: string;
    payload: CreateNotePayload;
  } | null>(null);

  const existingStatus = existingMedicationLog?.status;
  const existingNotes = existingMedicationLog?.notes;
  useEffect(() => {
    setMedicationStatus(
      existingStatus === "skipped"
        ? "skipped"
        : existingStatus === "late"
          ? "late"
          : existingStatus === "missed"
            ? "missed"
            : "on-time",
    );
    setMedicationNotes(existingNotes ?? "");
    setActionStatus("completed");
    setActionNotes("");
    setSaveError(null);
    medicationSubmissionRef.current = null;
    actionSubmissionRef.current = null;
  }, [row?.id, visible, existingStatus, existingNotes]);

  if (!row) return null;

  const isMedication = row.kind === "medication";
  const isMonitoring =
    row.kind === "monitoring" || row.appointmentResultType === "monitoring";
  const isAppointment = row.kind === "appointment" || row.kind === "monitoring";
  const medicationIllustration = isMedication
    ? getMedicationLogIllustration(row)
    : null;
  const title = isMonitoring
    ? t("homepage.review_concept.log_monitoring_visit_title")
    : t("homepage.review_concept.log_prefix", {
        params: { title: row.title },
      });
  const summary = isMedication
    ? t("homepage.review_concept.log_medication_summary")
    : isMonitoring
      ? t("homepage.review_concept.say_whether_you_went_to_the_visit")
      : isAppointment
        ? t("homepage.review_concept.log_appointment_summary")
        : t("homepage.review_concept.log_task_summary");

  // A monitoring visit is a yes or no question; anything that needs follow-up
  // goes in the notes. No is saved as what it says, that she did not go,
  // rather than as a request for help she never made.
  const attendanceOptions: MoonSegmentedOption<
    Extract<TodayActionCompletionStatus, "attended" | "did_not_attend">
  >[] = [
    {
      value: "attended",
      label: t("homepage.review_concept.monitoring_attendance.yes_attended"),
      action: "today_completion_choice",
    },
    {
      value: "did_not_attend",
      label: t("homepage.review_concept.monitoring_attendance.no_not_attended"),
      action: "today_completion_choice",
    },
  ];
  const attendanceAnswer =
    attendanceOptions.find((option) => option.value === actionStatus)?.value ??
    null;

  const actionOptions = isAppointment
    ? [
        {
          id: "completed" as const,
          label: t("homepage.review_concept.appointment_log_complete_label"),
          detail: t("homepage.review_concept.appointment_log_complete_detail"),
        },
        {
          id: "rescheduled" as const,
          label: t("homepage.review_concept.appointment_log_rescheduled_label"),
          detail: t(
            "homepage.review_concept.appointment_log_rescheduled_detail",
          ),
        },
        {
          id: "needs_help" as const,
          label: t("homepage.review_concept.appointment_log_issue_label"),
          detail: t("homepage.review_concept.appointment_log_issue_detail"),
        },
      ]
    : [
        {
          id: "completed" as const,
          label: t("homepage.review_concept.task_log_complete_label"),
          detail: t("homepage.review_concept.task_log_complete_detail"),
        },
      ];

  const handleSave = async () => {
    if (isMedication && !row.medicationId) {
      setSaveError(
        t("homepage.review_concept.medication_log_missing_medication_id"),
      );
      return;
    }

    setSaveError(null);
    try {
      if (isMedication) {
        const apiStatus: DoseStatus =
          medicationStatus === "late"
            ? "late"
            : medicationStatus === "missed"
              ? "missed"
              : medicationStatus === "skipped"
                ? "skipped"
                : "taken";
        const medicationIntent = {
          patientMedicationId: row.medicationId!,
          medicationName: row.medicationName ?? row.title,
          dosage: row.medicationDose ?? null,
          scheduledFor: row.scheduledFor ?? undefined,
          status: apiStatus,
          notes: medicationNotes.trim() ? medicationNotes.trim() : null,
          reminderId: null,
        } satisfies Omit<CreateMedicationLogPayload, "takenAt">;
        const signature = JSON.stringify(medicationIntent);
        const existingSubmission = medicationSubmissionRef.current;
        const submission =
          existingSubmission?.signature === signature
            ? existingSubmission
            : {
                signature,
                key: createIdempotencyKey(),
                payload: {
                  ...medicationIntent,
                  takenAt: new Date().toISOString(),
                },
              };
        medicationSubmissionRef.current = submission;
        await logDose.mutateAsync({
          ...submission.payload,
          idempotencyKey: submission.key,
        });
      } else {
        const date = row.date ?? getTodayString(timezone);
        const actionIntent = {
          actionId: getTodayActionId(row),
          kind:
            row.kind === "monitoring"
              ? "monitoring"
              : row.kind === "task"
                ? "task"
                : "appointment",
          status: actionStatus,
          date,
          notes: actionNotes.trim(),
          source: completionSource,
        } as const;
        const signature = JSON.stringify(actionIntent);
        const existingSubmission = actionSubmissionRef.current;
        const submission =
          existingSubmission?.signature === signature
            ? existingSubmission
            : {
                signature,
                key: createIdempotencyKey(),
                payload: {
                  cycleId: null,
                  date,
                  text: serializeTodayActionCompletion({
                    ...actionIntent,
                    recordedAt: new Date().toISOString(),
                  }),
                },
              };
        actionSubmissionRef.current = submission;
        await createPatientNote.mutateAsync({
          ...submission.payload,
          idempotencyKey: submission.key,
        });
      }
    } catch (error) {
      console.error(
        "[TodayRowCompletionDialog] Failed to save completion:",
        error,
      );
      setSaveError(
        t(
          isMedication
            ? "homepage.review_concept.medication_log_save_failed"
            : "homepage.review_concept.action_log_save_failed",
        ),
      );
      return;
    }

    onComplete(row);
  };

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      layout={Platform.OS === "web" ? "centered" : "bottom"}
      closeButton="minimal"
    >
      <View style={{ maxHeight: Dimensions.get("window").height * 0.78 }}>
        {medicationIllustration ? (
          <DialogIcon
            source={medicationIllustration}
            width={96}
            height={88}
            style={{ marginBottom: 10 }}
            testID="medication-log-illustration"
          />
        ) : null}
        {/* Centred under the dose illustration; otherwise left, clear of
            the close button. */}
        <DialogHeader
          align={medicationIllustration ? "center" : "start"}
          title={title}
          subtitle={summary}
          style={{ marginBottom: 16 }}
        />

        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{
            gap: 14,
            paddingBottom: 18,
          }}
        >
          {isMedication ? (
            <>
              <View style={{ gap: 8 }}>
                {medicationLogOptions.map((option) => {
                  const visual = MEDICATION_LOG_VISUALS[option.id];
                  return (
                    <TodayCompletionChoice
                      key={option.id}
                      label={option.label}
                      detail={option.detail}
                      selected={medicationStatus === option.id}
                      icon={visual.icon}
                      iconColor={visual.color}
                      onPress={() => {
                        haptic("selection");
                        setMedicationStatus(option.id);
                      }}
                    />
                  );
                })}
              </View>
              <DialogNotesField
                label={t("homepage.review_concept.field_notes")}
                value={medicationNotes}
                onChangeText={setMedicationNotes}
                placeholder={t(
                  "homepage.review_concept.placeholder_medication_notes",
                )}
              />
            </>
          ) : (
            <>
              {isMonitoring ? (
                <MoonFormField
                  label={t(
                    "homepage.review_concept.monitoring_attendance.did_you_attend_this_visit",
                  )}
                >
                  <MoonSegmentedControl
                    tabs={attendanceOptions}
                    active={attendanceAnswer}
                    onChange={(next) => {
                      haptic("selection");
                      setActionStatus(next);
                    }}
                    optionRole="radio"
                    accessibilityLabel={t(
                      "homepage.review_concept.monitoring_attendance.did_you_attend_this_visit",
                    )}
                    style={{ marginTop: 0 }}
                  />
                </MoonFormField>
              ) : (
                <View style={{ gap: 8 }}>
                  {actionOptions.map((option) => (
                    <TodayCompletionChoice
                      key={option.id}
                      label={option.label}
                      detail={option.detail}
                      selected={actionStatus === option.id}
                      onPress={() => {
                        haptic("selection");
                        setActionStatus(option.id);
                      }}
                    />
                  ))}
                </View>
              )}
              <DialogNotesField
                label={t("homepage.review_concept.field_notes")}
                value={actionNotes}
                onChangeText={setActionNotes}
                placeholder={t(
                  isMonitoring
                    ? "homepage.review_concept.placeholder_monitoring_notes"
                    : isAppointment
                      ? "homepage.review_concept.placeholder_appointment_result"
                      : "homepage.review_concept.placeholder_generic_notes",
                )}
              />
              {onRequestDocumentAttachment ? (
                <DocumentFileDropZone
                  compact
                  onPick={() => onRequestDocumentAttachment(row)}
                  primaryLabel={t("homepage.review_concept.attach_document")}
                  secondaryLabel={t("care.beta_result.documents_drop_hint")}
                  action="attach_document"
                />
              ) : null}
            </>
          )}
        </ScrollView>

        {saveError ? (
          <ActionBanner
            variant="important"
            message={saveError}
            style={{ marginTop: 2 }}
          />
        ) : null}

        <View
          style={{
            flexDirection: "row",
            gap: 10,
            paddingTop: 6,
          }}
        >
          {isMedication ? (
            <>
              <SecondaryButton
                label={t("homepage.review_concept.dialog_cancel")}
                onPress={onClose}
                flex={1}
              />
              <PrimaryButton
                label={t("homepage.review_concept.dialog_save")}
                onPress={() => void handleSave()}
                loading={logDose.isPending}
                flex={1}
              />
            </>
          ) : (
            <>
              <SecondaryButton
                label={t("homepage.review_concept.dialog_cancel")}
                onPress={onClose}
                flex={1}
              />
              <PrimaryButton
                label={t("homepage.review_concept.dialog_save")}
                onPress={() => void handleSave()}
                loading={createPatientNote.isPending}
                flex={1}
              />
            </>
          )}
        </View>
      </View>
    </Dialog>
  );
}

/**
 * One way to log a Today row: the canonical Row with a check on the current
 * choice. A medication choice leads with its dose status icon in the status
 * hue the logged row will show. The input fill keeps the dialog's scroll
 * area from clipping a glass row's shadow into a grey band.
 */
function TodayCompletionChoice({
  label,
  detail,
  selected,
  icon: Icon,
  iconColor = semanticColors.text.secondary,
  onPress,
}: {
  label: string;
  detail: string;
  selected: boolean;
  icon?: LucideIcon;
  iconColor?: string;
  onPress: () => void;
}) {
  return (
    <Row
      surface="input"
      // Dense, so four choices and the notes field fit in the sheet.
      density="dense"
      paddingVertical={10}
      leading={
        Icon ? (
          <Icon size={20} color={iconColor} strokeWidth={1.5} />
        ) : undefined
      }
      leadingSize={24}
      title={label}
      subtitle={detail}
      chevron={false}
      trailing={
        selected ? (
          <Check
            size={18}
            color={semanticColors.text.primary}
            strokeWidth={2.2}
          />
        ) : undefined
      }
      accessibilityState={{ selected }}
      action="today_completion_choice"
      onPress={onPress}
    />
  );
}

function TaskList({
  values,
  action,
  onAction,
  onCompletionFeedback,
}: {
  values: Task[];
  action?: PlanAction;
  onAction: (action: PlanAction) => void;
  onCompletionFeedback?: () => void;
}) {
  const rows = values.map(taskToTodayRow);

  return (
    <View style={{ gap: 10 }}>
      <TodayList
        rows={rows}
        onCompletionFeedback={onCompletionFeedback}
        showCompletion
      />
      {action ? (
        <View style={{ alignSelf: "flex-start" }}>
          <CompactActionButton
            label={action.label}
            trailingIcon={ArrowRight}
            minWidth={168}
            onPress={() => onAction(action)}
          />
        </View>
      ) : null}
    </View>
  );
}

/**
 * A quiet text action inside the hero, for the steps the single primary button
 * cannot hold. Extracted so the recorded-result hero can carry more than one
 * without the two drifting apart in size or colour.
 */
function HeroInlineLink({
  label,
  onPress,
}: {
  label: string;
  onPress: () => void;
}) {
  return (
    <TextActionButton
      label={label}
      action="hero_inline_link"
      onPress={onPress}
      tone="onDark"
      size="sm"
      style={{ paddingHorizontal: 0, paddingVertical: 8 }}
    />
  );
}

function TimelineView({ items }: { items: TimelineItem[] }) {
  return (
    <View style={{ gap: 0 }}>
      {items.map((item, index) => {
        const isDone = item.status === "done";
        const isNow = item.status === "now";
        return (
          <View
            key={`${item.label}-${index}`}
            style={{ flexDirection: "row", gap: 10 }}
          >
            <View style={{ alignItems: "center", width: 20 }}>
              <View
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 999,
                  backgroundColor: isDone
                    ? semanticColors.status.success
                    : isNow
                      ? colors.softBrown
                      : "rgba(107,85,72,0.24)",
                  marginTop: 3,
                }}
              />
              {index < items.length - 1 ? (
                <View
                  style={{
                    width: 1,
                    flex: 1,
                    minHeight: 24,
                    backgroundColor: "rgba(107,85,72,0.18)",
                    marginTop: 4,
                    marginBottom: 4,
                  }}
                />
              ) : null}
            </View>
            <View style={{ flex: 1, paddingBottom: 13 }}>
              <CardTitleText>{item.label}</CardTitleText>
              <CardSubtitleText>{item.detail}</CardSubtitleText>
            </View>
          </View>
        );
      })}
    </View>
  );
}

function OutcomeActionRowsView({
  rows,
  onAction,
}: {
  rows: OutcomeActionRow[];
  onAction: (action: PlanAction) => void;
}) {
  return (
    <View style={{ gap: 10 }}>
      {rows.map((row) => (
        <ActionRowCard
          key={row.title}
          leading={<OrganicMiniIcon kind={row.icon} size={30} />}
          title={row.title}
          subtitle={row.detail}
          onPress={row.action ? () => onAction(row.action!) : undefined}
          // A chevron promises somewhere to go, and `ActionRowCard` draws one
          // unless something else occupies that slot. A row with no action has
          // nowhere to send her — "Wait" is a statement, not a door — so it
          // takes the slot with nothing in it. Done here rather than in the
          // shared primitive, which twenty-nine other rows rely on.
          trailing={
            row.comingSoon ? (
              <StatusBadge type="coming-soon" />
            ) : row.action ? undefined : (
              <View />
            )
          }
        />
      ))}
    </View>
  );
}

/**
 * The carousel of informative cards, exported because a first pregnancy
 * announcement shows the same cards in a window on the cycle screen. One
 * presentation, so the two places cannot drift.
 */
export function WhatToKnowView({ cards }: { cards: WhatToKnowCard[] }) {
  return (
    <HorizontalSnapCarousel
      items={cards}
      equalizeItemHeights
      measureKey="active-cycle-what-to-know-v2"
      gap={10}
      fallbackCardWidth={260}
      getCardWidth={(containerWidth) =>
        Math.min(288, Math.max(236, containerWidth * 0.82))
      }
      keyExtractor={(card) => card.title}
      renderItem={(card, width, _index, equalizedHeight) => (
        <WhatToKnowCarouselCard
          card={card}
          width={width}
          equalizedHeight={equalizedHeight}
        />
      )}
      contentContainerStyle={{ paddingRight: 4 }}
    />
  );
}

function WhatToKnowCarouselCard({
  card,
  width,
  equalizedHeight,
}: {
  card: WhatToKnowCard;
  width: number;
  equalizedHeight?: number;
}) {
  const isCaution = card.tone === "caution";
  return (
    <CardSurface
      tone={isCaution ? "caution" : "default"}
      style={{
        width,
        minHeight: equalizedHeight,
        paddingHorizontal: 12,
        paddingVertical: 12,
        gap: 9,
      }}
    >
      {/* The badge, and the card's own glyph beside it when it has one —
          drawn in the app's hand, and large enough to be read at a glance
          rather than decorate. Space-between so the two sit at the card's
          corners instead of crowding each other on a narrow card. */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <Tag label={card.badge} style={{ alignSelf: "flex-start" }} />
        {card.illustration ? (
          <OrganicMiniIcon kind={card.illustration} size={34} />
        ) : null}
      </View>
      <CardTitleText>{card.title}</CardTitleText>
      <CardSubtitleText style={{ marginTop: 0 }}>{card.body}</CardSubtitleText>
      <View style={{ gap: 7 }}>
        {card.bullets.map((entry) => {
          const bullet =
            typeof entry === "string" ? { label: entry, tag: null } : entry;
          // A tagged step is one we could work out for her, so its marker is the
          // accent rather than the same faint dot as the prose around it. The
          // untagged lines stay quiet: colouring everything would be colouring
          // nothing.
          const hasTag = !!bullet.tag;
          return (
            <View
              key={bullet.label}
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 7,
              }}
            >
              {bullet.icon ? (
                <View style={{ marginTop: -3, marginLeft: -2, flexShrink: 0 }}>
                  <OrganicMiniIcon kind={bullet.icon} size={22} />
                </View>
              ) : (
                <View
                  style={{
                    width: hasTag ? 6 : 5,
                    height: hasTag ? 6 : 5,
                    borderRadius: 999,
                    backgroundColor: isCaution
                      ? "rgba(121,70,51,0.58)"
                      : hasTag
                        ? EMBER.colors.warm
                        : "rgba(92,52,32,0.36)",
                    marginTop: 6,
                    flexShrink: 0,
                  }}
                />
              )}
              <View style={{ flex: 1, gap: 4 }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    lineHeight: 16,
                    color: EMBER.colors.text,
                    fontWeight: "700",
                  }}
                >
                  {bullet.label}
                </Text>
                {bullet.tag ? (
                  <Tag
                    label={bullet.tag}
                    tone="gold"
                    style={{ alignSelf: "flex-start" }}
                  />
                ) : null}
              </View>
            </View>
          );
        })}
      </View>
    </CardSurface>
  );
}

/** The log dialog's notes field: the canonical field label and chrome. */
function DialogNotesField({
  label,
  value,
  onChangeText,
  placeholder,
}: {
  label: string;
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <TextField
      label={label}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      notes
    />
  );
}

function DecisionView({ options }: { options: DecisionOption[] }) {
  return (
    <View style={{ gap: 14 }}>
      {options.map((option) => (
        <View key={option.title}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                flex: 1,
                fontSize: 14,
                lineHeight: 18,
                color: EMBER.colors.text,
                fontWeight: "800",
              }}
            >
              {option.title}
            </Text>
            <StatusBadge label={option.status} />
          </View>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 16,
              color: EMBER.colors.textMid,
              marginTop: 4,
            }}
          >
            {option.detail}
          </Text>
        </View>
      ))}
    </View>
  );
}

function LabMetricPill({ metric }: { metric: LabUpdateMetric }) {
  const isPending = metric.value.toLowerCase() === "pending";

  return (
    <View style={{ gap: 2 }}>
      <MetricLabelText>{metric.label}</MetricLabelText>
      <MetricValueText
        style={isPending ? { color: EMBER.colors.textLight } : undefined}
      >
        {metric.value}
      </MetricValueText>
      <MetaText style={{ marginTop: 0 }}>{metric.note}</MetaText>
    </View>
  );
}

function LabUpdateCarouselCard({
  card,
  width,
  equalizedHeight,
}: {
  card: LabUpdateCard;
  width: number;
  equalizedHeight?: number;
}) {
  const t = useTranslations();
  const isPending = card.status === "pending";

  return (
    <CardSurface
      variant="panel"
      style={{
        width,
        minHeight: equalizedHeight,
        padding: 13,
        gap: 11,
        opacity: isPending ? 0.78 : 1,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <CardHeader
          style={{ flex: 1 }}
          title={card.title}
          subtitle={card.timeframe}
          subtitleVariant="meta"
          titleStyle={{
            fontSize: 16,
            lineHeight: 20,
            fontWeight: "900",
          }}
          subtitleStyle={{ marginTop: 2, fontWeight: "800" }}
        />
        <StatusBadge
          label={
            card.status === "next"
              ? t("homepage.review_concept.next_label")
              : t("homepage.review_concept.not_yet_label")
          }
          tone={card.status === "next" ? "gold" : "neutral"}
        />
      </View>

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 17,
          color: EMBER.colors.textMid,
          fontWeight: "500",
        }}
      >
        {card.body}
      </Text>

      <View style={{ gap: 12 }}>
        {card.metrics.map((metric) => (
          <LabMetricPill
            key={`${card.title}-${metric.label}`}
            metric={metric}
          />
        ))}
      </View>
    </CardSurface>
  );
}

export function LabUpdateCarousel({ cards }: { cards: LabUpdateCard[] }) {
  return (
    <HorizontalSnapCarousel
      items={cards}
      gap={10}
      fallbackCardWidth={252}
      getCardWidth={(containerWidth) =>
        Math.min(282, Math.max(238, containerWidth * 0.82))
      }
      keyExtractor={(card) => card.title}
      equalizeItemHeights
      measureKey="embryo-lab-update-carousel"
      renderItem={(card, cardWidth, _index, equalizedHeight) => (
        <LabUpdateCarouselCard
          card={card}
          width={cardWidth}
          equalizedHeight={equalizedHeight}
        />
      )}
    />
  );
}

function CycleLogisticsStatusIcon({
  status,
}: {
  status: CycleLogisticsChecklistItem["status"];
}) {
  const isDone = status === "done";

  // A bare glyph in a fixed-width slot, so the labels still line up. A done
  // item's check takes the label's colour; a pending one stays quieter, and
  // background shows an info mark rather than a tick it hasn't earned.
  return (
    <View
      style={{
        width: 20,
        height: 20,
        alignItems: "center",
        justifyContent: "center",
        marginTop: -2,
      }}
    >
      {isDone ? (
        <Check size={14} color={EMBER.colors.text} strokeWidth={2.2} />
      ) : status === "context" ? (
        <Info size={14} color={EMBER.colors.textLight} strokeWidth={1.9} />
      ) : (
        <Clock3
          size={14}
          color={
            status === "next" ? EMBER.colors.textMid : EMBER.colors.textLight
          }
          strokeWidth={1.9}
        />
      )}
    </View>
  );
}

function CycleLogisticsCarouselCard({
  card,
  width,
  equalizedHeight,
  onAction,
}: {
  card: CycleLogisticsCard;
  width: number;
  equalizedHeight?: number;
  onAction: (action: PlanAction) => void;
}) {
  return (
    <CardSurface
      style={{
        width,
        minHeight: equalizedHeight,
        padding: 13,
        gap: 11,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          gap: 10,
        }}
      >
        <View
          style={{
            width: 38,
            height: 38,
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <OrganicMiniIcon kind={card.icon} size={36} />
        </View>
        <CardHeader
          style={{ flex: 1 }}
          title={card.title}
          subtitle={card.timeframe}
          subtitleVariant="meta"
          titleStyle={{
            fontSize: 16,
            lineHeight: 20,
            fontWeight: "900",
          }}
          subtitleStyle={{ marginTop: 2, fontWeight: "800" }}
        />
      </View>

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 17,
          color: EMBER.colors.textMid,
          fontWeight: "500",
        }}
      >
        {card.body}
      </Text>

      <View style={{ gap: 8 }}>
        {card.items.map((item) => (
          <View
            key={`${card.title}-${item.label}`}
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 9,
            }}
          >
            <CycleLogisticsStatusIcon status={item.status} />
            <View style={{ flex: 1 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12.5,
                  lineHeight: 16,
                  color: EMBER.colors.text,
                  fontWeight: "800",
                }}
              >
                {item.label}
              </Text>
              <MetaText style={{ marginTop: 1, fontWeight: "600" }}>
                {item.detail}
              </MetaText>
            </View>
          </View>
        ))}
      </View>

      {card.action ? (
        <SectionActionLink
          label={card.action.label}
          onPress={() => onAction(card.action!)}
          style={{ alignSelf: "flex-start", paddingLeft: 0, marginTop: "auto" }}
        />
      ) : null}
    </CardSurface>
  );
}

function CycleLogisticsCarousel({
  cards,
  onAction,
}: {
  cards: CycleLogisticsCard[];
  onAction: (action: PlanAction) => void;
}) {
  return (
    <HorizontalSnapCarousel
      items={cards}
      gap={10}
      fallbackCardWidth={252}
      getCardWidth={(containerWidth) =>
        Math.min(282, Math.max(238, containerWidth * 0.82))
      }
      keyExtractor={(card) => card.title}
      equalizeItemHeights
      measureKey="active-cycle-logistics-carousel"
      renderItem={(card, cardWidth, _index, equalizedHeight) => (
        <CycleLogisticsCarouselCard
          card={card}
          width={cardWidth}
          equalizedHeight={equalizedHeight}
          onAction={onAction}
        />
      )}
    />
  );
}

/**
 * The exploration door, closed by default.
 *
 * Same shape as the collapsible funnel beside it, so the page has one way of
 * saying "there is more here if you want it" rather than two.
 */
function CollapsibleExploreOptionsWidget({
  widget,
  onAction,
}: {
  widget: Extract<Widget, { type: "explore-options" }>;
  onAction: (action: PlanAction) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <CardSurface style={{ padding: 15 }}>
      <Accordion
        type="multiple"
        value={expanded ? ["explore-options"] : []}
        onValueChange={(value: string[]) =>
          setExpanded(value.includes("explore-options"))
        }
      >
        <AccordionItem value="explore-options">
          <AccordionTrigger
            accessibilityLabel={`${expanded ? "Collapse" : "Expand"} ${widget.title}`}
            analyticsLabel={widget.title}
            className="gap-2.5 py-0"
          >
            <CardHeader
              leading={<OrganicMiniIcon kind={widget.icon} size={36} />}
              title={widget.title}
              subtitle={widget.subtitle}
              style={{ flex: 1, minWidth: 0 }}
            />
          </AccordionTrigger>

          <AccordionContent className="pb-0">
            <View style={{ paddingTop: 12 }}>
              <OutcomeActionRowsView rows={widget.rows} onAction={onAction} />
            </View>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </CardSurface>
  );
}

function CollapsibleOutcomeEstimateFunnelWidget({
  widget,
  onAction,
}: {
  widget: Extract<Widget, { type: "outcome-estimate-funnel" }>;
  onAction: (action: PlanAction) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const { data: patient } = usePatient();
  const userAge = calculateAge(patient?.dateOfBirth);

  return (
    <CardSurface style={{ padding: 15 }}>
      <Accordion
        type="multiple"
        value={expanded ? ["outcome-estimate"] : []}
        onValueChange={(value: string[]) =>
          setExpanded(value.includes("outcome-estimate"))
        }
      >
        <AccordionItem value="outcome-estimate">
          <AccordionTrigger
            accessibilityLabel={`${expanded ? "Collapse" : "Expand"} ${widget.title}`}
            analyticsLabel={widget.title}
            className="gap-2.5 py-0"
          >
            <CardHeader
              leading={<OrganicMiniIcon kind={widget.icon} size={36} />}
              title={widget.title}
              subtitle={widget.subtitle}
              style={{ flex: 1, minWidth: 0 }}
            />
          </AccordionTrigger>

          <AccordionContent className="pb-0">
            <View style={{ paddingTop: 12 }}>
              <OutcomeEstimateFunnelSection
                userAge={userAge}
                onAction={onAction}
                variant={widget.variant}
              />
            </View>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </CardSurface>
  );
}

function CryoVaultWidget({
  onAction,
}: {
  onAction: (action: PlanAction) => void;
}) {
  const t = useTranslations();
  const { storageGroups } = useFrozenSpecimens();
  const { data: oocyteCohorts = [] } = useOocyteCohorts();
  const buildSnapshots = useCryoSpecimenSnapshotsBuilder();
  const liveSpecimens = buildSnapshots(storageGroups, oocyteCohorts);
  const openVault = () =>
    onAction({
      label: t("homepage.review_concept.inline.action_view_cryo_vault"),
      screen: "CryoVaultScreen",
    });
  return (
    <CryoVaultOverviewCard
      specimens={liveSpecimens}
      onPress={openVault}
      onAddSpecimen={openVault}
      onNoSpecimens={openVault}
    />
  );
}

function WidgetView({
  widget,
  onAction,
  renderMonitoringCard,
  renderSemenAnalysisPending,
  realTestResults,
  testResultsQueryState,
  projectionSourceCycleId,
  onOpenMedicationFactSheet,
  onOpenProcedureFactSheet,
  onOpenTestFactSheet,
  onRequestDocumentAttachment,
  onCompletionFeedback,
  todayRows,
  peekAheadDays,
  viewerIsSupporting = false,
  currentStepExplainer = null,
}: {
  widget: Widget;
  onAction: (action: PlanAction) => void;
  renderMonitoringCard?: (
    widget: Extract<Widget, { type: "monitoring-chart" }>,
  ) => ReactNode;
  renderSemenAnalysisPending?: () => ReactNode;
  realTestResults?: ResultInterpretation[];
  testResultsQueryState?: TestResultInsightsQueryState;
  projectionSourceCycleId?: string | null;
  onOpenMedicationFactSheet?: (
    payload: HomepageMedicationFactSheetPayload,
  ) => void;
  onOpenProcedureFactSheet?: (
    payload: HomepageProcedureFactSheetPayload,
  ) => void;
  onOpenTestFactSheet?: (payload: HomepageTestFactSheetPayload) => void;
  onRequestDocumentAttachment?: (row: TodayRow) => void;
  onCompletionFeedback?: () => void;
  /** Real today agenda from the patient's meds/appointments. When provided
   *  (even empty), it replaces the variant's fixture rows so the Today section
   *  reflects real data and falls back to its empty state. */
  todayRows?: TodayRow[];
  peekAheadDays?: PeekAheadDay[];
  /** A partner sees the patient's Today read-only: nothing to tick. */
  viewerIsSupporting?: boolean;
  /** What a `step-explainer` widget shows; see `useCurrentStepExplainer`. */
  currentStepExplainer?: StepExplainer | null;
}) {
  if (widget.type === "today") {
    return (
      <TodayList
        rows={todayRows ?? widget.rows}
        showCompletion={!viewerIsSupporting}
        onOpenMedicationFactSheet={onOpenMedicationFactSheet}
        onOpenProcedureFactSheet={onOpenProcedureFactSheet}
        onRequestDocumentAttachment={onRequestDocumentAttachment}
        onCompletionFeedback={onCompletionFeedback}
        peekAheadDays={peekAheadDays}
      />
    );
  }

  if (widget.type === "action-rows") {
    return <OutcomeActionRowsView rows={widget.rows} onAction={onAction} />;
  }

  if (widget.type === "explore-options") {
    return (
      <CollapsibleExploreOptionsWidget widget={widget} onAction={onAction} />
    );
  }

  if (widget.type === "outcome-estimate-funnel") {
    return (
      <CollapsibleOutcomeEstimateFunnelWidget
        widget={widget}
        onAction={onAction}
      />
    );
  }

  if (widget.type === "test-results") {
    return (
      <TestResultsPreview
        results={realTestResults}
        title={widget.title}
        subtitle={widget.subtitle}
        queryState={testResultsQueryState}
        onAction={onAction}
        onOpenTestFactSheet={onOpenTestFactSheet}
      />
    );
  }

  if (widget.type === "monitoring-chart") {
    if (renderMonitoringCard) return renderMonitoringCard(widget);
    return (
      <MonitoringEmptyStateCard
        title={widget.title}
        onAddData={() => onAction(widget.primaryAction)}
        showSectionTitle={false}
        marginTop={0}
      />
    );
  }

  if (widget.type === "semen-analysis-pending") {
    return renderSemenAnalysisPending?.() ?? null;
  }

  if (widget.type === "data-status") {
    return (
      <ActionRowCard
        leading={<OrganicMiniIcon kind={widget.icon} size={30} />}
        title={widget.title}
        subtitle={widget.detail}
        onPress={widget.action ? () => onAction(widget.action!) : undefined}
        accessibilityLabel={`${widget.title}. ${widget.detail}`}
      />
    );
  }

  if (widget.type === "records-status") {
    if (widget.state === "loading") {
      return (
        <CardSurface
          testID="home-records-loading"
          accessibilityLiveRegion="polite"
          style={{ minHeight: 86, padding: 16, justifyContent: "center" }}
        >
          <LoadingState
            caption={widget.loadingCaption}
            style={{ paddingVertical: 0 }}
          />
        </CardSurface>
      );
    }
    return (
      <ClinicalDataStatus
        state="unavailable"
        title={widget.unavailableTitle}
        message={widget.unavailableMessage}
        retryLabel={widget.retryLabel}
        retryingLabel={widget.retryingLabel}
        onRetry={widget.onRetry}
        isRetrying={widget.isRetrying}
        testID="home-records-unavailable"
      />
    );
  }

  if (widget.type === "what-to-know") {
    return <WhatToKnowView cards={widget.cards} />;
  }

  if (widget.type === "cycle-logistics") {
    return <CycleLogisticsCarousel cards={widget.cards} onAction={onAction} />;
  }

  if (widget.type === "semen-analysis") {
    return (
      <SpermAnalysisSkeletonCard
        onAction={onAction}
        layout="single"
        results={widget.results}
        emptyCard={widget.emptyCard}
      />
    );
  }

  if (widget.type === "sperm-result-paths") {
    return <SpermResultPathsCard onAction={onAction} />;
  }

  if (widget.type === "step-explainer") {
    return currentStepExplainer ? (
      <StepExplainerCard explainer={currentStepExplainer} />
    ) : null;
  }

  if (widget.type === "cryo-vault") {
    return <CryoVaultWidget onAction={onAction} />;
  }

  if (widget.type === "future-use-estimate") {
    return (
      <FutureUseEstimateTabs
        primaryUnit={widget.primaryUnit}
        sourceCycleId={projectionSourceCycleId}
        onAction={onAction}
      />
    );
  }

  if (widget.type === "sperm-cryosurvival-estimate") {
    return <SpermCryosurvivalEstimateWidget onAction={onAction} />;
  }

  if (widget.type === "handoff") {
    /*
     * No list builds one of these today, and the rule is narrower than "don't":
     * a handoff meant to *close the page* cannot be placed from a list, because
     * a list renders above the records section `PlanHomeScreen` appends. The
     * post-cycle page stopped needing one — "What now?" answers the same
     * question as an ordinary section — but a mid-page handoff is still a
     * coherent thing to want, and this renders it.
     */
    return (
      <JourneyHandoffCard
        title={widget.title}
        body={widget.body}
        ctaLabel={widget.ctaLabel}
        checklist={widget.checklist}
        backgroundImage={handoffBackground}
        illustration={<OrganicExplanatoryIllustration kind="follicle-cohort" />}
        onPress={() => onAction(widget.action)}
      />
    );
  }

  if (widget.type === "funnel" && widget.bare) {
    // OutcomeDotFunnelRows already provides its own card, so render it without
    // an extra wrapper to avoid a double box under the section title.
    return (
      <>
        {widget.subtitle ? (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              lineHeight: 18,
              color: EMBER.colors.textLight,
              marginBottom: 8,
            }}
          >
            {widget.subtitle}
          </Text>
        ) : null}
        <OutcomeDotFunnelRows
          stages={widget.stages.map((stage, index) => ({
            id: `${stage.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${index}`,
            label: stage.label,
            value: stage.count,
            detail: stage.detail,
          }))}
          totalDots={widget.stages[0]?.count ?? 1}
        />
      </>
    );
  }

  if (widget.type === "tasks") {
    // Framed like every other widget so the list keeps its heading. Returning a
    // bare `TaskList` dropped `title` and `subtitle` on the floor, which left
    // rows like the post-positive red flags on screen with nothing saying what
    // they were — "Severe one-sided pain" reads as a task to do rather than a
    // reason to call the clinic. The action stays with `TaskList`, which renders
    // it as a pill button, so the frame is not given one to draw twice.
    return (
      <WidgetFrame
        title={widget.title}
        subtitle={widget.subtitle}
        icon={widget.icon}
        onAction={onAction}
      >
        <TaskList
          values={widget.tasks}
          action={widget.action}
          onAction={onAction}
          onCompletionFeedback={onCompletionFeedback}
        />
      </WidgetFrame>
    );
  }

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      icon={widget.icon}
      action={"action" in widget ? widget.action : undefined}
      onAction={onAction}
    >
      {widget.type === "metrics" ? (
        <MetricGrid metrics={widget.metrics} />
      ) : widget.type === "timeline" ? (
        <TimelineView items={widget.items} />
      ) : widget.type === "funnel" ? (
        // Inside the widget's card, so the rows sit straight in it.
        <OutcomeDotFunnelRows
          stages={widget.stages.map((stage, index) => ({
            id: `${stage.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${index}`,
            label: stage.label,
            value: stage.count,
            detail: stage.detail,
          }))}
          totalDots={widget.stages[0]?.count ?? 1}
          embedded
        />
      ) : widget.type === "lab-carousel" ? (
        <LabUpdateCarousel cards={widget.cards} />
      ) : (
        <DecisionView options={"options" in widget ? widget.options : []} />
      )}
    </WidgetFrame>
  );
}

export function ConceptSectionView({
  section: conceptSection,
  onAction,
  renderMonitoringCard,
  renderSemenAnalysisPending,
  hideSectionTitle = false,
  realTestResults,
  testResultsQueryState,
  projectionSourceCycleId,
  onOpenMedicationFactSheet,
  onOpenProcedureFactSheet,
  onOpenTestFactSheet,
  onRequestDocumentAttachment,
  onCompletionFeedback,
  topSpacing = 18,
  todayRows,
  peekAheadDays,
  viewerIsSupporting = false,
  currentStepExplainer = null,
}: {
  section: ConceptSection;
  onAction: (action: PlanAction) => void;
  renderMonitoringCard?: (
    widget: Extract<Widget, { type: "monitoring-chart" }>,
  ) => ReactNode;
  renderSemenAnalysisPending?: () => ReactNode;
  /**
   * The canonical monitoring widget owns its own title row. This is an
   * explicit Home-only layout decision; other ConceptSectionView consumers
   * should keep their section title even when previewing a monitoring widget.
   */
  hideSectionTitle?: boolean;
  realTestResults?: ResultInterpretation[];
  testResultsQueryState?: TestResultInsightsQueryState;
  projectionSourceCycleId?: string | null;
  onOpenMedicationFactSheet?: (
    payload: HomepageMedicationFactSheetPayload,
  ) => void;
  onOpenProcedureFactSheet?: (
    payload: HomepageProcedureFactSheetPayload,
  ) => void;
  onOpenTestFactSheet?: (payload: HomepageTestFactSheetPayload) => void;
  onRequestDocumentAttachment?: (row: TodayRow) => void;
  onCompletionFeedback?: () => void;
  topSpacing?: number;
  todayRows?: TodayRow[];
  peekAheadDays?: PeekAheadDay[];
  /** The viewer supports someone else's cycle; see WidgetView. */
  viewerIsSupporting?: boolean;
  /** See WidgetView. */
  currentStepExplainer?: StepExplainer | null;
}) {
  const t = useTranslations();
  const localizedActions = useMemo(() => buildActions(t), [t]);
  const sectionTitleIcons: Record<string, LucideIcon> = {
    "cycle logistics": CalendarClock,
    "good to know": Info,
    "monitoring progress": TrendingUp,
    testing: FlaskConical,
    today: CalendarCheck,
  };
  const sectionTitleIcon =
    sectionTitleIcons[conceptSection.title.toLowerCase()];
  const todayTitle = t(
    "homepage.review_concept.section.today_title",
  ).toLowerCase();
  const sectionAction =
    conceptSection.action ??
    (conceptSection.title.toLowerCase() === todayTitle ||
    conceptSection.title.toLowerCase() === "today"
      ? localizedActions.calendar
      : undefined);

  return (
    <View style={{ gap: 10, marginTop: topSpacing }}>
      {conceptSection.title && !hideSectionTitle ? (
        <SectionTitleRow
          title={conceptSection.title}
          icon={sectionTitleIcon}
          action={
            sectionAction
              ? {
                  label: sectionAction.label,
                  onPress: () => onAction(sectionAction),
                }
              : undefined
          }
          style={{ marginBottom: 0 }}
        />
      ) : null}
      {conceptSection.widgets.map((widget, index) => (
        <WidgetView
          key={`${conceptSection.title}-${widget.type}-${index}`}
          widget={widget}
          onAction={onAction}
          renderMonitoringCard={renderMonitoringCard}
          renderSemenAnalysisPending={renderSemenAnalysisPending}
          realTestResults={realTestResults}
          testResultsQueryState={testResultsQueryState}
          projectionSourceCycleId={projectionSourceCycleId}
          onOpenMedicationFactSheet={onOpenMedicationFactSheet}
          onOpenProcedureFactSheet={onOpenProcedureFactSheet}
          onOpenTestFactSheet={onOpenTestFactSheet}
          onRequestDocumentAttachment={onRequestDocumentAttachment}
          onCompletionFeedback={onCompletionFeedback}
          todayRows={todayRows}
          peekAheadDays={peekAheadDays}
          viewerIsSupporting={viewerIsSupporting}
          currentStepExplainer={currentStepExplainer}
        />
      ))}
    </View>
  );
}

type PostProcedureOutcomeStageView = {
  title: string;
  summary: string;
  sections: ConceptSection[];
};

type PostProcedureOutcomeMetadata = {
  source?: "admin" | "provider" | "patient" | "partner" | "system" | null;
  verificationStatus?: "unverified" | "verified" | "refuted" | null;
  /** What the record says the outcome was, for copy that must not be guessed. */
  recordedType?: PostProcedureOutcomeResult | null;
  /**
   * What changes when a pregnancy stops being one without `type` changing. The
   * acknowledged stage is decided once and never revisited, so any content that
   * only holds while a pregnancy is ongoing has to read these itself. See
   * `shouldShowEarlyPregnancyGuidance`.
   */
  resultStatus?: string | null;
  /**
   * Where the patient herself says the pregnancy stands. A birth and a loss are
   * both her statements and neither is on the result any more (#3492), so this
   * is what says a pregnancy has concluded.
   */
  patientPregnancyStands?: PatientPregnancyStands | "unknown" | null;
  /** Her record has been asked for and has not come back yet. */
  patientStatusPending?: boolean;
};

function buildPostProcedureOutcomeStageView(
  t: TranslationFunction,
  stage: PostProcedureOutcomeStage,
  contentKey: PostProcedureOutcomeContentKey | null,
  metadata: PostProcedureOutcomeMetadata,
  pregnancyTrackingActionLabel: string,
  route: PostProcedureOutcomeRoute,
  viewerIsSupporting: boolean,
): PostProcedureOutcomeStageView | null {
  /**
   * A standing pregnancy does not wait to be acknowledged.
   *
   * Everything this view builds — the record row, what the first weeks hold, the
   * offer to follow the pregnancy, somewhere to put a question — is information,
   * and none of it depends on a week count. Behind `stage === "recorded"` it
   * existed only once she had pressed "Let's look at what's next", a button whose
   * label promises nothing and which disappears when pressed. So a patient who
   * had reported a pregnancy and given no weeks had a page with her medication
   * list on it and nothing else. Withholding what the first weeks usually hold —
   * including which symptoms are worth a call — is not a thing to make her earn.
   *
   * The two-beat flow stays for every other ending. A negative or unsettled result
   * is a decision waiting to be made, and that is what the pause was designed for.
   */
  const standingPregnancy = shouldShowEarlyPregnancyGuidance({
    outcome: {
      type: metadata.recordedType,
      resultStatus: metadata.resultStatus,
      verificationStatus: metadata.verificationStatus,
    },
    patientPregnancyStands: metadata.patientPregnancyStands,
    patientStatusPending: metadata.patientStatusPending,
    viewerIsSupporting,
  });
  if (
    stage === "recorded" &&
    !standingPregnancy &&
    !metadata.patientStatusPending
  ) {
    return null;
  }

  const shared = "homepage.review_concept.post_procedure_outcome.shared";
  const modalityKey = contentKey ?? "ivf";
  const resultVariant = route.endsWith("-positive")
    ? "positive"
    : route.endsWith("-negative")
      ? "negative"
      : route.endsWith("-unavailable")
        ? "unavailable"
        : route.endsWith("-uncertain")
          ? "uncertain"
          : "adverse";
  const isPositive = resultVariant === "positive";
  const isNegative = resultVariant === "negative";
  const resultCopy = `homepage.review_concept.post_procedure_outcome.${modalityKey}.${resultVariant}`;
  const sourceKey = metadata.source ?? "unknown";
  const verificationKey = metadata.verificationStatus ?? "unverified";
  const saveQuestionAction: PlanAction = {
    label: t(`${shared}.stage_action_save_question`),
    signal: "open-ai-chat",
    params: {
      initialPrompt: t(`${shared}.stage_action_save_question_prompt`),
    },
  };
  const pregnancyTrackingAction: PlanAction = {
    label: pregnancyTrackingActionLabel,
    signal: "open-pregnancy-tracking-setup",
  };
  const readyRows: OutcomeActionRow[] = [
    ...(standingPregnancy
      ? [
          {
            title: pregnancyTrackingActionLabel,
            detail: t(`${shared}.stage_action_tracking_detail`),
            icon: "clinics" as const,
            action: pregnancyTrackingAction,
          },
        ]
      : []),
    {
      title: t(`${shared}.stage_action_save_question`),
      detail: t(`${shared}.stage_action_save_question_detail`),
      icon: "concierge",
      action: saveQuestionAction,
    },
  ];
  const resultValueKey = isPositive
    ? "known_result_value_pregnancy"
    : isNegative
      ? "known_result_value_no_pregnancy"
      : "known_result_value_not_confirmed";
  // "Unverified" is the assumed default for a patient-entered record, so
  // repeating it under a positive result reads as "this result isn't confirmed"
  // and undermines what she just recorded. Only surface the verification clause
  // when it carries new information — a clinic mark of verified, or a refuted
  // correction she should know about.
  const showsVerification =
    verificationKey === "verified" || verificationKey === "refuted";
  const knownResultDetail = [
    t(`${shared}.${resultValueKey}`),
    t(`${shared}.known_result_source_${sourceKey}`),
    showsVerification
      ? t(`${shared}.known_result_verification_${verificationKey}`)
      : null,
  ]
    .filter(Boolean)
    .join(" ");
  const knownResultRow: OutcomeActionRow = {
    title: t(`${resultCopy}.result_label`),
    detail: knownResultDetail,
    icon: "medical-records",
    // Opens PastCycleDetail for a closed cycle (or CycleDetail focused on the
    // result for an open one) so tapping the row lands on the recorded result
    // instead of doing nothing.
    action: {
      label: t(`${shared}.stage_action_review_records`),
      signal: "open-recorded-cycle",
      params: { focus: "outcome" },
    },
  };
  return {
    // The negative heading is shared across modalities and has no
    // per-modality `title`; see `buildPostProcedureOutcomeConcept`.
    title: isNegative
      ? t(`${shared}.take_a_breath_after_this_cycle`)
      : t(`${resultCopy}.title`),
    summary: t(`${resultCopy}.summary`),
    sections: [
      section(
        t(`${resultCopy}.today_section_title`),
        t(`${resultCopy}.section_subtitle`),
        [today(t(`${resultCopy}.today_section_title`), [])],
      ),
      section(t(`${shared}.known_title`), t(`${shared}.known_subtitle`), [
        actionRows([knownResultRow]),
      ]),
      section(
        t(`${shared}.when_ready_title`),
        t(`${shared}.when_ready_subtitle`),
        [actionRows(readyRows)],
      ),
    ],
  };
}

type BetaWaitAction = "schedule" | "record";

export function HomepageReviewConceptHome({
  variant,
  onAction,
  renderMonitoringCard,
  renderSemenAnalysisPending,
  testInsights,
  testResultsQueryState,
  projectionSourceCycleId,
  onOpenSchedule,
  onOpenJourneyMap,
  onOpenOutcomeFunnel,
  onOpenMedicationFactSheet,
  onOpenProcedureFactSheet,
  onOpenTestFactSheet,
  onRequestDocumentAttachment,
  todayRows: realTodayRows,
  peekAheadDays,
  afterTodaySlot,
  endOfPageSlot,
  onOpenPregnancyTrackingSetup,
  cycleStatusReviewKind,
  outcomeCycleId,
  endedCycleId,
  postCycleCycleId,
  postCycleEndDate,
  cycleEnding,
  viewerIsSupporting = false,
  pregnancyStatusReading = "unknown",
  onScrollToY,
}: {
  variant: HomepageReviewConceptVariant;
  pregnancyStatusReading?: PregnancyStatusReading;
  cycleStatusReviewKind?: CycleStatusReviewKind | null;
  onAction: (action: PlanAction) => void;
  renderMonitoringCard?: (
    widget: Extract<Widget, { type: "monitoring-chart" }>,
  ) => ReactNode;
  /**
   * The pending semen-analysis card, when the lab has not reported yet. It
   * sits in the sperm-freezing wait's "Today" section, below the hero.
   */
  renderSemenAnalysisPending?: () => ReactNode;
  /** Test results recorded for this patient; empty means no result is available. */
  testInsights?: ResultInterpretation[];
  /** Query state for the real Test Results read used by the Testing section. */
  testResultsQueryState?: TestResultInsightsQueryState;
  /** Closed cycle whose stored specimens back a post-cycle projection. */
  projectionSourceCycleId?: string | null;
  /**
   * Cycle that owns the outcome this home is presenting.
   *
   * `/active-cycle` returns nothing once a cycle closes, so the hero cannot find
   * a closed cycle's outcome on its own — without this the acknowledged-negative
   * pause is unreachable and the stage pins to `recorded` forever. The screen
   * resolves it from the same closed-cycle reading that chose the variant, so the
   * hero can never address a different cycle than the variant was derived from.
   */
  outcomeCycleId?: string | null;
  /**
   * The viewer is supporting someone else's cycle, not living it.
   *
   * The ending belongs to both of them — `acknowledgedAt` is on the shared
   * outcome — but the words for it do not. Defaults to the patient's voice, so a
   * surface that forgets to pass this addresses the person whose cycle it is.
   */
  viewerIsSupporting?: boolean;
  /**
   * Scroll the page that owns this block to an absolute offset.
   *
   * The scroll view belongs to the screen, not here, so reaching a section is
   * asked for rather than done. Absent, the hero action still acknowledges and
   * simply does not move the page.
   */
  onScrollToY?: (y: number) => void;
  onOpenSchedule?: (action: BetaWaitAction) => void;
  onOpenJourneyMap?: () => void;
  onOpenOutcomeFunnel?: () => void;
  onOpenMedicationFactSheet?: (
    payload: HomepageMedicationFactSheetPayload,
  ) => void;
  onOpenProcedureFactSheet?: (
    payload: HomepageProcedureFactSheetPayload,
  ) => void;
  onOpenTestFactSheet?: (payload: HomepageTestFactSheetPayload) => void;
  onRequestDocumentAttachment?: (row: TodayRow) => void;
  /** Real today agenda (meds + appointments) for the in-cycle Today section.
   *  When provided it replaces the variant's fixture rows. */
  todayRows?: TodayRow[];
  peekAheadDays?: PeekAheadDay[];
  /** Content rendered between the first (Today) section and the rest, so the
   *  two-week-wait home can surface the chat + readings above Testing / Good
   *  to know. */
  afterTodaySlot?: ReactNode;
  /** Content rendered after all concept sections, near the foot of the page —
   *  used by outcome + stim homes to surface a curated reading below the rest
   *  of the surface, close to Documents. */
  endOfPageSlot?: ReactNode;
  onOpenPregnancyTrackingSetup?: () => void;
  /**
   * The cycle the ending on screen belongs to, when Home is reporting on a closed
   * one.
   *
   * Separate from `outcomeCycleId`, which is only set on the post-result routes
   * because it decides which record the hero *reads*. This one only names a
   * destination, so it is also set for the cancelled surface — whose variant is
   * `cycle-status-review`, not a post-procedure route, and which therefore has no
   * cycle id of its own to link to.
   */
  endedCycleId?: string | null;
  /**
   * The closed cycle a post-cycle Home is about, read without the arrival
   * window.
   *
   * `endedCycleId` expires after thirty days — it is the *arrival* reading, and
   * it is what tells this hero to be celebratory. This one does not expire,
   * because the post-cycle Home does not: her cycle stays reviewable, and a new
   * one stays startable, for as long as this Home is her Home. Keeping the two
   * apart is what lets acknowledging change the hero's tone without taking its
   * buttons away.
   */
  postCycleCycleId?: string | null;
  /** That cycle's end date, for the hero's eyebrow. */
  postCycleEndDate?: string | null;
  /**
   * A cycle that has just ended and that no other hero copy here is already
   * about.
   *
   * The screen resolves this and passes null wherever this hero states the
   * ending in its own words, so the two can never both speak. It exists because
   * the card that used to announce endings sat above *every* Home surface, and
   * these variants — an active cycle, a wait, a donor's home — have no ending
   * copy of their own to fall back on.
   */
  cycleEnding?: HeroCycleEnding | null;
}) {
  const [confettiTrigger, setConfettiTrigger] = useState(0);
  const t = useTranslations();
  const captureConceptPress = usePressCapture("HomepageReviewConceptHome");
  const { locale } = useLocale();
  const cycleHeaderState = useCycleHeaderState();
  const { cycle: activeCycleForHero, phases: heroPhases = [] } =
    useActiveCycleWithPhases({ cycleId: outcomeCycleId ?? null });
  const allFrozenSpecimens = useFrozenSpecimens();
  const cycleFrozenSpecimens = useFrozenSpecimensForCycle(
    projectionSourceCycleId,
  );
  const isOutcomeVariant = isPostProcedureOutcomeVariant(variant);
  // Server-held. A week count that changes with the device is not a week count.
  const pregnancyFollowUp = useCyclePregnancyFollowUp(
    isOutcomeVariant && variant.endsWith("-positive")
      ? activeCycleForHero?.id
      : null,
  );
  /**
   * What she has said about the pregnancy, which is a different record from the
   * result and from the follow-up (#3492). Every "has it concluded" question on
   * this page is answered from here; none of them may be answered from the
   * outcome, which no longer says, or from the follow-up, which never did.
   */
  const currentPregnancyStatus =
    pregnancyStatusReading === "unknown" ? null : pregnancyStatusReading;
  const pregnancyStatusUnknown = pregnancyStatusReading === "unknown";
  const resolvedPregnancyStands = resolvePregnancyStands(
    activeCycleForHero?.outcome,
    pregnancyStatusUnknown ? "unknown" : currentPregnancyStatus?.status,
  );
  const outcomeStageState = usePostProcedureOutcomeStage({
    route: isOutcomeVariant ? variant : "ivf-negative",
    cycleId: activeCycleForHero?.id,
    // Acknowledgement belongs to the outcome event, not the cycle shell. A
    // corrected or additional result must not inherit the old cycle state.
    outcomeId: activeCycleForHero?.outcome?.id,
    acknowledgement:
      activeCycleForHero?.outcome?.id &&
      activeCycleForHero.outcome.acknowledgedAt
        ? {
            outcomeId: activeCycleForHero.outcome.id,
            acknowledgedAt: activeCycleForHero.outcome.acknowledgedAt,
          }
        : null,
  });
  /**
   * The same audience and still-ongoing test the guidance cards use, asked in
   * the component so the dated section answers to it too. One rule, two
   * surfaces: a partner must not be shown either, and neither must someone whose
   * pregnancy has already ended.
   */
  const showsEarlyPregnancyGuidance = shouldShowEarlyPregnancyGuidance({
    outcome: {
      type: activeCycleForHero?.outcome?.type ?? null,
      resultStatus: activeCycleForHero?.outcome?.resultStatus ?? null,
      verificationStatus:
        activeCycleForHero?.outcome?.verificationStatus ?? null,
    },
    patientPregnancyStands: resolvedPregnancyStands,
    patientStatusPending: pregnancyStatusUnknown,
    viewerIsSupporting,
  });

  /**
   * What she has said about the pregnancy, if anything.
   *
   * Her own record and nothing else. It used to be reconstructed from the
   * outcome's week count, which was the app's proxy for "she has spoken" —
   * `type: "pregnancy"` is written by the beta recording, not by her. Gestational
   * age is gated on the same evidence.
   */
  const announcedPregnancyStands = pregnancyStatusUnknown
    ? "unknown"
    : resolvedPregnancyStands;

  /**
   * The dates the "what comes next" steps carry, when there are any.
   *
   * Her reported weeks are the dating; the follow-up's own gestation refines it
   * where it exists, because it knows the day within the week. Undefined is the
   * ordinary case, and the card then reads exactly as it did before any of this.
   */
  const earlyPregnancyDating = useMemo(() => {
    const serverGestation = pregnancyFollowUp.data?.gestation ?? null;
    const reported = gestationFromReportedWeeks({
      weekCount: currentPregnancyStatus?.gestationalWeek ?? null,
      // The day she gave the week, which her own record now carries as a
      // calendar date. It used to be sliced from the outcome's `updatedAt`, an
      // instant — west of UTC in the evening that reads as tomorrow, which trips
      // the "reported after today" guard and removes every date for the rest of
      // her day, and east of UTC it adds one to her age. `recordedAt` is the
      // fallback for a record with no reported date, converted locally for the
      // same reason.
      reportedOn:
        currentPregnancyStatus?.reportedDate ??
        (currentPregnancyStatus?.recordedAt
          ? toLocalDateString(new Date(currentPregnancyStatus.recordedAt))
          : null),
      today: toLocalDateString(new Date()),
    });
    return buildEarlyPregnancyDating({
      t,
      locale,
      announcement: {
        followUpStatus: pregnancyFollowUp.data?.status ?? null,
        pregnancyStands: announcedPregnancyStands,
      },
      gestation: serverGestation
        ? {
            asOf: serverGestation.asOf,
            gestationalDays: serverGestation.gestationalDays,
          }
        : reported,
      preciseAge: serverGestation
        ? {
            weeks: serverGestation.gestationalWeeks,
            dayOfWeek: serverGestation.gestationalDayOfWeek,
          }
        : null,
      resultDate: activeCycleForHero?.outcome?.dateRecorded ?? null,
      today: toLocalDateString(new Date()),
    });
  }, [
    activeCycleForHero?.outcome?.dateRecorded,
    announcedPregnancyStands,
    currentPregnancyStatus?.gestationalWeek,
    currentPregnancyStatus?.recordedAt,
    currentPregnancyStatus?.reportedDate,
    locale,
    pregnancyFollowUp.data?.gestation,
    pregnancyFollowUp.data?.status,
    t,
  ]);

  /**
   * "Good to know" belongs to the pregnancy, not to a stage of the page.
   *
   * It used to live inside the acknowledged-stage view, so it existed only once
   * she had pressed "Regardons la suite" — and pressing it replaced the surface
   * that carried the button. The content is the same either way: what the first
   * weeks usually hold, how a beta is read, when not to wait. She should be able
   * to reach it by scrolling, and the hero action should take her to it rather
   * than conjure it.
   */
  const earlyPregnancySection: ConceptSection | null = useMemo(() => {
    // Both halves matter. The audience test reads the outcome record, which an
    // ordinary Home can also carry — without the variant check, any page whose
    // cycle holds an ongoing pregnancy grew this section, labelled with IVF copy
    // by the `?? "ivf"` fallback below.
    if (!isOutcomeVariant || !showsEarlyPregnancyGuidance) return null;
    const shared = "homepage.review_concept.post_procedure_outcome.shared";
    return section(
      t(`${shared}.good_to_know`),
      t(`${shared}.what_happens_next_and_who_to_ask`),
      [
        whatToKnow(
          t(`${shared}.the_first_few_weeks`),
          "panel",
          buildEarlyPregnancyWhatToKnowCards(
            t,
            getPostProcedureOutcomeContentKey(variant) ?? "ivf",
            earlyPregnancyDating,
          ),
        ),
      ],
    );
  }, [
    earlyPregnancyDating,
    isOutcomeVariant,
    showsEarlyPregnancyGuidance,
    t,
    variant,
  ]);

  /** Where this block starts in the page, and where the section starts in it. */
  const [homeTopY, setHomeTopY] = useState(0);
  const [earlyPregnancyY, setEarlyPregnancyY] = useState<number | null>(null);

  /**
   * Take her to the guidance rather than summoning it.
   *
   * The hero action used to acknowledge and, in doing so, replace the surface it
   * was on — the button vanished and the content it promised appeared somewhere
   * she had not been looking. It still acknowledges, because that is a real
   * thing the record carries, but what she sees is the page moving to the
   * section she asked for.
   */
  const scrollToEarlyPregnancy = useCallback(() => {
    if (earlyPregnancyY === null) return;
    onScrollToY?.(Math.max(0, homeTopY + earlyPregnancyY - 12));
  }, [earlyPregnancyY, homeTopY, onScrollToY]);

  /**
   * A scroll that waits for the page to stop moving.
   *
   * Acknowledging adds the stage's own sections above the guidance, so a scroll
   * fired in the same handler lands correctly and is then pushed down by content
   * that arrived after it. Asking again once the section reports a new offset is
   * what makes the page settle where she was sent.
   */
  const pendingScrollRef = useRef<number | null>(null);
  useEffect(() => {
    const measuredWhenAsked = pendingScrollRef.current;
    if (measuredWhenAsked === null || earlyPregnancyY === null) return;
    // Only once the section has actually moved. Waiting on a re-render would
    // fire before the new sections have laid out — the very thing this defers
    // past — so the offset changing is the signal, not the render.
    if (earlyPregnancyY === measuredWhenAsked) return;
    pendingScrollRef.current = null;
    scrollToEarlyPregnancy();
  }, [earlyPregnancyY, scrollToEarlyPregnancy]);

  const outcomeMetadata = useMemo<PostProcedureOutcomeMetadata>(
    () => ({
      source: activeCycleForHero?.outcome?.source,
      verificationStatus: activeCycleForHero?.outcome?.verificationStatus,
      recordedType: activeCycleForHero?.outcome?.type ?? null,
      resultStatus: activeCycleForHero?.outcome?.resultStatus ?? null,
      patientPregnancyStands: resolvedPregnancyStands,
      patientStatusPending: pregnancyStatusUnknown,
    }),
    [
      activeCycleForHero?.outcome?.source,
      activeCycleForHero?.outcome?.verificationStatus,
      activeCycleForHero?.outcome?.type,
      activeCycleForHero?.outcome?.resultStatus,
      resolvedPregnancyStands,
      pregnancyStatusUnknown,
    ],
  );
  /**
   * A birth recorded against this cycle, when there is one.
   *
   * Read from the record rather than from the route, because the route cannot
   * tell: a birth keeps `type: "pregnancy"`, so it arrives on the same
   * `*-outcome-positive` Home as a beta drawn this morning. See
   * `resolveRecordedBirth`.
   */
  const recordedBirth = useMemo(
    () =>
      resolveRecordedBirth(activeCycleForHero?.outcome, pregnancyStatusReading),
    [activeCycleForHero?.outcome, pregnancyStatusReading],
  );

  /**
   * What the hero says once a baby has been born: the birth, and nothing about a
   * result whose next step has already happened.
   */
  const recordedBirthHeroCopy = useMemo(() => {
    if (!recordedBirth) return null;
    const keys = recordedBirthCopyKeys({
      birth: recordedBirth,
      viewerIsSupporting,
    });
    return { title: t(keys.titleKey), summary: t(keys.bodyKey) };
  }, [recordedBirth, t, viewerIsSupporting]);

  /**
   * And what it says while the pregnancy is still going.
   *
   * `showsEarlyPregnancyGuidance` is the same question the guidance section asks —
   * is there a standing pregnancy, and is this the person who carried the cycle —
   * so the heading and the content it heads can never disagree. A birth takes its
   * voice ahead of this one, and cannot collide with it: a birth sets
   * `pregnancyOutcome`, which is what makes the guidance test false.
   */
  const standingPregnancyHeroCopy = useMemo(() => {
    if (!showsEarlyPregnancyGuidance) return null;
    const keys = standingPregnancyCopyKeys({ viewerIsSupporting });
    return { title: t(keys.titleKey), summary: t(keys.bodyKey) };
  }, [showsEarlyPregnancyGuidance, t, viewerIsSupporting]);

  /**
   * And what it says when she closed the cycle without naming an ending.
   *
   * Read from the record for the same reason the two above are: the route is
   * resolved from an outcome state that maps `other` onto "uncertain", which is
   * a different claim — "we can't call this result" rather than "there was no
   * result to call" — and it is the one the modality copy is written for.
   */
  const closedWithoutResultHeroCopy = useMemo(() => {
    if (
      !wasClosedWithoutARecordedEnding({
        cycleStatus: activeCycleForHero?.status,
        outcomeType: activeCycleForHero?.outcome?.type,
      })
    ) {
      return null;
    }
    const keys = closedWithoutResultCopyKeys({ viewerIsSupporting });
    return { title: t(keys.titleKey), summary: t(keys.bodyKey) };
  }, [
    activeCycleForHero?.status,
    activeCycleForHero?.outcome?.type,
    t,
    viewerIsSupporting,
  ]);

  /**
   * The burst that says the app noticed.
   *
   * Fired once per visit, not once per record: the ref is what keeps a re-render
   * from setting it off again while she reads the page. It repeats on her next
   * visit, which is the same thing the recorded-result card in the treatment hub
   * does — and it only lasts as long as the cycle stays open, because closing it
   * is what this hero is asking for.
   */
  const celebratedBirthRef = useRef(false);
  useEffect(() => {
    if (!recordedBirth || celebratedBirthRef.current) return;
    celebratedBirthRef.current = true;
    setConfettiTrigger((current) => current + 1);
  }, [recordedBirth]);

  const dynamicHeroCopy = useMemo(
    () =>
      presentCycleHeaderForHero({
        state: cycleHeaderState,
        t,
        locale,
        viewerIsSupporting,
      }),
    [cycleHeaderState, t, locale, viewerIsSupporting],
  );

  // ─── Hero metrics widget ────────────────────────────────────────────────
  const treatmentDashboard = useTreatmentDashboard();
  const postRetrievalHeroCopy = useMemo(
    () => buildPostRetrievalHeroCopy(t, treatmentDashboard.rawCycleType),
    [t, treatmentDashboard.rawCycleType],
  );
  const heroToday = toLocalDateString(new Date());
  const { data: heroMedications = [] } = usePatientMedications({
    enabled: !!activeCycleForHero,
    window: { from: heroToday, to: heroToday },
  });
  const dynamicHeroMetrics = useMemo(() => {
    const todayIso = toLocalDateString(new Date());
    let todayMedDoseCount = 0;
    for (const med of heroMedications) {
      if (!med.startDate || !med.timeTaken || med.timeTaken.length === 0) {
        continue;
      }
      todayMedDoseCount += med.timeTaken.length;
    }
    let daysPostTransfer: number | null = null;
    // luteal_phase.startDate is set to the transfer date when the user
    // enters the wait — that's the anchor for "days since transfer".
    const lutealPhase = heroPhases.find((p) => p.type === "luteal_phase");
    if (lutealPhase?.startDate) {
      const start = new Date(`${lutealPhase.startDate}T00:00:00`);
      const today = new Date(`${todayIso}T00:00:00`);
      const diff = Math.floor((today.getTime() - start.getTime()) / 86_400_000);
      if (diff >= 0) daysPostTransfer = diff;
    }
    return buildHeroMetrics({
      state: cycleHeaderState,
      t,
      locale,
      context: {
        leadFollicleMm:
          treatmentDashboard.monitoringSnapshot?.leadFollicleMm ?? null,
        estradiol: treatmentDashboard.monitoringSnapshot?.estradiol ?? null,
        endometriumMm:
          treatmentDashboard.monitoringSnapshot?.endometriumMm ?? null,
        estimatedRetrievalDate:
          treatmentDashboard.estimatedRetrieval?.date ?? null,
        daysPostTransfer,
        todayMedDoseCount,
      },
    });
  }, [
    cycleHeaderState,
    treatmentDashboard.monitoringSnapshot,
    treatmentDashboard.estimatedRetrieval,
    heroMedications,
    heroPhases,
    t,
    locale,
  ]);
  // ─── Two-week-wait hero treatment ───────────────────────────────────────
  // During the wait the hero's job is to carry "where am I": the day of the
  // wait (as a felt progress bar + title), the beta anchor date, and one
  // reassurance line that adapts to how far along the wait is. This overrides
  // the generic beta copy for the beta-wait / iui-wait homepage surfaces.
  const isBetaWait =
    cycleHeaderState.kind === "beta_scheduled" ||
    cycleHeaderState.kind === "beta_pending";
  const isSpermResultsWait = variant === "sperm-freezing-wait";
  /**
   * The step an IVF Home is on after stimulation. `ivf-active` stays the same
   * variant from stimulation to transfer day, and `post-retrieval` from
   * retrieval to the transfer, so the step decides what each shows.
   */
  const ivfStep =
    variant === "ivf-active" || variant === "post-retrieval"
      ? ivfHomeStep(cycleHeaderState.kind)
      : null;
  const isIvfTransferStep = variant === "ivf-active" && ivfStep === "transfer";
  /** Embryo development has embryos to describe; egg freezing does not. */
  const isEmbryoStep =
    variant === "post-retrieval" &&
    ivfStep === "embryos" &&
    activeCycleForHero?.cycleType !== "egg_freezing";
  /**
   * How the step she is on works, the treatment hub's explainer for the same
   * step, shown in full wherever a variant leaves room for it.
   */
  const currentStepExplainer = useCurrentStepExplainer(
    viewerIsSupporting ? "partner" : "self",
  );
  const waitCopySuffix =
    cycleHeaderState.kind === "beta_scheduled" ||
    cycleHeaderState.kind === "beta_pending"
      ? getPostProcedureWaitContext(cycleHeaderState.cycleType).heroCopySuffix
      : null;
  const betaDateForHero =
    cycleHeaderState.kind === "beta_scheduled"
      ? cycleHeaderState.betaDate
      : null;
  const betaDateLabel = betaDateForHero
    ? formatRelativePhaseDate(betaDateForHero, locale, t)
    : null;
  const semenResults = useSemenAnalysisResults();
  /**
   * The results wait shows only what this cycle recorded. The post-cycle
   * reading also falls back to an older lab report or a stored vial, which
   * here would announce last year's analysis while this sample is at the lab.
   */
  const cycleSemenResults = useSpermiogramAnalysisResults();
  const isIntendedParentHome = variant === "intended-parent";
  const intendedParentPatientQuery = usePatient();
  const { data: intendedParentPatient, isSuccess: patientLoaded } =
    intendedParentPatientQuery;
  const intendedParentDocumentsQuery = useDocuments({
    enabled: isIntendedParentHome,
  });
  const { data: intendedParentDocuments, isSuccess: documentsLoaded } =
    intendedParentDocumentsQuery;
  // A vault that failed to load reads as no embryos; it isn't.
  const vaultLoadFailed =
    Boolean(allFrozenSpecimens.error) &&
    allFrozenSpecimens.specimens.length === 0;
  const intendedParentFacts = useMemo((): IntendedParentFacts | null => {
    if (
      !isIntendedParentHome ||
      !patientLoaded ||
      !documentsLoaded ||
      allFrozenSpecimens.isLoading ||
      vaultLoadFailed
    ) {
      return null;
    }
    return {
      embryos: allFrozenSpecimens.totalEmbryos,
      legalDocuments: (intendedParentDocuments ?? []).filter(isConsentDocument)
        .length,
      carrierConnected: (intendedParentPatient?.counterparties ?? []).some(
        (counterparty) => counterparty.role === "gestational-carrier",
      ),
      participantStatus: intendedParentPatient?.participantStatus ?? null,
    };
  }, [
    allFrozenSpecimens.isLoading,
    allFrozenSpecimens.totalEmbryos,
    documentsLoaded,
    intendedParentDocuments,
    intendedParentPatient?.counterparties,
    intendedParentPatient?.participantStatus,
    isIntendedParentHome,
    patientLoaded,
    vaultLoadFailed,
  ]);
  const intendedParentRecordsLoad = {
    failed:
      (intendedParentPatientQuery.isError && !patientLoaded) ||
      (intendedParentDocumentsQuery.isError && !documentsLoaded) ||
      vaultLoadFailed,
    onRetry: () => {
      if (intendedParentPatientQuery.isError) {
        void intendedParentPatientQuery.refetch();
      }
      if (intendedParentDocumentsQuery.isError) {
        void intendedParentDocumentsQuery.refetch();
      }
      if (allFrozenSpecimens.error) void allFrozenSpecimens.refetch();
    },
    isRetrying:
      intendedParentPatientQuery.isFetching ||
      intendedParentDocumentsQuery.isFetching ||
      allFrozenSpecimens.isFetching,
  };
  const concepts = useConcepts(
    semenResults,
    cycleStatusReviewKind,
    betaDateLabel,
    viewerIsSupporting,
  );
  const baseConcept = isIntendedParentHome
    ? buildIntendedParentConcept(
        t,
        intendedParentFacts,
        intendedParentRecordsLoad,
      )
    : concepts[variant];
  const betaTimeForHero =
    cycleHeaderState.kind === "beta_scheduled"
      ? cycleHeaderState.betaTime
      : null;
  const waitProgress = useMemo(() => {
    if (!isBetaWait) return null;
    const luteal = heroPhases.find((p) => p.type === "luteal_phase");
    const start = luteal?.startDate ?? null;
    if (!start) return null;
    const startMs = new Date(`${start}T00:00:00`).getTime();
    const todayMs = new Date(
      `${toLocalDateString(new Date())}T00:00:00`,
    ).getTime();
    const dayOfWait = Math.max(0, Math.round((todayMs - startMs) / 86_400_000));
    let totalWaitDays: number | null = null;
    if (betaDateForHero) {
      const betaMs = new Date(`${betaDateForHero}T00:00:00`).getTime();
      const total = Math.round((betaMs - startMs) / 86_400_000);
      if (total > 0) totalWaitDays = total;
    }
    return { dayOfWait, totalWaitDays };
  }, [isBetaWait, heroPhases, betaDateForHero]);
  const HERO_DYNAMIC = "homepage.review_concept.hero_dynamic";
  const isPostProcedureOutcomeHome = isOutcomeVariant;
  const isUnresolvedOutcome =
    variant.endsWith("-unavailable") ||
    variant.endsWith("-uncertain") ||
    variant.endsWith("-adverse");
  const outcomeStage =
    isPostProcedureOutcomeHome && !isUnresolvedOutcome
      ? outcomeStageState.stage
      : "recorded";
  const outcomeStageView = useMemo(() => {
    if (!isPostProcedureOutcomeHome) return null;
    // A birth has its own hero, and the acknowledged stage view would talk over
    // it: its title is the positive result the birth grew out of, and its "When
    // you're ready" section offers to turn on pregnancy follow-up — a week count
    // and a due date for a pregnancy that has already ended in a baby. Dropping
    // it leaves the plain result concept underneath, which is a Today section and
    // nothing else.
    if (recordedBirth) return null;
    return buildPostProcedureOutcomeStageView(
      t,
      outcomeStage,
      getPostProcedureOutcomeContentKey(variant),
      outcomeMetadata,
      pregnancyTrackingSetupStatusLabel(
        t,
        pregnancyFollowUp.data?.status ?? null,
      ),
      variant,
      viewerIsSupporting,
    );
  }, [
    isPostProcedureOutcomeHome,
    outcomeStage,
    outcomeMetadata,
    pregnancyFollowUp.data?.status,
    recordedBirth,
    t,
    variant,
    viewerIsSupporting,
  ]);
  const waitHeroCopy = useMemo(() => {
    if (
      isPostProcedureOutcomeHome ||
      isSpermResultsWait ||
      !isBetaWait ||
      !waitProgress
    ) {
      return null;
    }
    const { dayOfWait, totalWaitDays } = waitProgress;
    const reassureKey =
      dayOfWait <= 3
        ? "wait_reassure_early"
        : totalWaitDays && dayOfWait >= totalWaitDays - 2
          ? "wait_reassure_late"
          : "wait_reassure_mid";
    const copyKey = (base: string) =>
      waitCopySuffix
        ? `${HERO_DYNAMIC}.${base}_${waitCopySuffix}`
        : `${HERO_DYNAMIC}.${base}`;
    // The early and late lines tell the patient to keep her medication going,
    // so a partner gets the same reassurance in words that ask nothing of a
    // body that is not theirs. The middle line already reads for both.
    const supportingReassuranceKey = !viewerIsSupporting
      ? null
      : reassureKey === "wait_reassure_early"
        ? `${HERO_DYNAMIC}.supporting.nothing_to_detect_yet`
        : reassureKey === "wait_reassure_late"
          ? `${HERO_DYNAMIC}.supporting.the_blood_test_is_the_honest_answer`
          : null;
    const reassuranceKey =
      supportingReassuranceKey ??
      (waitCopySuffix
        ? `${HERO_DYNAMIC}.${reassureKey}_${waitCopySuffix}`
        : `${HERO_DYNAMIC}.${reassureKey}`);
    return {
      eyebrow: { label: t(copyKey("wait_eyebrow")) },
      title: totalWaitDays
        ? t(
            viewerIsSupporting
              ? `${HERO_DYNAMIC}.supporting.day_n_of_the_wait`
              : `${HERO_DYNAMIC}.wait_day_title`,
            { params: { day: String(dayOfWait) } },
          )
        : t(copyKey("wait_day_title_no_total"), {
            params: { day: String(dayOfWait) },
          }),
      summary: t(reassuranceKey),
    };
  }, [
    isBetaWait,
    isPostProcedureOutcomeHome,
    isSpermResultsWait,
    viewerIsSupporting,
    waitCopySuffix,
    waitProgress,
    t,
  ]);

  const heroStatusCopy = useMemo(
    () => buildHeroStatusCopy(t, viewerIsSupporting),
    [t, viewerIsSupporting],
  );
  /**
   * Eggs and sperm are one person's material, so a partner reads about that
   * cycle rather than being told they went through it. Embryos belong to both
   * of them, and that journey's copy already reads that way.
   */
  const readsAboutPartnersOwnMaterial =
    viewerIsSupporting &&
    (variant === "egg-freezing-postcycle" ||
      variant === "sperm-freezing-postcycle");
  /**
   * Whether this post-cycle hero is still the arrival moment.
   *
   * The window is the whole rule: `endedCycleId` is set only while the ending is
   * recent, and when it lapses the same hero states the fact instead of
   * celebrating it.
   *
   * Deliberately not acknowledgeable. This hero offers no "Got it" because
   * pressing one would change nothing she can see — the hero stays, both its
   * buttons stay, and only the wording settles. The thirty days are a breath,
   * and a breath does not need dismissing.
   */
  const postCycleShowsArrival =
    isPostCycleVariant(variant) &&
    !!endedCycleId &&
    // The tone and the buttons have to be about one cycle. `endedCycleId` is the
    // newest ending of *any* type; `postCycleCycleId` is the newest closed cycle
    // of this journey's type. Where they differ the celebration described one
    // cycle while "Review this cycle" opened another — and where the second is
    // missing entirely (this variant is also the fall-through for
    // `postcycle_storage`) the hero rendered the celebration with no buttons at
    // all, which is worse than the card it replaced.
    endedCycleId === postCycleCycleId;

  const postCycleHeroCopy = useMemo(() => {
    if (!isPostCycleVariant(variant)) return null;
    // The arrival tone is journey-agnostic on purpose: reaching the end of a
    // preservation cycle is the same event whichever material it stored, and
    // the eyebrow beside it already names which one.
    if (postCycleShowsArrival) {
      const CC = "homepage.cycle_closing.preservation.complete";
      return readsAboutPartnersOwnMaterial
        ? {
            title: t(
              "homepage.review_concept.supporting.your_partner_went_all_the_way_through",
            ),
            summary: t(
              "homepage.review_concept.supporting.what_was_stored_is_there_when_your_partner_is_ready",
            ),
          }
        : {
            title: t(`${CC}.preservation_reached_completion`),
            summary: t(`${CC}.stored_materials_are_ready`),
          };
    }
    const counts = projectionSourceCycleId
      ? {
          eggs: cycleFrozenSpecimens.cycleTotalEggs,
          embryos: cycleFrozenSpecimens.cycleTotalEmbryos,
          sperm: cycleFrozenSpecimens.cycleTotalSperm,
        }
      : {
          eggs: allFrozenSpecimens.totalEggs,
          embryos: allFrozenSpecimens.totalEmbryos,
          sperm: allFrozenSpecimens.totalSperm,
        };
    return buildPostCycleHeroCopy(
      t,
      variant,
      counts,
      readsAboutPartnersOwnMaterial,
    );
  }, [
    allFrozenSpecimens.totalEggs,
    allFrozenSpecimens.totalEmbryos,
    allFrozenSpecimens.totalSperm,
    cycleFrozenSpecimens.cycleTotalEggs,
    cycleFrozenSpecimens.cycleTotalEmbryos,
    cycleFrozenSpecimens.cycleTotalSperm,
    postCycleShowsArrival,
    projectionSourceCycleId,
    readsAboutPartnersOwnMaterial,
    t,
    variant,
  ]);
  const hasOpenCycle = activeCycleForHero?.status === "open";
  const isSpermCollectionDay = variant === "sperm-freezing-active";
  const { data: collectionDayDocuments } = useDocuments({
    enabled: isSpermCollectionDay,
  });
  const collectionFacts = useMemo(
    () =>
      isSpermCollectionDay
        ? spermCollectionFacts({
            cycle: activeCycleForHero,
            phases: heroPhases,
            todayIso: toLocalDateString(new Date()),
          })
        : null,
    [activeCycleForHero, heroPhases, isSpermCollectionDay],
  );
  const spermCollectionContext = useMemo(
    () =>
      collectionFacts
        ? {
            facts: collectionFacts,
            formatDate: (iso: string) =>
              formatRelativePhaseDate(iso, locale, t),
            hasConsentDocument: (collectionDayDocuments ?? []).some(
              isConsentDocument,
            ),
          }
        : null,
    [collectionDayDocuments, collectionFacts, locale, t],
  );
  const isFetActive = variant === "fet-active";
  const fetFacts = useMemo(
    () =>
      isFetActive
        ? fetTransferFacts({
            cycle: activeCycleForHero,
            phases: heroPhases,
            todayIso: toLocalDateString(new Date()),
          })
        : null,
    [activeCycleForHero, heroPhases, isFetActive],
  );
  const detailedSections = useMemo(
    () =>
      buildDetailedConceptSections(
        t,
        hasOpenCycle,
        spermCollectionContext,
        fetFacts,
        cycleSemenResults,
      ),
    [t, hasOpenCycle, spermCollectionContext, fetFacts, cycleSemenResults],
  );
  /**
   * The frozen-transfer hero, from the steps. The cycle-header hero reads any
   * `protocol` step as ovarian stimulation, so lining preparation was shown as
   * "your follicles are growing" with follicle and retrieval tags. The booked
   * transfer leads, then the day of lining preparation and the clinic as tags.
   */
  const fetHeroCopy = useMemo(() => {
    if (!fetFacts) return null;
    const ns = "homepage.fet_lining_prep";
    const todayIso = toLocalDateString(new Date());
    const { transfer } = fetFacts;
    if (transfer.status === "booked" && transfer.on === todayIso) {
      return {
        title: t(`${ns}.transfer_is_today`),
        summary: t(`${ns}.take_medications_and_follow_clinic_today`),
      };
    }
    if (transfer.status === "booked") {
      return {
        title: t(`${ns}.preparing_for_your_transfer`),
        summary: t(`${ns}.keep_taking_medications_lining_checked_first`),
      };
    }
    if (transfer.status === "overdue") {
      return {
        title: t(`${ns}.log_your_transfer`),
        summary: t(`${ns}.tell_us_whether_it_happened`),
      };
    }
    return {
      title: t(`${ns}.preparing_for_your_transfer`),
      summary: t(`${ns}.add_the_date_when_clinic_gives_it`),
    };
  }, [fetFacts, t]);
  const fetHeroMetrics = useMemo<Metric[]>(() => {
    if (!fetFacts) return [];
    const ns = "homepage.fet_lining_prep";
    const todayIso = toLocalDateString(new Date());
    const metrics: Metric[] = [];
    const { transfer, liningPrepDay, clinic } = fetFacts;
    if (transfer.status === "booked" && transfer.on !== todayIso) {
      metrics.push({
        label: t(`${ns}.transfer`),
        value: formatRelativePhaseDate(transfer.on, locale, t),
      });
    }
    if (liningPrepDay != null) {
      metrics.push({
        label: t(`${ns}.lining_prep`),
        value: t(`${ns}.lining_prep_day`, {
          params: { day: String(liningPrepDay) },
        }),
      });
    }
    if (clinic) metrics.push({ label: t(`${ns}.clinic`), value: clinic });
    return metrics;
  }, [fetFacts, locale, t]);
  /**
   * The collection-day hero, from the steps: the booked day leads, then
   * screening and the clinic as tags. A booked date already behind with the
   * step not done asks to log it, because until then Home cannot follow the
   * sample to the lab.
   */
  const spermCollectionHeroCopy = useMemo(() => {
    if (!collectionFacts) return null;
    const ns = "homepage.sperm_collection_day";
    const todayIso = toLocalDateString(new Date());
    const { collection } = collectionFacts;
    if (collection.status === "booked" && collection.on === todayIso) {
      return {
        title: t(`${ns}.collection_is_today`),
        summary: t(`${ns}.bring_id_and_collect_the_whole_sample`),
      };
    }
    if (collection.status === "booked") {
      return {
        title: t(`${ns}.getting_ready_for_your_collection`),
        summary: t(`${ns}.leave_two_to_seven_days_before_it`),
      };
    }
    if (collection.status === "overdue") {
      return {
        title: t(`${ns}.log_your_collection`),
        summary: t(`${ns}.mark_it_done_so_home_can_follow_the_sample`),
      };
    }
    return {
      title: t(`${ns}.getting_ready_for_your_collection`),
      summary: t(`${ns}.add_the_date_then_prepare`),
    };
  }, [collectionFacts, t]);
  const spermCollectionHeroMetrics = useMemo<Metric[]>(() => {
    if (!collectionFacts) return [];
    const ns = "homepage.sperm_collection_day";
    const todayIso = toLocalDateString(new Date());
    const metrics: Metric[] = [];
    const { collection, screening, clinic } = collectionFacts;
    if (collection.status === "booked" && collection.on !== todayIso) {
      metrics.push({
        label: t(`${ns}.collection`),
        value: formatRelativePhaseDate(collection.on, locale, t),
      });
    }
    if (screening.status === "done") {
      metrics.push({
        label: t(`${ns}.screening`),
        value: t(`${ns}.screening_done`),
      });
    } else if (screening.status === "booked") {
      metrics.push({
        label: t(`${ns}.screening`),
        value: formatRelativePhaseDate(screening.on, locale, t),
      });
    }
    if (clinic) metrics.push({ label: t(`${ns}.clinic`), value: clinic });
    return metrics;
  }, [collectionFacts, locale, t]);
  /**
   * What the sperm-results wait can say for certain: the day the sample was
   * given, the day the analysis is booked, and the clinic. Each tag is shown
   * only when the cycle records it.
   */
  const spermWaitHeroMetrics = useMemo<Metric[]>(() => {
    if (!isSpermResultsWait) return [];
    const facts = semenAnalysisWaitFacts({
      cycle: activeCycleForHero,
      phases: heroPhases,
      todayIso: toLocalDateString(new Date()),
    });
    const metrics: Metric[] = [];
    if (facts.collectedOn) {
      metrics.push({
        label: t("homepage.sperm_results_wait.sample_given"),
        value: formatRelativePhaseDate(facts.collectedOn, locale, t),
      });
    }
    if (facts.analysisOn) {
      metrics.push({
        label: t("homepage.sperm_results_wait.analysis_booked"),
        value: formatRelativePhaseDate(facts.analysisOn, locale, t),
      });
    }
    if (facts.clinic) {
      metrics.push({
        label: t("homepage.sperm_results_wait.clinic"),
        value: facts.clinic,
      });
    }
    return metrics;
  }, [activeCycleForHero, heroPhases, isSpermResultsWait, locale, t]);
  const concept = outcomeStageView
    ? {
        ...baseConcept,
        title: outcomeStageView.title,
        summary: outcomeStageView.summary,
        sections: outcomeStageView.sections,
      }
    : baseConcept;
  // All three read the record, so all three come before the chain that reads the
  // route — which cannot tell a pregnancy in its tenth week from a beta drawn
  // this morning, and gave them the same heading, nor a cycle closed with no
  // ending from a result it could not call.
  const heroCopy = recordedBirthHeroCopy
    ? recordedBirthHeroCopy
    : standingPregnancyHeroCopy
      ? standingPregnancyHeroCopy
      : closedWithoutResultHeroCopy
        ? closedWithoutResultHeroCopy
        : outcomeStageView
          ? outcomeStageView
          : cycleEnding
            ? { title: cycleEnding.title, summary: cycleEnding.body }
            : isPostCycleVariant(variant)
              ? postCycleHeroCopy
              : isPostProcedureOutcomeHome
                ? heroStatusCopy[variant]
                : isSpermResultsWait
                  ? heroStatusCopy[variant]
                  : isSpermCollectionDay && spermCollectionHeroCopy
                    ? spermCollectionHeroCopy
                    : isFetActive && fetHeroCopy
                      ? fetHeroCopy
                      : variant === "post-retrieval"
                        ? isEmbryoStep && dynamicHeroCopy
                          ? dynamicHeroCopy
                          : postRetrievalHeroCopy
                        : variant === "cycle-status-review"
                          ? concept
                            ? { title: concept.title, summary: concept.summary }
                            : heroStatusCopy[variant]
                          : (dynamicHeroCopy ?? heroStatusCopy[variant]);
  // Pre-cycle variants (setup, learn-explorer, *-precycle) render through the
  // stepper home (GuidedPreCycleConceptHome), so they have no entry here. This
  // component is only ever rendered with review-dashboard variants; the guard
  // keeps the lookups type-safe now that the maps are Partial.
  if (!concept || !heroCopy) return null;
  /**
   * The page under the hero, when the hero says the cycle closed with no ending
   * recorded.
   *
   * `heroCopy` only replaces the title and summary, so the sections kept the ones
   * written for an unclear result: "Clarify what was recorded" sat directly below
   * "This cycle is closed", heading the day's medication list. The hero and the
   * page were answering different questions about the same cycle.
   */
  const conceptForHero =
    closedWithoutResultHeroCopy && concept
      ? {
          ...concept,
          sections: concept.sections.map((conceptSection, index) =>
            index === 0
              ? {
                  ...conceptSection,
                  title: t(
                    "homepage.closed_without_a_result.what_stays_in_your_records",
                  ),
                  subtitle: t(
                    "homepage.closed_without_a_result.nothing_here_needs_deciding_today",
                  ),
                }
              : conceptSection,
          ),
        }
      : concept;
  const handleConceptAction = (action: PlanAction) => {
    captureConceptPress(pressLabel(action.label));
    if (action.signal === "open-pregnancy-tracking-setup") {
      onOpenPregnancyTrackingSetup?.();
      return;
    }
    onAction(action);
  };
  const sections = [
    ...(variant === "ivf-active" && ivfStep === "retrieval"
      ? buildIvfRetrievalSections(t)
      : isIvfTransferStep
        ? buildIvfTransferSections(t)
        : conceptForHero.sections),
    ...(detailedSections[variant] ?? []),
    ...(earlyPregnancySection ? [earlyPregnancySection] : []),
  ].filter((conceptSection) => {
    // A section that only holds the step explainer has nothing to show
    // until there is a step to explain.
    if (
      !currentStepExplainer &&
      conceptSection.widgets.length > 0 &&
      conceptSection.widgets.every((widget) => widget.type === "step-explainer")
    ) {
      return false;
    }
    /**
     * An empty day is not worth a heading once the cycle is over.
     *
     * Every other Home showing this section is mid-treatment, where "nothing
     * scheduled today" is real news. After a preservation cycle it is the answer
     * on almost every day, so the canonical empty state would sit under her
     * results for months. Only the section whose sole widget is the day is
     * dropped — a variant's own fixture rows are left alone.
     *
     * The intended parent's Home is the same: most days her calendar is her
     * carrier's, not hers.
     *
     * `realTodayRows` is empty while the schedule query is still in flight, so on
     * a day that does have something the section arrives with the query rather
     * than on first paint. Distinguishing "not loaded" from "nothing today" needs
     * the query's state, which this component is not given; worth doing when it
     * is, not worth a second source of truth before then.
     */
    if (
      !(isPostCycleVariant(variant) || variant === "intended-parent") ||
      !realTodayRows
    ) {
      return true;
    }
    const isTodayOnlySection =
      conceptSection.widgets.length === 1 &&
      conceptSection.widgets[0]?.type === "today";
    return !isTodayOnlySection || realTodayRows.length > 0;
  });
  const canShowOutcomeFunnel =
    variant.includes("egg") ||
    variant.includes("embryo") ||
    variant.includes("ivf") ||
    variant.includes("iui") ||
    variant.includes("sperm") ||
    variant.includes("family");
  /**
   * A pending beta is a scheduling action; once the date exists, the same hero
   * action records the result. Keep those intents distinct so the former opens
   * the prefilled appointment dialog instead of making the patient find the
   * test-date row inside Cycle Detail.
   */
  const betaWaitAction: BetaWaitAction =
    cycleHeaderState.kind === "beta_pending" &&
    cycleHeaderState.cycleType !== "fet"
      ? "schedule"
      : "record";
  const betaWaitActionKey =
    betaWaitAction === "schedule" ? "schedule_my_beta" : "record_my_beta";

  /**
   * The fallback names the same destination the label promises.
   *
   * It is reachable — `HomepageCanvasScreen` renders this component with no
   * `onOpenSchedule` — though nothing navigates there today: that screen's
   * `handleAction` only records the action and handles two signals. Keep the
   * fallback aligned with the real Home consumer.
   */
  const openSchedule =
    onOpenSchedule ??
    ((action: BetaWaitAction) =>
      onAction({
        label: t(
          `${HERO_DYNAMIC}.cta.${action === "schedule" ? "schedule_my_beta" : "record_my_beta"}`,
        ),
        screen: "CycleDetail",
        params: { focus: action === "schedule" ? "beta" : "outcome" },
      }));
  const betaEndLabel = betaDateForHero
    ? [
        t(`${HERO_DYNAMIC}.wait_progress_end`),
        formatRelativePhaseDate(betaDateForHero, locale, t),
        betaTimeForHero ? formatPhaseTime(betaTimeForHero, locale) : null,
      ]
        .filter(Boolean)
        .join(" ")
    : t(`${HERO_DYNAMIC}.wait_progress_end`);
  const waitProgressStartLabel = waitCopySuffix
    ? t(`${HERO_DYNAMIC}.wait_progress_start_${waitCopySuffix}`)
    : t(`${HERO_DYNAMIC}.wait_progress_start`);
  /**
   * The hero menu's Calendar item opens the Care page, where the calendar
   * actually lives.
   *
   * Kept separate from `openSchedule`, which the beta-wait CTA uses either to
   * schedule a pending beta or reach the outcome step for a recorded result.
   * Sharing one handler sent Calendar to the cycle instead. No `focus` param
   * here: that opens the add-appointment modal, which is not what a calendar
   * link should do.
   */
  const openCareCalendar = () =>
    onAction({
      label: t("homepage.review_concept.calendar_label"),
      screen: "CareOverview",
    });

  const heroActions: HeroMenuAction[] = [
    {
      icon: CalendarClock,
      label: t("homepage.review_concept.calendar_label"),
      action: "open_care_calendar",
      onPress: openCareCalendar,
    },
    ...(canShowOutcomeFunnel && onOpenOutcomeFunnel
      ? [
          {
            icon: Funnel,
            label: t("homepage.review_concept.outlook_label"),
            action: "open_outlook",
            onPress: onOpenOutcomeFunnel,
          },
        ]
      : []),
    ...(onOpenJourneyMap
      ? [
          {
            icon: MapIcon,
            label: t("homepage.review_concept.journey_map_label"),
            action: "open_journey_map",
            onPress: onOpenJourneyMap,
          },
        ]
      : []),
  ];
  /**
   * A result is recorded but the cycle is still open. Closing it is the hero's
   * primary action — the ending belongs in the hero rather than in a card stacked
   * above it.
   *
   * Now that Home resolves a recorded result on an open cycle, the outcome
   * surface can be showing at the same time, and it has its own next step:
   * acknowledging the result, which is what opens the way to pregnancy follow-up.
   * Only one primary action fits, so closing keeps it and acknowledging sits
   * beside "Review cycle" below — see `bottomContent`. Without that the
   * acknowledge action had no route to the screen at all.
   */
  const isRecordedResultHero = cycleHeaderState.kind === "result_recorded";

  /**
   * The tap that ends the interruption, and the ways out beside it.
   *
   * `homeSubjectClosedCycle` lets a cycle she has just closed take Home from a
   * pregnancy it was following, and hands it back the moment `acknowledgedAt` is
   * set. The closing carousel normally writes that field, and it is suppressed on
   * a Home already built around this ending — otherwise the two state it twice,
   * in different words, one of them in a light card whose stacked buttons are in
   * none of the hero's design language. Which left the ending with no way to be
   * dismissed at all: the acknowledge path in `usePostProcedureOutcomeStage`
   * refuses any route with no acknowledged surface, and `-uncertain` has none.
   *
   * So the hero carries it. The write is not route-gated — it is the same
   * mutation the carousel calls — so it travels as a signal like the other hero
   * side-effects.
   *
   * `endedCycleId` is the gate, not the variant. `cycle-status-review` is
   * reachable with the cycle still *open*: a positive beta the clinic later
   * withdraws resolves to `cancelled` without closing anything, and that Home's
   * hero owns "Close this cycle" — the only route to closing it from here. Keyed
   * on the variant alone, this action displaced it and pointed instead at a list
   * of closed cycles that could not contain the open one. The id is non-null only
   * where a closed ending drives the experience, which is exactly where this hero
   * has an ending to acknowledge.
   */
  const endsInAnAcknowledgeableHero =
    !!closedWithoutResultHeroCopy ||
    (variant === "cycle-status-review" && !!endedCycleId);

  const openEndedCycleAction = (
    label: string,
    cycleId: string,
  ): PlanAction => ({
    label,
    screen: "PastCycleDetail",
    params: { cycleId },
  });

  /**
   * The way into the closed cycle, and nothing else.
   *
   * This used to offer "Got it" instead whenever the ending stood in front of a
   * continuing pregnancy, because acknowledging then handed Home back to that
   * pregnancy. Since the most recent cycle decides the home — `resolveHomeSubject`,
   * 2026-09-02 — acknowledging no longer moves the subject at all, so that press
   * changed nothing a patient could see. A button that does nothing is worse than
   * no button, which is the rule this file already stated and now finally obeys
   * on every one of its heroes.
   */
  const endedCycleHeroAction: HeroPrimaryAction | undefined =
    !endsInAnAcknowledgeableHero || !endedCycleId
      ? undefined
      : {
          label: t("homepage.closed_without_a_result.review_this_cycle"),
          icon: CalendarCheck,
          onPress: () =>
            onAction(
              openEndedCycleAction(
                t("homepage.closed_without_a_result.review_this_cycle"),
                endedCycleId,
              ),
            ),
        };

  /**
   * "Egg freezing · ended 14 August".
   *
   * The arrival title says she went all the way through but never says through
   * what, and the settled title names the journey but not when it ended. The
   * eyebrow carries both facts under either tone, which is what makes this hero
   * legible to someone returning to it months later.
   *
   * Falls back to the journey alone when the cycle stored no end date.
   */
  // Keyed on the copy that actually won, not on the variant: when a birth, a
  // standing pregnancy or a closed-without-a-result hero takes the title, an
  // eyebrow reading "Egg freezing · ended 14 August" is describing a different
  // subject than the words beneath it.
  const postCycleJourneyLabel =
    isPostCycleVariant(variant) && heroCopy === postCycleHeroCopy
      ? concept.eyebrow
      : undefined;
  const postCycleHeroEyebrow = postCycleJourneyLabel
    ? {
        label: postCycleEndDate
          ? `${postCycleJourneyLabel} · ${t(
              "homepage.review_concept.cycle_ended_on",
              {
                params: {
                  // Absolute, and carrying its year.
                  //
                  // The relative formatter this used to call answers "today",
                  // "yesterday" or a weekday for anything inside a week, which
                  // reads as "cycle terminé le aujourd'hui" — and those are the
                  // common cases on a Home seen just after the cycle closes. It
                  // also drops the year, on the one hero deliberately built to
                  // outlive its thirty-day window: a cycle closed last August
                  // would read as this August.
                  date: formatDate(postCycleEndDate, locale, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  }),
                },
              },
            )}`
          : postCycleJourneyLabel,
      }
    : undefined;

  const endingHeroAction: HeroPrimaryAction | undefined = cycleEnding
    ? {
        label: cycleEnding.primary.label,
        icon: CalendarCheck,
        onPress: cycleEnding.primary.onPress,
      }
    : undefined;

  /**
   * The post-cycle hero's two buttons.
   *
   * Both are permanent: the cycle she finished stays reviewable, and the next
   * one stays startable, for as long as this Home is her Home. That is why they
   * are keyed on `postCycleCycleId` — the windowless reading — and not on
   * `endedCycleId`, which stops being set after thirty days.
   *
   * Two buttons rather than one plus a kebab entry: reviewing what she stored
   * and banking more are the only two things this page exists to offer, and
   * neither is a secondary thought worth hiding behind a tap.
   */
  const postCycleReviewAction: HeroPrimaryAction | undefined =
    isPostCycleVariant(variant) && postCycleCycleId
      ? {
          label: t("homepage.closed_without_a_result.review_this_cycle"),
          icon: CalendarCheck,
          onPress: () =>
            onAction(
              openEndedCycleAction(
                t("homepage.closed_without_a_result.review_this_cycle"),
                postCycleCycleId,
              ),
            ),
        }
      : undefined;

  /**
   * Starting another one of *these*, not another cycle in general.
   *
   * Without the preset the screen falls back to
   * `startTreatmentModalDefaults.presetCycleType`, which resolves the plan's
   * modality before the treatment goal — so on an egg-freezing post-cycle Home
   * belonging to a patient whose plan says IVF, "Start another cycle" opened the
   * modal preselected to IVF. The variant already knows the journey; every other
   * start-cycle entry point passes it, and so does this one now.
   */
  const postCycleStartAnotherPreset: StartTreatmentPresetCycleType | undefined =
    variant === "egg-freezing-postcycle"
      ? "egg_freezing"
      : variant === "embryo-freezing-postcycle"
        ? "embryo_freezing"
        : variant === "sperm-freezing-postcycle"
          ? "sperm_freezing"
          : undefined;

  // Starting another cycle is the patient's to do, so a partner keeps the way
  // into the finished cycle without the button that would start the next one.
  const postCycleStartAnotherAction: HeroPrimaryAction | undefined =
    postCycleReviewAction && !viewerIsSupporting
      ? {
          label: t("homepage.cycle_complete.start_another"),
          icon: CalendarClock,
          onPress: () =>
            onAction({
              label: t("homepage.cycle_complete.start_another"),
              signal: "open-start-treatment-modal",
              ...(postCycleStartAnotherPreset
                ? {
                    params: { presetCycleType: postCycleStartAnotherPreset },
                  }
                : {}),
            }),
        }
      : undefined;

  // Closing the cycle records how it ended, which is not a partner's to do:
  // the same rule that keeps "specify this cycle's outcome" off their Home.
  const recordedResultHeroAction: HeroPrimaryAction | undefined =
    isRecordedResultHero && !viewerIsSupporting
      ? {
          label: t("homepage.cycle_closing.ui.close_cycle"),
          icon: CalendarCheck,
          // Closing is a mutation the screen owns, so it travels as a signal the
          // same way the other hero side-effects do.
          onPress: () =>
            onAction({
              label: t("homepage.cycle_closing.ui.close_cycle"),
              signal: "close-recorded-cycle",
            }),
        }
      : undefined;

  /**
   * Open the cycle this result belongs to.
   *
   * A `CycleDetailScreen` action carrying no cycleId is read by the action router
   * as a request to create a cycle, so a "review what was recorded" button became
   * the start-treatment flow. The id is always sent; a closed cycle is reviewed
   * through history, an open one through its detail screen, and with no id at all
   * the patient lands on the cycle list rather than in a creation flow.
   */
  const reviewRecordedCycleAction = (label: string): PlanAction => {
    const cycleId = activeCycleForHero?.id;
    if (!cycleId) return { label, screen: "AllPastCycles" };
    return activeCycleForHero?.status === "closed"
      ? { label, screen: "PastCycleDetail", params: { cycleId } }
      : { label, screen: "CycleDetailScreen", params: { cycleId } };
  };

  const postRetrievalResultsAction = reviewRecordedCycleAction(
    t("homepage.review_concept.inline.post_retrieval.add_or_edit_results"),
  );
  const postRetrievalFunnelStages =
    variant === "post-retrieval" || isIvfTransferStep
      ? buildPostRetrievalFunnelStages(
          heroPhases,
          activeCycleForHero?.cycleType,
          t,
          () => handleConceptAction(postRetrievalResultsAction),
        )
      : [];

  /**
   * Which links sit under "Close this cycle". The rule is four states wide, so it
   * lives in `resolveOutcomeHeroLinks` where it can be asserted; `outcomeHeroAction`
   * is resolved just below and only its existence matters here.
   */
  const heroLinks = resolveOutcomeHeroLinks({
    variant,
    outcomeStage,
    hasOutcomeAction: isPostProcedureOutcomeHome,
  });

  const outcomeHeroAction = isPostProcedureOutcomeHome
    ? (() => {
        const isPositive = variant.endsWith("-positive");
        const isUnavailable = variant.endsWith("-unavailable");
        const isUncertain = variant.endsWith("-uncertain");
        const isAdverse = variant.endsWith("-adverse");
        const isUnresolved = isUnavailable || isUncertain || isAdverse;
        const shared = "homepage.review_concept.post_procedure_outcome.shared";
        // Once a baby has been born, closing the cycle is the only thing left to
        // offer. What this would add is either "Let's look at what's next" —
        // which acknowledges the result to reach pregnancy follow-up, and there
        // is no follow-up to set up for a pregnancy that has ended in a birth —
        // or a second route into the cycle the hero already links to.
        //
        // Only when closing is actually on screen. On a cycle that is already
        // closed there is nothing to press instead, and suppressing this as well
        // would leave the hero with no action at all.
        if (recordedBirth && isRecordedResultHero) return undefined;
        // The hero owns the primary entry into the cycle. Secondary actions
        // below should not repeat the same destination, so the setup/pause
        // state uses the precise cycle-review label here and the action group
        // stays focused on questions and optional pregnancy tracking.
        // Nothing to acknowledge and nowhere to go: the hero would be offering a
        // press that changes nothing, so it names the cycle instead — and only
        // then, because the link beside it already goes there and two identical
        // links is the hero telling her twice.
        const canReachGuidance = Boolean(earlyPregnancySection);
        if (
          !isUnresolved &&
          !canReachGuidance &&
          (outcomeStage !== "recorded" || !outcomeStageState.canAcknowledge)
        ) {
          // Saying nothing is only right when the inline link beside the hero
          // already names the cycle — two identical links is the hero telling
          // her twice. Everywhere else, returning null left a closed or negative
          // outcome, and a partner on any of them, with no route into the cycle.
          if (isRecordedResultHero) {
            return null;
          }
          const reviewLabel = t(`${shared}.stage_action_review_records`);
          return {
            label: reviewLabel,
            icon: CalendarClock,
            onPress: () => onAction(reviewRecordedCycleAction(reviewLabel)),
          };
        }
        const label = isUnresolved
          ? t(
              `${shared}.${isAdverse ? "hero_adverse_action" : isUncertain ? "hero_uncertain_action" : "hero_unavailable_action"}`,
            )
          : t(`${shared}.hero_${isPositive ? "positive" : "negative"}_action`);
        return {
          label,
          icon: isUnresolved || isPositive ? CalendarCheck : CalendarClock,
          onPress: () => {
            if (isUnresolved) {
              onAction(reviewRecordedCycleAction(label));
              return;
            }
            // Acknowledging is a fact about the record and still happens, but it
            // is no longer what she gets for pressing: the page goes to the
            // guidance, which stays where it is whether or not she has pressed
            // anything.
            if (
              outcomeStage === "recorded" &&
              outcomeStageState.canAcknowledge
            ) {
              outcomeStageState.acknowledge();
              // The sections it adds sit above the guidance, so wait for the
              // section to re-measure rather than scrolling to where it was.
              // Remembering where it was is what lets us tell "it moved" from
              // "something else re-rendered"; a refused acknowledgement simply
              // never moves it, and the request lapses instead of firing later.
              pendingScrollRef.current = earlyPregnancyY;
              return;
            }
            scrollToEarlyPregnancy();
          },
        };
      })()
    : undefined;

  return (
    <View
      // Two measurements, because a section's own layout is relative to this
      // container and the container sits below other content in the page's
      // scroll view. Summed, they are where "Bon à savoir" actually starts.
      onLayout={(event) => setHomeTopY(event.nativeEvent.layout.y)}
      style={{ gap: 18, position: "relative", overflow: "visible" }}
    >
      <View
        pointerEvents="none"
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: -80,
          height: Dimensions.get("window").height,
          zIndex: 50,
          overflow: "visible",
        }}
      >
        <ConfettiBurst trigger={confettiTrigger} />
      </View>
      <Hero
        title={waitHeroCopy ? waitHeroCopy.title : heroCopy.title}
        summary={waitHeroCopy ? waitHeroCopy.summary : heroCopy.summary}
        eyebrow={
          waitHeroCopy
            ? waitHeroCopy.eyebrow
            : postCycleHeroEyebrow
              ? postCycleHeroEyebrow
              : heroCopy === dynamicHeroCopy
                ? dynamicHeroCopy?.eyebrow
                : undefined
        }
        metrics={
          isSpermResultsWait && spermWaitHeroMetrics.length > 0
            ? spermWaitHeroMetrics
            : isSpermCollectionDay && spermCollectionHeroCopy
              ? spermCollectionHeroMetrics
              : isFetActive && fetHeroCopy
                ? fetHeroMetrics
                : !isPostProcedureOutcomeHome &&
                    !isSpermResultsWait &&
                    (variant !== "post-retrieval" || isEmbryoStep) &&
                    variant !== "cycle-status-review" &&
                    dynamicHeroMetrics.length > 0
                  ? dynamicHeroMetrics
                  : concept.heroMetrics
        }
        metricVariant={
          (isSpermResultsWait && spermWaitHeroMetrics.length > 0) ||
          (isSpermCollectionDay && spermCollectionHeroMetrics.length > 0) ||
          (isFetActive && fetHeroMetrics.length > 0)
            ? "tags"
            : isPostProcedureOutcomeHome ||
                isSpermResultsWait ||
                (variant === "post-retrieval" && !isEmbryoStep)
              ? undefined
              : isBetaWait
                ? "cards"
                : dynamicHeroMetrics.length > 0 &&
                    variant !== "cycle-status-review"
                  ? "tags"
                  : undefined
        }
        metricsFillRow={
          !isBetaWait &&
          !isPostProcedureOutcomeHome &&
          !isSpermResultsWait &&
          !isSpermCollectionDay &&
          !isFetActive &&
          variant !== "post-retrieval"
        }
        backgroundSource={
          !isPostProcedureOutcomeHome &&
          !isSpermResultsWait &&
          variant !== "cycle-status-review" &&
          dynamicHeroCopy
            ? inCycleHeroBackground
            : undefined
        }
        primaryAction={
          // In the order the hero copy itself is resolved: the surface that owns
          // the words owns the button under them.
          endingHeroAction ??
          postCycleReviewAction ??
          endedCycleHeroAction ??
          recordedResultHeroAction ??
          outcomeHeroAction ??
          undefined
        }
        secondaryAction={postCycleStartAnotherAction}
        menuActions={heroActions}
        bottomContent={
          <>
            {/* The outcome stage is the only press here that can be refused —
              no hero acknowledges an ending any more. A refusal that reverts in
              silence is what makes a button look broken, so the message sits
              beside whatever the hero is showing rather than inside one of its
              shapes. */}
            {outcomeStageState.didFail ? (
              <Text
                style={{
                  fontSize: 12,
                  lineHeight: 17,
                  color: semanticColors.onDark.text.secondary,
                  marginBottom: 8,
                }}
              >
                {t(
                  "homepage.review_concept.post_procedure_outcome.shared.could_not_save_that_try_again",
                )}
              </Text>
            ) : null}
            {
              // The other ways out of a recorded-but-open cycle sit in the hero
              // beside the first, rather than in a card above it. They precede the
              // wait content deliberately: the wait is over once a result exists.
              //
              // `outcomeHeroAction` is here because the primary slot is taken. On the
              // outcome surface it is the acknowledge step, and acknowledging is what
              // reaches the setup stage and pregnancy follow-up — with only "Close
              // cycle" on screen there was no way to get there at all.
              //
              // Only when it is that step, though. See `resolveOutcomeHeroLinks`:
              // everywhere else it lands on the same screen as the link beside it,
              // so rendering both is the hero offering the same thing twice.
              // A birth is the exception: there is no follow-up left to reach,
              // so the hero carries "Close this cycle" and nothing beside it.
              // Absent when the primary action already goes here: the hero must
              // not offer the same destination twice.
              isRecordedResultHero && !recordedBirth ? (
                <View style={{ gap: 8 }}>
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 16,
                    }}
                  >
                    {outcomeHeroAction && heroLinks.showsOutcomeAction ? (
                      <HeroInlineLink
                        label={outcomeHeroAction.label}
                        onPress={outcomeHeroAction.onPress}
                      />
                    ) : null}
                    {/* Through the same resolver the rest of the surface uses. Built by
                  hand, this link named `CycleDetail` with no cycleId whenever the
                  hero had no cycle to name — which `resolveCycleDetailAction`
                  reads as a request to start one, so "Revoir ce cycle" answered
                  with the start-treatment flow. It also sent a closed cycle to
                  the open-cycle route, which cannot show it.

                  Absent when the link above already asks for the result: that one
                  goes to the same screen and says why. */}
                    {heroLinks.showsReviewCycleLink ? (
                      <HeroInlineLink
                        label={t("homepage.cycle_closing.ui.review_cycle")}
                        onPress={() =>
                          onAction(
                            reviewRecordedCycleAction(
                              t("homepage.cycle_closing.ui.review_cycle"),
                            ),
                          )
                        }
                      />
                    ) : null}
                  </View>
                </View>
              ) : waitHeroCopy && waitProgress?.totalWaitDays ? (
                <HeroWaitProgress
                  day={waitProgress.dayOfWait}
                  total={waitProgress.totalWaitDays}
                  startLabel={waitProgressStartLabel}
                  endLabel={betaEndLabel}
                />
              ) : isBetaWait && !isPostProcedureOutcomeHome ? (
                <GlassButton
                  label={
                    viewerIsSupporting
                      ? t(`${HERO_DYNAMIC}.supporting.record_the_beta`)
                      : t(`${HERO_DYNAMIC}.cta.record_my_beta`)
                  }
                  action={`${HERO_DYNAMIC}.cta.${betaWaitActionKey}`}
                  accessibilityLabel={
                    viewerIsSupporting
                      ? t(
                          `${HERO_DYNAMIC}.supporting.${betaWaitAction === "schedule" ? "schedule_the_beta" : "record_the_beta"}`,
                        )
                      : t(`${HERO_DYNAMIC}.cta.${betaWaitActionKey}`)
                  }
                  // A flask, not a clock. The label and the destination now say
                  // "put the result in", and a calendar glyph beside them still
                  // said "book a date".
                  leading={
                    <FlaskConical
                      size={15}
                      color={semanticColors.onDark.text.primary}
                      strokeWidth={1.8}
                    />
                  }
                  trailing={
                    <ArrowRight
                      size={15}
                      color={semanticColors.onDark.text.secondary}
                      strokeWidth={1.8}
                    />
                  }
                  onPress={() => openSchedule(betaWaitAction)}
                  style={{ marginTop: 16 }}
                />
              ) : undefined
            }
          </>
        }
      />

      {sections.map((conceptSection, index) => (
        <Fragment key={`${conceptSection.title}-${index}`}>
          <View
            onLayout={
              conceptSection === earlyPregnancySection
                ? (event) => setEarlyPregnancyY(event.nativeEvent.layout.y)
                : undefined
            }
          >
            <ConceptSectionView
              section={conceptSection}
              viewerIsSupporting={viewerIsSupporting}
              onAction={handleConceptAction}
              renderMonitoringCard={renderMonitoringCard}
              renderSemenAnalysisPending={renderSemenAnalysisPending}
              hideSectionTitle={
                conceptSection.widgets.length === 1 &&
                conceptSection.widgets[0]?.type === "monitoring-chart"
              }
              realTestResults={testInsights}
              testResultsQueryState={testResultsQueryState}
              projectionSourceCycleId={projectionSourceCycleId}
              onOpenMedicationFactSheet={onOpenMedicationFactSheet}
              onOpenProcedureFactSheet={onOpenProcedureFactSheet}
              onOpenTestFactSheet={onOpenTestFactSheet}
              onRequestDocumentAttachment={onRequestDocumentAttachment}
              onCompletionFeedback={() =>
                setConfettiTrigger((current) => current + 1)
              }
              todayRows={realTodayRows}
              peekAheadDays={peekAheadDays}
              currentStepExplainer={currentStepExplainer}
            />
          </View>
          {index === 0 &&
          (variant === "post-retrieval" || isIvfTransferStep) ? (
            <View style={{ marginTop: 18, gap: 10 }}>
              <SectionTitleRow
                title={t(
                  "homepage.review_concept.inline.post_retrieval.lab_section_title",
                )}
                icon={FlaskConical}
              />
              <OutcomeDotFunnelRows
                stages={postRetrievalFunnelStages}
                totalDots={postRetrievalFunnelStages[0]?.value ?? 1}
              />
            </View>
          ) : null}
          {/* Wait extras land right after the Today section, above Testing /
              Good to know. */}
          {index === 0 && afterTodaySlot ? afterTodaySlot : null}
        </Fragment>
      ))}
      {endOfPageSlot}
    </View>
  );
}
